// src/pages/Workouts.js
// Unified Workouts page — three flat tabs: Upcoming, On Demand, Shorts
// "Upcoming" = scheduling + streaming + reminders (supports live AND pre-recorded classes)
// "On Demand" = class recordings from Mux (auto-saved after live or pre-recorded classes end)
// "Shorts" = free quick videos from Bunny
// Feature-flagged via siteConfig.workoutsPageEnabled
//
// Class types:
//   - 'live' (default): admin broadcasts in real-time via Mux live stream
//   - 'prerecorded': admin uploads a video that premieres at the scheduled time

import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import AppNav from '../components/AppNav';
import LivePlayer from '../components/LivePlayer';
import LiveBroadcaster from '../components/LiveBroadcaster';
import VideoCard from '../components/VideoCard';
import { useNavigate, useLocation } from 'react-router-dom';
import VideoPlayerModal from '../components/VideoPlayerModal';
import FilterBar from '../components/FilterBar';
import ClassGuide from '../components/ClassGuide';
import ClassChoicePair from '../components/ClassChoicePair';
import FormattedText from '../components/FormattedText';
import { SeriesShareButton, SeriesMetaPills, SeriesInfoBlocks } from '../components/SeriesContent';
import ClassComments from '../components/ClassComments';
import PresenceSheet from '../components/PresenceSheet';
import BroadcasterCueSequence from '../components/BroadcasterCueSequence';
import { fetchProgramImages, fetchFreeVideos, fetchVideosFromCollection } from '../services/bunnyStream';
import { fetchSiteContent, getCachedSiteContent, fetchUserCompletions, markWorkoutComplete, deleteCompletionById, fetchVideoCompletions, indexCompletions, db } from '../services/firebase';
import { fetchSeries, seriesDayLabel, orderSeriesVideos, findSeriesByToken, seriesShareUrl,
    isSeriesDayReleased, seriesDayReleaseAt, formatSeriesReleaseDate, indexSeriesDays } from '../services/series';
import { fetchProgramVideos, shapeProgramVideo } from '../services/programVideos';
import { setClassReminder, removeClassReminder, getUserReminders } from '../services/classReminders';
import { getUserClassPurchases, canAccessClass, hasActiveSubscription } from '../services/classAccess';
import { fetchClassRecordings, updateClassRecordingThumbnail, setClassRecordingFree } from '../services/classRecordings';
import ClassMetricsModal from '../components/ClassMetricsModal';
import ReAirClassModal from '../components/ReAirClassModal';
import { ClassTogetherPanel, TakenOnTile } from '../components/ClassTogether';
import ClassDayReminderControl from '../components/ClassDayReminderControl';
import ClassAudiencePicker from '../components/ClassAudiencePicker';
import WelcomeModal from '../components/WelcomeModal';
import { canSeeClass } from '../services/classVisibility';
import { hasWelcomePending, clearWelcomePending, markWelcomePending } from '../services/welcomeMoment';
import { serverNow, syncServerClock } from '../services/serverClock';
import { isClassCompleteForViewer, markClassCompleted, clearClassCompleted } from '../services/classCompletion';
import { isReleaseDayClass, isReleasedForViewer, projectReleaseClass, resolveReleaseVideo, releaseAnchorIso, localWeekStartKey, localDateKey, formatReleaseDate } from '../services/classRelease';
import { isPushSupported, subscribeToPush, isSubscribedToPush, getPermissionState } from '../services/pushNotifications';
import { setFavoriteState, getUserFavorites } from '../services/classFavorites';
import { setVideoFavoriteState, getUserVideoFavorites, getVideoLikeCounts } from '../services/videoFavorites';
import { onSnapshot, doc as firestoreDoc } from 'firebase/firestore';
import {
    fetchLiveClasses, createLiveClass, updateLiveClass, deleteLiveClass,
    publishDraftClass,
    markClassLive, markClassEnding, markClassEnded, formatClassTime, isClassSoon,
    subscribeToViewerCount, joinPresence, leavePresence, subscribeToViewers,
    logHealthEvent,
} from '../services/liveClasses';
import { getClassState } from '../services/classState';
import { PlanPickerModal, useAnnualOffer } from '../components/PlanPicker';
import { resolvePlanCopy } from '../services/subscriptionPlans';
import AvatarPhoto from '../components/AvatarPhoto';
import MemberCardTrigger from '../components/MemberCardTrigger';
import { useMemberPhotos } from '../services/memberProfiles';
// Pre-recorded recording buffer (2026-04-23 hardening) — IDB-backed chunk
// store that replaces the prior in-memory array in LiveBroadcaster. We
// import the recovery + cleanup primitives here: `clearRecording` is called
// once the Mux upload succeeds (IDB is no longer the source of truth);
// `listRecordings` + `getRecordingBlob` drive the on-mount recovery modal.
import {
    listRecordings as bufferListRecordings,
    getRecordingBlob as bufferGetRecordingBlob,
    clearRecording as bufferClearRecording,
    getRecordingMetadata as bufferGetMetadata,
} from '../services/recordingBuffer';

// ── Class-form datetime helpers ──────────────────────────────────────────────
// Pure parse/build over the existing `datetime-local` string format
// ("YYYY-MM-DDTHH:MM"). The form's `scheduledAt` field is unchanged — these
// helpers just split it into displayable parts for the new time-picker UI and
// recombine on every change. Same string in, same string out, same timezone
// semantics — no changes to handleSaveClass or handleEditClass round-trip.
// Deep muted teal for the "Strong+" difficulty tag — matched to the "From light
// to strong" program artwork, and deliberately distinct from the sage used by
// "Ease In" (--mom-success) so the two tags never read as the same signal.
// A literal rather than a theme token: the tag taxonomy is fixed, not admin-themed.
const STRONG_PLUS_COLOR = '#4f7c7a';

// Schedule order within a day (owner, 2026-10-13): Classic always first
// ("on the left"), Strong+ always last ("on the right"), everything else -
// untagged and Ease In - in between. Array.prototype.sort is stable, so ties
// keep their incoming chronological order: a day with no Classic/Strong+ tags
// renders in exactly the order it did before.
const difficultyRank = (c) => (c && c.classic ? 0 : c && c.strongPlus ? 2 : 1);
const orderByDifficulty = (classes) => [...classes].sort((a, b) => difficultyRank(a) - difficultyRank(b));

const FORM_MINUTE_OPTIONS = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'];

function parseScheduledAt(str) {
    if (!str || typeof str !== 'string') return { date: '', hour12: '', minute: '', ampm: '' };
    const [date, time] = str.split('T');
    if (!time) return { date: date || '', hour12: '', minute: '', ampm: '' };
    const [h, m] = time.split(':');
    const h24 = parseInt(h, 10);
    if (Number.isNaN(h24)) return { date: date || '', hour12: '', minute: '', ampm: '' };
    let hour12;
    if (h24 === 0) hour12 = 12;          // 00:xx → 12:xx AM
    else if (h24 > 12) hour12 = h24 - 12; // 13:xx-23:xx → 1-11 PM
    else hour12 = h24;                    // 1-12
    return {
        date: date || '',
        hour12: String(hour12),
        minute: (m || '00').padStart(2, '0').slice(0, 2),
        ampm: h24 >= 12 ? 'PM' : 'AM',
    };
}

function buildScheduledAt({ date, hour12, minute, ampm }) {
    if (!date || !hour12 || !minute || !ampm) return '';
    let h24 = parseInt(hour12, 10);
    if (Number.isNaN(h24)) return '';
    // 12 AM = 00, 12 PM = 12, otherwise add 12 if PM
    if (ampm === 'AM' && h24 === 12) h24 = 0;
    else if (ampm === 'PM' && h24 !== 12) h24 += 12;
    return `${date}T${String(h24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

// Calm human-readable confirmation line. Empty string if input is incomplete
// (caller should conditionally render). Uses the viewer's locale + timezone.
function formatScheduledAtForDisplay(str) {
    if (!str) return '';
    const d = new Date(str);
    if (Number.isNaN(d.getTime())) return '';
    try {
        const dateStr = d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
        const timeStr = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short' });
        return `${dateStr} at ${timeStr}`;
    } catch (e) {
        return '';
    }
}

// ── Presence row: "Also in class" + chips of current viewers ──
// Rendered below the player for both viewers and admin (admin observes but isn't in the list).
// Each chip: small single-letter avatar + "FirstName L" label. Capped at 5 visible chips, with
// "+N more" as overflow. Cream/brand-aligned styling sits cleanly under the dark player on both
// mobile and desktop (flexWrap lets chips flow to a second line on narrow viewports if needed).
// ── Pinned tab bar scroll offset ──
// The Workouts tab bar is sticky (pinned under the app's top bar). Any code that
// scrolls an element "to the top" must land it BELOW both bars, or it ends up
// hidden under them. Heights mirror AppNav (70px desktop / 52px mobile top bar,
// swap at 768px) and the pinned tab pill INCLUDING its tier captions (they stay
// visible while pinned). Read at call time so it always matches the viewport.
// ── Airtime is DISPLAY-ONLY on members' devices (owner rule, 2026-10-06) ──
// "No member's device should ever determine what a scheduled, date/time-driven
// event should be." A pre-recorded class that is still 'scheduled' in the
// database, has its video ready, and whose airtime has passed by the SERVER
// clock is SHOWN as live on this device — and nothing is written. The server
// cron (send-class-reminder) is the only writer of pre-recorded class state
// (scheduled → live → ended, plus a self-heal for anything stuck 'live').
// Previously each device also wrote 'live' to the shared doc; an offline phone
// replayed that write hours late and resurrected two ended classes (incident
// 2026-10-05). One rule, applied wherever a viewer reads a class, keeps every
// listener moving forward only: once aired, never back to the waiting room.
function withAirtime(cls) {
    if (!cls || (cls.classType || 'live') !== 'prerecorded') return cls;
    // Release-day classes never air (no live surface, no auto-activate) — each
    // member plays them from her own midnight. See services/classRelease.js.
    if (isReleaseDayClass(cls)) return cls;
    const s = cls.state || cls.status;
    if (s !== 'scheduled') return cls;
    if (!(cls.uploadStatus === 'ready' || cls.muxAssetId) || !cls.scheduledAt) return cls;
    const startMs = new Date(cls.scheduledAt).getTime();
    if (!Number.isFinite(startMs) || serverNow() < startMs) return cls;
    return { ...cls, state: 'live', status: 'live' };
}

function pinnedHeaderOffset() {
    if (typeof window === 'undefined' || !window.matchMedia) return 80;
    const narrowNav = window.matchMedia('(max-width: 768px)').matches;
    const phone = window.matchMedia('(max-width: 600px)').matches;
    return (narrowNav ? 52 : 70) + (phone ? 74 : 82) + 14;
}

function ViewerPresenceRow({ viewers, onExpand }) {
    // Photos looked up by userId (adr-member-profile-photos) — the presence
    // objects in liveClasses.viewers[] are deliberately unchanged.
    const photoFor = useMemberPhotos((viewers || []).slice(0, 5).map((v) => v.userId));
    if (!viewers || viewers.length === 0) return null;

    const maxVisible = 5;
    const visible = viewers.slice(0, maxVisible);
    const overflow = viewers.length - maxVisible;

    return (
        <div style={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'center',
            gap: 6, padding: '12px 0 4px',
        }}>
            <span style={{
                fontFamily: 'var(--mom-font-sans)', fontSize: 12,
                fontStyle: 'italic', color: 'var(--mom-warm-gray)',
                letterSpacing: '0.02em', marginRight: 4,
            }}>Also in class</span>
            {visible.map((v, i) => {
                const first = (v.firstName || '').trim();
                const last = (v.lastInitial || '').trim();
                const displayName = first
                    ? (last ? `${first} ${last.charAt(0).toUpperCase()}` : first)
                    : '—';
                const avatarLetter = (first || '?').charAt(0).toUpperCase();
                return (
                    // Member card on hover/tap when this viewer has a tagline
                    // (PHOTO-002); without one the trigger returns the chip as-is.
                    <MemberCardTrigger key={(v.userId || '') + '_' + i} userId={v.userId} name={displayName} style={{ maxWidth: '100%' }}>
                    <div style={{
                        display: 'inline-flex', alignItems: 'center',
                        gap: 6, padding: '3px 10px 3px 3px',
                        borderRadius: 999,
                        background: 'var(--mom-cream)',
                        border: '1px solid var(--mom-cream-dark)',
                        maxWidth: '100%',
                    }}>
                        <div style={{
                            width: 22, height: 22, borderRadius: '50%',
                            background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#fff', flexShrink: 0,
                            fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700,
                            letterSpacing: '-0.02em', position: 'relative',
                        }}>{avatarLetter}<AvatarPhoto url={photoFor(v.userId)} /></div>
                        <span style={{
                            fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600,
                            color: 'var(--mom-charcoal)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden', textOverflow: 'ellipsis',
                            maxWidth: 140,
                        }}>{displayName}</span>
                    </div>
                    </MemberCardTrigger>
                );
            })}
            {overflow > 0 && (
                // D-063: tappable overflow — opens PresenceSheet with the full viewer list.
                // Still visually a small text affordance; button role for accessibility.
                <button
                    type="button"
                    onClick={onExpand}
                    style={{
                        fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 700,
                        color: 'var(--mom-primary)', padding: '4px 10px',
                        background: 'transparent', border: '1px solid color-mix(in srgb, var(--mom-primary) 40%, transparent)',
                        borderRadius: 999, cursor: 'pointer',
                        transition: 'all 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'color-mix(in srgb, var(--mom-primary) 10%, transparent)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                    }}
                    aria-label={`Show all ${viewers.length} viewers`}
                >
                    +{overflow} more
                </button>
            )}
        </div>
    );
}

// ── RETIRED (D-053): Instructor "start teaching" cue ──
// Replaced by <BroadcasterCueSequence> which gates on real pipeline events instead of a
// blind 10s countdown. This component is kept in-tree per the D-053 rollout note in
// adr-live-class-rebuild.md so the migration can be reverted in a single diff if needed,
// but it is no longer mounted. Safe to delete in a follow-up cleanup after the rebuild
// has baked in production.
// eslint-disable-next-line no-unused-vars
function GoLiveCue({ goLiveAt, onDismiss }) {
    const WARMUP_SECONDS = 10;
    const READY_HOLD_SECONDS = 5;
    const [phase, setPhase] = useState('counting'); // 'counting' | 'ready' | 'hidden'
    const [secondsLeft, setSecondsLeft] = useState(WARMUP_SECONDS);

    // Keep the latest onDismiss in a ref so the effect below doesn't need it as a
    // dependency — otherwise an inline arrow `onDismiss={() => setGoLiveAt(null)}` at
    // the call site would bust the effect on every parent render, potentially re-entering
    // the tick path mid-countdown and (previously) hitting a temporal-dead-zone on `interval`.
    const onDismissRef = useRef(onDismiss);
    useEffect(() => { onDismissRef.current = onDismiss; }, [onDismiss]);

    useEffect(() => {
        if (!goLiveAt) return;
        let interval = null;
        let readyTimeout = null;
        let cancelled = false;

        // Sync-compute initial state so we don't have to rely on a first tick — avoids any
        // ordering/TDZ pitfalls and handles the edge case where the effect re-runs *after*
        // the warmup window has already elapsed (e.g., if the cue was mounted, then re-keyed).
        const initialElapsed = (Date.now() - goLiveAt) / 1000;
        if (initialElapsed >= WARMUP_SECONDS) {
            setPhase('ready');
            readyTimeout = setTimeout(() => {
                if (cancelled) return;
                setPhase('hidden');
                onDismissRef.current?.();
            }, READY_HOLD_SECONDS * 1000);
        } else {
            setPhase('counting');
            setSecondsLeft(Math.max(1, Math.ceil(WARMUP_SECONDS - initialElapsed)));
            interval = setInterval(() => {
                if (cancelled) return;
                const elapsed = (Date.now() - goLiveAt) / 1000;
                const remaining = WARMUP_SECONDS - elapsed;
                if (remaining > 0) {
                    setSecondsLeft(Math.max(1, Math.ceil(remaining)));
                } else {
                    setPhase('ready');
                    if (interval) { clearInterval(interval); interval = null; }
                    readyTimeout = setTimeout(() => {
                        if (cancelled) return;
                        setPhase('hidden');
                        onDismissRef.current?.();
                    }, READY_HOLD_SECONDS * 1000);
                }
            }, 250);
        }

        return () => {
            cancelled = true;
            if (interval) clearInterval(interval);
            if (readyTimeout) clearTimeout(readyTimeout);
        };
    }, [goLiveAt]);

    if (phase === 'hidden' || !goLiveAt) return null;

    const isReady = phase === 'ready';
    // Floating overlay: pinned to the top of the viewport with a high z-index so the
    // instructor sees it regardless of scroll position. The broadcaster panel is tall
    // enough that the cue would otherwise sit above the fold when admin is looking at
    // the camera preview — admin missed it the last time because it was off-screen.
    // Mobile-aware width (16px side insets) with a max of 560px for desktop.
    return (
        <div style={{
            position: 'fixed',
            top: 12,
            left: 12,
            right: 12,
            margin: '0 auto',
            maxWidth: 560,
            zIndex: 2000,
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '14px 20px', borderRadius: 14,
            background: isReady
                ? 'linear-gradient(135deg, color-mix(in srgb, var(--mom-success, #7a9f7a) 96%, var(--mom-white)), color-mix(in srgb, var(--mom-success, #7a9f7a) 82%, var(--mom-white)))'
                : 'linear-gradient(135deg, color-mix(in srgb, var(--mom-primary) 96%, var(--mom-white)), color-mix(in srgb, var(--mom-primary) 82%, var(--mom-white)))',
            border: `1.5px solid ${isReady ? 'color-mix(in srgb, var(--mom-success, #7a9f7a) 60%, transparent)' : 'color-mix(in srgb, var(--mom-primary) 60%, transparent)'}`,
            boxShadow: `0 8px 32px ${isReady ? 'color-mix(in srgb, var(--mom-success, #7a9f7a) 35%, transparent)' : 'color-mix(in srgb, var(--mom-primary) 35%, transparent)'}, 0 2px 8px rgba(0,0,0,0.08)`,
            backdropFilter: 'blur(8px) saturate(1.4)',
            WebkitBackdropFilter: 'blur(8px) saturate(1.4)',
            transition: 'all 0.4s ease',
            animation: 'goLiveCueSlideIn 0.4s ease-out',
        }}>
            {isReady ? (
                /* Checkmark circle */
                <div style={{
                    flexShrink: 0,
                    width: 32, height: 32, borderRadius: '50%',
                    background: 'rgba(255,255,255,0.95)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--mom-success, #7a9f7a)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                    </svg>
                </div>
            ) : (
                /* Countdown circle — large readable number */
                <div style={{
                    flexShrink: 0,
                    width: 44, height: 44, borderRadius: '50%',
                    background: 'rgba(255,255,255,0.95)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                }}>
                    <span style={{
                        fontFamily: 'var(--mom-font-serif)', fontSize: 20, fontWeight: 700,
                        fontStyle: 'italic', color: 'var(--mom-primary)', lineHeight: 1,
                    }}>{secondsLeft}</span>
                </div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{
                    fontFamily: 'var(--mom-font-sans)', fontSize: 11,
                    fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.85)',
                    margin: 0, marginBottom: 2,
                }}>
                    {isReady ? "You're live" : 'Viewers connecting'}
                </p>
                <p style={{
                    fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600,
                    color: '#fff', margin: 0, lineHeight: 1.4,
                }}>
                    {isReady
                        ? 'Your viewers can see and hear you now — go ahead and start the class!'
                        : `Hold on a moment — start teaching in ${secondsLeft}s so no one misses your opening.`}
                </p>
            </div>
        </div>
    );
}

// Maps share-friendly URL slugs → internal tab keys. The internal keys are
// historical ('live' = Schedule, 'classes' = On Demand), so external marketing
// links use the human-readable slugs (?tab=schedule / on-demand / shorts / series)
// and we also accept the raw keys as a fallback. Unknown slugs resolve to null
// (→ default tab), so a typo never errors.
const TAB_SLUG_TO_KEY = {
    schedule: 'live', live: 'live',
    'on-demand': 'classes', 'on_demand': 'classes', ondemand: 'classes', classes: 'classes',
    shorts: 'shorts',
    series: 'series',
};

// Resolve the requested tab key from the current URL's ?tab= param. Returns null
// when absent/unknown, or when a more-specific ?recording= deep-link is present
// (that one owns the tab switch — see the recording effect).
// `search` is optional. Pass the ROUTER's location.search when reacting to an
// in-app navigation — raw history.replaceState (used by the param-strippers in
// this file) never touches the router's copy, so it stays truthful. Omitting it
// reads window.location, which is only safe during the very first render, before
// any sibling effect has had a chance to rewrite the address bar.
function resolveTabFromUrl(search) {
    try {
        const params = new URLSearchParams(typeof search === 'string' ? search : window.location.search);
        if (params.get('recording') || params.get('class')) return null;
        // A ?series=<slug-or-id> deep link implies the Series tab, so a shared
        // challenge link doesn't also have to carry ?tab=series. The recording/
        // class deep links above still win (more specific + pre-existing).
        if ((params.get('series') || '').trim()) return 'series';
        const raw = (params.get('tab') || '').toLowerCase().trim();
        return raw ? (TAB_SLUG_TO_KEY[raw] || null) : null;
    } catch (_) { return null; }
}

// SERIES detail rich-content blocks + share control now live in
// src/components/SeriesContent.js (extracted 2026-09-14) so the public,
// signed-out challenge page renders a challenge identically to this one.

function Workouts({ demoMode }) {
    // ── Tab state ──
    // Lazy-init from ?tab= so a deep-linked non-series tab paints immediately (no
    // Schedule flash). 'series' depends on an async flag (enableSeriesTab), so it
    // starts on the safe default and the effect below upgrades it once known.
    const [activeTab, setActiveTab] = useState(() => {
        const key = resolveTabFromUrl();
        return (key && key !== 'series') ? key : 'live';
    }); // 'live' | 'classes' | 'shorts' | 'series'

    // ── URL deep-link intent, captured during the FIRST render ──
    // These MUST be read once, before any effect runs, and never re-read: the
    // address-bar sync effect below strips ?series= on mount (activeTab starts on
    // the safe default 'live', so that effect concludes no challenge is open).
    // The tab-upgrade effect defers on its first pass while siteConfig resolves,
    // and on its second pass a re-read of window.location would find the param
    // already gone — silently dropping the link and latching the member on
    // Schedule. Only non-admins reach that deferral (`isAdmin` short-circuits
    // seriesAvailable), which is why this was invisible in admin testing.
    const urlTabIntentRef = useRef(undefined);
    if (urlTabIntentRef.current === undefined) urlTabIntentRef.current = resolveTabFromUrl();
    // Whether this page load arrived carrying a ?series= link at all. Lets the
    // sync effect tell "landed on a challenge link" apart from "browsing the
    // Series tab normally", so it only withholds the clear in the former case.
    const initialSeriesTokenRef = useRef(undefined);
    if (initialSeriesTokenRef.current === undefined) {
        let t = '';
        try { t = (new URLSearchParams(window.location.search).get('series') || '').trim(); } catch (_) { t = ''; }
        initialSeriesTokenRef.current = t;
    }

    // ── Live classes state ──
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeClass, setActiveClass] = useState(null);
    // Per-class last-seen shared state, used ONLY to detect the admin-restart
    // edge (ended/ending → live) so the completion latch can be cleared. See the
    // un-latch block in loadClasses + adr-class-completion-absorbing-state.md.
    const prevClassStatesRef = useRef(new Map());
    const [creating, setCreating] = useState(false);
    const [editingClassId, setEditingClassId] = useState(null); // null = create mode, string = edit mode
    const [form, setForm] = useState({ title: '', description: '', scheduledAt: '', duration: 45, imageUrl: '', price: 1, classType: 'prerecorded', muscleGroups: [], classic: false, easeIn: false, strongPlus: false, visibleToUserIds: [], releaseMode: 'day', releaseDate: '', wasReleaseDay: false });
    const [saving, setSaving] = useState(false);
    const [streamLoading, setStreamLoading] = useState(false);
    const [copied, setCopied] = useState('');
    const [showObs, setShowObs] = useState(false);
    const [broadcastingClassId, setBroadcastingClassId] = useState(null);
    const [viewerCount, setViewerCount] = useState(0);
    const [liveViewers, setLiveViewers] = useState([]);
    // D-063: PresenceSheet is opt-in — a "+N more" chip click opens the full viewer list.
    const [presenceSheetOpen, setPresenceSheetOpen] = useState(false);
    const [showAdminPreview, setShowAdminPreview] = useState(false);
    // Timestamp (ms) when admin clicked Go Live — retained as a historical handle for
    // the retired GoLiveCue component (kept in-tree per D-053 rollout note but no longer
    // mounted). Supersedes: BroadcasterCueSequence now drives go-live feedback.
    const [goLiveAt, setGoLiveAt] = useState(null);

    // ── D-053: BroadcasterCueSequence lifecycle state ──
    // Cue is visible from the moment LiveBroadcaster's ws.onopen fires until step 5
    // completes + 2s hold + onDismiss. Each gate is driven by a LiveBroadcaster callback.
    // Non-live classes (pre-recorded) never activate these — cue.active is additionally
    // gated on classType === 'live' at the render site.
    const [cueActive, setCueActive] = useState(false);
    const [cueWsOpen, setCueWsOpen] = useState(false);
    const [cueStabilized, setCueStabilized] = useState(false);
    const [cueMuxConnected, setCueMuxConnected] = useState(false);
    const [driveImages, setDriveImages] = useState([]);
    const [imagesLoading, setImagesLoading] = useState(false);
    const [showImagePicker, setShowImagePicker] = useState(false);
    const [userReminders, setUserReminders] = useState(new Set());
    // Reminder feedback toast — surfaces when push permission is required,
    // when the reminder is set successfully, or when push isn't supported on
    // this device. Auto-dismisses via the effect below. Replaces the previous
    // silent-failure path where "Reminder set ✓" lied to users whose push
    // wasn't actually enabled.
    const [reminderToast, setReminderToast] = useState(null);
    useEffect(() => {
        if (!reminderToast) return undefined;
        const t = setTimeout(() => setReminderToast(null), 5000);
        return () => clearTimeout(t);
    }, [reminderToast]);


    // ── On demand state ──
    const [freeVideos, setFreeVideos] = useState([]);
    const [classRecordings, setClassRecordings] = useState([]);
    const [onDemandLoading, setOnDemandLoading] = useState(true);
    const [onDemandLoaded, setOnDemandLoaded] = useState(false); // true after first successful fetch
    const [activeVideo, setActiveVideo] = useState(null);
    // Target reply id from a notification deep-link (?comment=) — handed to the
    // VideoPlayerModal's ClassComments to expand + scroll + highlight. Cleared on close.
    const [deepLinkCommentId, setDeepLinkCommentId] = useState(null);
    const [completedIds, setCompletedIds] = useState(new Set());
    // Per-video completion history { [videoId]: { count, lastCompletedAt } } for
    // the VideoCard history chip (event-log model). See adr-completions-event-log.md.
    const [perVideoCompletions, setPerVideoCompletions] = useState({});
    // ── Series (free self-paced challenges — 4th tab) ──
    // Lazily loaded the first time the Series tab is opened (it's not the
    // default tab, so we never pay for it on a normal Workouts visit).
    // Completions for a series day carry programId === seriesId (the opaque
    // scope token), so they NEVER mix with the programId-less On Demand index
    // above. See docs/decisions/adr-series.md.
    const [seriesList, setSeriesList] = useState([]);
    const [seriesLoaded, setSeriesLoaded] = useState(false);
    const [seriesLoading, setSeriesLoading] = useState(false);
    const [selectedSeriesId, setSelectedSeriesId] = useState(null); // null = list view
    const [seriesVideos, setSeriesVideos] = useState([]);            // ordered days for the open series
    const [seriesVideosLoading, setSeriesVideosLoading] = useState(false);
    const [seriesVideosLoadedFor, setSeriesVideosLoadedFor] = useState(null); // which SERIES seriesVideos belongs to (days can mix Bunny + recorded, so the series is the cache key)
    // Per-series completion counts: { [seriesId]: { [videoId]: { count, lastCompletedAt } } }
    const [seriesCompletion, setSeriesCompletion] = useState({});
    const [activeSeriesVideo, setActiveSeriesVideo] = useState(null); // day video open in the modal
    // Set when a ?series= deep link resolves to nothing the viewer may see
    // (deleted, still a draft, or a typo'd slug). Drives one calm line on the
    // list view — a bad link never produces an error screen or a blank surface.
    const [seriesLinkMissed, setSeriesLinkMissed] = useState(false);
    // Responsive: the Workouts segmented control switches from a centered pill
    // (desktop) to a full-width equal-segment control (mobile) so 4 tabs don't
    // overflow a phone screen. matchMedia keeps it in sync without a resize spam.
    const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 600px)').matches);
    // Pinned tab bar: sticks under AppNav's top bar (70px desktop / 52px <=768px)
    // and densifies its glass once pinned. See the sticky wrapper.
    const [isNarrowNav, setIsNarrowNav] = useState(() => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(max-width: 768px)').matches);
    const [tabsPinned, setTabsPinned] = useState(false);
    const tabsSentinelRef = useRef(null);
    const [odLengthFilter, setOdLengthFilter] = useState([]);
    const [odGroupFilter, setOdGroupFilter] = useState([]);
    const [odSortBy, setOdSortBy] = useState('newest');
    const [odCompletedFilter, setOdCompletedFilter] = useState(null);
    const [odFavoritedFilter, setOdFavoritedFilter] = useState(null);
    const [odEaseInFilter, setOdEaseInFilter] = useState(null);
    const [odStrongPlusFilter, setOdStrongPlusFilter] = useState(null);

    // ── Thumbnail picker state (admin only) ──
    // { id, playbackId, durationSeconds, isRecording } or null
    const [thumbnailPicker, setThumbnailPicker] = useState(null);

    // ── Class metrics drill-down state (admin only) ──
    // When set to a classId, ClassMetricsModal mounts and loads metrics for that
    // class. See docs/decisions/adr-class-metrics.md.
    const [metricsClassId, setMetricsClassId] = useState(null);
    // ── Re-air a past class (admin-only) ──
    // reAirOpen mounts the modal; reAirSeed pre-selects a recording when the flow
    // is launched from an On Demand card (null = open on the picker step).
    const [reAirOpen, setReAirOpen] = useState(false);
    const [reAirSeed, setReAirSeed] = useState(null);

    // ── Locked video paywall modal ──
    const [lockedVideo, setLockedVideo] = useState(null);
    const navigate = useNavigate();
    // Router location — the deep-link receiver below MUST react to in-app
    // navigation (notification bell → /workouts?class=…) and not just to a fresh
    // mount. Reading window.location alone only works on a cold load.
    const location = useLocation();

    // ── Draft preview / publish modals (admin-only) ──
    // previewDraft: the draft class whose recording we're previewing in a Mux
    //   Player modal. Cleared on close.
    // publishConfirm: the draft class we're about to publish. Cleared on cancel
    //   or after the publish completes.
    // livePlayerTestDraft: the draft class we're watching via LivePlayer (the
    //   real viewer-mode player wrapped around the draft's recording, so admin
    //   can apples-to-apples verify LivePlayer behaviors — playback locks,
    //   auto-fullscreen on rotate, auto-exit fullscreen on end — WITHOUT
    //   exposing a class to real users. Drafts are filtered from upcoming so
    //   nobody else sees the class.
    const [previewDraft, setPreviewDraft] = useState(null);
    const [publishConfirm, setPublishConfirm] = useState(null);
    const [publishing, setPublishing] = useState(false);
    const [livePlayerTestDraft, setLivePlayerTestDraft] = useState(null);

    // ── Class detail modal (2026-04-26) ──
    // Opens when a viewer clicks a scheduled or waiting-room-open tile. Surfaces
    // title, full date/time, description, metadata, and a context-aware primary
    // CTA. Information is identical for subscribers and non-subscribers; only
    // the primary action differs (Subscribe vs Set reminder vs Join Waiting Room).
    const [classDetailModal, setClassDetailModal] = useState(null);

    // ── End Class confirmation modal ──
    // Admin clicks End Class → this modal appears → admin must explicitly confirm.
    // Prevents accidental end-of-class clicks (mid-broadcast misclicks are catastrophic
    // since handleEndClass immediately seals the Mux manifest via /complete and stops
    // the broadcaster — there is no undo).
    // Value: null (hidden) | classItem (shown, about to end this class).
    const [endClassConfirm, setEndClassConfirm] = useState(null);
    // Delete safety (2026-10-13, after an accidental delete of a real class):
    // every admin Delete opens a confirm first. Holds the class being confirmed.
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [deleting, setDeleting] = useState(false);
    // Non-blocking banner shown while Mux wraps up the stream in the background after
    // admin ends a class. Admin can navigate freely; banner auto-dismisses at T+15s,
    // well past the LiveBroadcaster 5s WS flush + admin UI unmount. Value: null (hidden)
    // | { title, subtext } (shown).
    const [endingBanner, setEndingBanner] = useState(null);

    // ── Pre-recorded RECORDING-phase end signal (Option A unification) ──
    // For live classes, LiveBroadcaster's stopBroadcast is triggered by
    // `activeClass.endingAt` transitioning from null to a value. Pre-recorded
    // classes in the RECORDING phase have NO activeClass (status is still
    // 'scheduled' — auto-flip to 'live' only happens at scheduledAt). So we
    // carry the end-signal in a separate piece of state and fold it into the
    // endingAt prop when the broadcaster is wired up. Transitions from null →
    // timestamp once per recording session; reset to null after the broadcaster
    // is torn down (T+6s) so a subsequent record starts cleanly.
    const [recordingEndingAt, setRecordingEndingAt] = useState(null);
    // Ref guard — set true the moment handleEndRecording starts its seal-before-
    // stop flow, so when LiveBroadcaster's subsequent onBroadcastEnd fires (5s
    // after MediaRecorder flushes) we DON'T double-fire /complete + a second
    // Firestore snapshot listener via the legacy handleStopRecording path.
    // A ref (not state) — closures inside onBroadcastEnd read the live value.
    const recordingEndingRef = useRef(false);

    // ── Pre-recorded Direct Upload state (2026-04-21) ──
    // Populated when LiveBroadcaster hands us the recorded Blob via onRecordingComplete.
    // Drives the upload banner + beforeunload guard while the PUT to Mux runs.
    // Shape: { classId, classTitle, progress (0-1), loadedBytes, totalBytes, phase, error, blob, mimeType }
    //   phase: 'requesting' | 'uploading' | 'processing' | 'done' | 'error'
    //   blob/mimeType retained for retry so a transient network failure doesn't force a re-record.
    const [uploadingClass, setUploadingClass] = useState(null);

    // ── Pre-recorded recovery modal state (2026-04-23) ──
    // If the admin's previous recording session ended with unuploaded chunks
    // in IDB (tab closed, component unmounted, upload never completed), we
    // surface a prompt on next admin mount letting them either upload what
    // was captured or discard the buffer. Shape:
    //   null — no pending recovery
    //   { classId, classTitle, chunkCount, startedAt, lastChunkAt,
    //     estimatedSeconds, mimeType, classExists } — show modal with these
    // Only ever shows ONE at a time (the unlikely case of multiple pending
    // recoveries is handled by re-scanning after the admin acts on the first).
    const [pendingRecoveryRecording, setPendingRecoveryRecording] = useState(null);
    const [recoveryBusy, setRecoveryBusy] = useState(false);
    // Set true after we've already scanned IDB in this session so effect
    // re-renders don't re-scan continuously (classes changes every focus tick).
    const recoveryScannedRef = useRef(false);
    // XHR instance for the in-flight upload so we can abort on admin cancel/unmount.
    const uploadXhrRef = useRef(null);

    // ── Favorites state ──
    const [userFavorites, setUserFavorites] = useState(new Set());
    const [userVideoFavorites, setUserVideoFavorites] = useState(new Set());
    const [videoLikeCounts, setVideoLikeCounts] = useState({});

    // ── Access / purchase state ──
    const [purchasedClassIds, setPurchasedClassIds] = useState(new Set());
    const [purchaseLoading, setPurchaseLoading] = useState(false);
    const [siteConfig, setSiteConfig] = useState({});
    // Tracks whether siteConfig has resolved — the ?tab=series deep-link must wait
    // for enableSeriesTab to be known before it decides whether to honor or ignore.
    const [siteConfigLoaded, setSiteConfigLoaded] = useState(false);
    const [successToast, setSuccessToast] = useState('');

    // Subscription checkout error toast — surfaces "couldn't start checkout"
    // failures inline (auto-dismisses after 5s, click to dismiss). Subscription
    // checkouts go straight to Stripe; express consent was captured at signup
    // via Clerk's checkbox (see `adr-waiver-clerk-express-consent.md`).
    const [checkoutErrorToast, setCheckoutErrorToast] = useState('');
    useEffect(() => {
        if (!checkoutErrorToast) return undefined;
        const t = setTimeout(() => setCheckoutErrorToast(''), 5000);
        return () => clearTimeout(t);
    }, [checkoutErrorToast]);

    // ── Upcoming week-ribbon day filter ──
    // Clicking a day in the ribbon filters the tile grid to just that day's classes. Clicking
    // the same day again (or the "Show full calendar" pill) clears the filter and restores the
    // full chronological schedule. Only days with classes are clickable.
    const [selectedDay, setSelectedDay] = useState(null); // YYYY-MM-DD or null
    // Hover lives in React state rather than inline DOM mutation. The old `e.currentTarget.style`
    // approach leaked — a cell tinted on hover would retain its inline background after selection
    // changed, making a previously-selected day still look active. React-controlled styles always
    // reflect current state.
    const [hoveredDayKey, setHoveredDayKey] = useState(null);

    // ── Admin affordance refs ──
    // Clicking "Edit" or "Open Broadcaster" from far down the list would otherwise silently
    // mount these panels at the top of the page with no visible feedback. We auto-scroll to
    // them and briefly pulse so admin sees something happened.
    const broadcasterRef = useRef(null);
    const createFormRef = useRef(null);
    const [adminPulse, setAdminPulse] = useState(null); // 'broadcaster' | 'createForm' | null

    // ── Active-class auto-scroll ──
    // Clicking "Join Waiting Room" (or any state transition that sets activeClass) from a tile
    // deep in the grid would otherwise leave the user scrolled to the tile with the waiting-room
    // player silently mounted at the top. We scroll the active-class wrapper into view whenever
    // activeClass.id transitions to a new (truthy) value. Leaving (null) intentionally does NOT
    // scroll — user should stay where they were browsing.
    //
    // We use getBoundingClientRect + window.scrollTo with an 80px offset (matches the broadcaster
    // scroll pattern) so the sticky desktop top nav doesn't cover the top of the player. Plain
    // `scrollIntoView({ block: 'start' })` left the player partially behind the nav — the user
    // reported feeling that "something scrolled but not enough to focus on the player".
    const activeClassRef = useRef(null);
    const prevActiveClassIdRef = useRef(null);
    // Hold broadcastingClassId in a ref so this effect doesn't re-scroll when it
    // changes (admin opening/closing the broadcaster panel has its own scroll effect).
    // We only read it at the moment activeClass transitions, to decide whether to skip.
    const broadcastingClassIdRef = useRef(null);
    useEffect(() => {
        const newId = activeClass?.id || null;
        if (newId && newId !== prevActiveClassIdRef.current) {
            // If admin is actively broadcasting THIS class, they're working in the
            // broadcaster panel above — do NOT scroll away to the viewer-preview
            // player. That was causing admins to miss the "broadcasting" confirmation
            // because the page would jump to the viewer preview the moment they hit
            // Go Live. The broadcaster-open effect has already scrolled them to the
            // right place.
            if (broadcastingClassIdRef.current === newId) {
                prevActiveClassIdRef.current = newId;
                return;
            }
            // rAF waits for the wrapper to mount so the ref is attached before we scroll.
            requestAnimationFrame(() => {
                const el = activeClassRef.current;
                if (!el) return;
                const y = el.getBoundingClientRect().top + window.scrollY - pinnedHeaderOffset();
                window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
            });
        }
        prevActiveClassIdRef.current = newId;
    }, [activeClass?.id]);
    // Keep the ref in sync with broadcastingClassId so the scroll decision above reads fresh.
    useEffect(() => { broadcastingClassIdRef.current = broadcastingClassId; }, [broadcastingClassId]);

    // ── Schedule page anchor (2026-05-04 weekly-rhythm) ──
    // On the user's first arrival at the Schedule tab (per page lifetime), scroll the
    // page so today's day-section is the topmost visible section. With the aired-this-week
    // change, Monday's class can sit above today's section in the grid — without an
    // anchor, users would land at the top (Monday's tile) and have to scroll past past
    // classes to find what's next.
    //
    // Anchor target priority:
    //   1. Section matching today's dateKey (today has classes)
    //   2. First section whose dateKey >= today (today is empty; lands on next-upcoming)
    //   3. None: leave page at top (no future sections — empty week + no future)
    //
    // Skip rules:
    //   - selectedDay set: filter view is the user's explicit position; don't override.
    //   - activeTab !== 'live': anchor doesn't apply to On Demand / Shorts surfaces.
    //   - Already anchored this page lifetime: tab-switching back must NOT yank scroll.
    //
    // Layout effect (not effect): scroll positioning happens BEFORE paint, so users
    // never see the un-anchored top-of-page state flash.
    const hasAnchoredScheduleRef = useRef(false);
    useLayoutEffect(() => {
        if (hasAnchoredScheduleRef.current) return;
        if (activeTab !== 'live') return;
        if (loading) return;
        // selectedDay set BEFORE first anchor: user has chosen a filter explicitly. Skip
        // anchor and lock the flag so we don't anchor when they later clear the filter.
        if (selectedDay) {
            hasAnchoredScheduleRef.current = true;
            return;
        }
        const todayKey = dateKey(new Date());
        let target = document.querySelector(`[data-section-key="${todayKey}"]`);
        if (!target) {
            // Find the first section whose dateKey is >= today. dateKey format
            // (YYYY-MM-DD, zero-padded) is lexicographically sortable.
            const allSections = document.querySelectorAll('[data-section-key]');
            for (const sec of allSections) {
                const k = sec.getAttribute('data-section-key');
                if (k && k >= todayKey) { target = sec; break; }
            }
        }
        if (!target) {
            // No today and no future sections (e.g. only past-this-week tiles exist
            // — unusual, but possible early Monday before this week's classes are
            // scheduled). Lock the flag so we don't keep retrying every render.
            hasAnchoredScheduleRef.current = true;
            return;
        }
        // 80px offset matches the activeClassRef scroll pattern (clears the sticky
        // desktop top nav). Use legacy two-arg scrollTo for guaranteed-instant
        // behavior across browsers — the object form's default behavior varies and
        // a smooth-animated scroll on first paint reads as uncanny.
        const y = target.getBoundingClientRect().top + window.scrollY - pinnedHeaderOffset();
        window.scrollTo(window.scrollX, Math.max(0, y));
        hasAnchoredScheduleRef.current = true;
    }, [loading, activeTab, selectedDay]);

    // ── D-053: Cue reset on broadcaster close ──
    // When admin closes the broadcaster panel (broadcastingClassId flips to null), wipe
    // any residual cue state so a subsequent session starts from step 1 'connecting'.
    // BroadcasterCueSequence also resets internally on `active=false`, but clearing
    // wsOpen/stabilized/muxConnected prevents a flash of advanced steps on re-open.
    useEffect(() => {
        if (!broadcastingClassId) {
            setCueActive(false);
            setCueWsOpen(false);
            setCueStabilized(false);
            setCueMuxConnected(false);
        }
    }, [broadcastingClassId]);

    // ── Day ribbon horizontal scroll state ──
    // Drives the chevron arrow visibility + edge fade gradients. We update on mount, on scroll,
    // and on resize so the arrows hide cleanly when you reach an end.
    const ribbonScrollRef = useRef(null);
    const [ribbonScrollState, setRibbonScrollState] = useState({ canLeft: false, canRight: false });

    // ── Day ribbon responsive cell sizing ──
    // We size cells so that exactly 7 fit across the ribbon's measured width at any viewport.
    // On mobile where 7 would make cells too tiny, we clamp to a readable minimum and let swipe
    // handle the rest. ResizeObserver keeps this in sync with container resizes.
    const [ribbonMetrics, setRibbonMetrics] = useState({ cellWidth: 58, gap: 6 });

    const { getToken } = useAuth();
    const clerkData = useUser();
    let isAdmin = false;
    let isFriend = false;
    let userId = '';
    let userEmail = '';
    let clerkUser = null;
    if (!demoMode) {
        const user = clerkData?.user;
        isAdmin = user?.publicMetadata?.role === 'admin';
        isFriend = user?.publicMetadata?.role === 'friend';
        userId = user?.id || '';
        userEmail = user?.primaryEmailAddress?.emailAddress || '';
        clerkUser = user;
    } else {
        isAdmin = true;
        isFriend = false;
        userId = 'demo';
    }

    // Preview mode: admin can see app as a regular user would
    const previewAsUser = typeof window !== 'undefined' && localStorage.getItem('mom-preview-as-user') === 'true';
    if (previewAsUser && isAdmin) { isAdmin = false; }

    const isSubscriber = !demoMode && hasActiveSubscription(clerkUser);
    const hasAccess = isAdmin || isFriend;
    // Configurable subscription price label — used across all subscribe buttons/banners
    const priceLabel = siteConfig.subscriptionPriceLabel || '$15/mo';
    // Yearly plan (docs/ux/annual-plan.md). Only fetched for non-members when
    // the admin flag is on. null ⇒ every subscribe button is today's one-tap
    // monthly checkout; non-null ⇒ the button opens the plan sheet first.
    const annualOffer = useAnnualOffer(!demoMode && siteConfig.enableAnnualPlan === true && !isSubscriber && !hasAccess);
    const [planPickerFor, setPlanPickerFor] = useState(null); // { classItem } while the plan sheet is open
    // Admin-editable completion-control copy (event-log model). Undefined keys fall
    // back to VideoCard's built-in defaults. See adr-completions-event-log.md.
    const completionCopy = {
        logLabel: siteConfig.completionLogLabel,
        logAgainLabel: siteConfig.completionLogAgainLabel,
        loggedLabel: siteConfig.completionLoggedLabel,
        undoLabel: siteConfig.completionUndoLabel,
        removeConfirmLabel: siteConfig.completionRemoveConfirmLabel,
        removeKeepLabel: siteConfig.completionRemoveKeepLabel,
        historyDisclosureLabel: siteConfig.completionHistoryDisclosureLabel,
        historyRemoveLabel: siteConfig.completionHistoryRemoveLabel,
    };
    // L-1: Configurable subscribe CTA verb — unifies the word used across all subscribe CTAs.
    // Falls back to "Subscribe" (current default) so behavior is identical when unset.
    const subscribeCta = siteConfig.subscribeCtaLabel || 'Subscribe';
    const tellMeMoreCta = siteConfig.tellMeMoreCtaLabel || 'Tell me more';

    // Check if user can access a specific class
    const userCanAccessClass = (classId) => {
        if (isAdmin || isFriend || isSubscriber) return true;
        // Free classes are open to everyone
        const cls = classes.find((c) => c.id === classId);
        if (cls && (cls.price === 0 || cls.price === '0')) return true;
        return purchasedClassIds.has(classId);
    };

    // Check if a class is free
    const isClassFree = (classId) => {
        const cls = classes.find((c) => c.id === classId);
        return cls && (cls.price === 0 || cls.price === '0');
    };

    // ══════════════════════════════════════════════
    //  LIVE CLASSES TAB — DATA LOADING
    // ══════════════════════════════════════════════

    const loadClasses = useCallback(async () => {
        const all = await fetchLiveClasses();

        // Pre-recorded airtime is applied for DISPLAY only (withAirtime) — this
        // device never writes a class's state. The server cron owns it.
        for (let i = 0; i < all.length; i++) {
            all[i] = withAirtime(all[i]);
            // NOTE: the client-side `live → ended` accelerator was REMOVED here
            // (incident 2026-05-29). It decided the terminal transition off the
            // browser's local clock (`now`) and wrote a globally-SHARED
            // `status: 'ended'` to Firestore. A participant whose device clock ran
            // ~4 min fast flipped the shared doc early; the viewer onSnapshot then
            // fanned `ended` to the entire cohort, cutting off a pre-recorded class
            // ~4 min early for everyone at the same content position. The VOD was
            // perfect — only the LIVE surface was truncated.
            //
            // The `live → ended` transition is owned exclusively by the server cron
            // (`api/send-class-reminder.js`), which uses a TRUSTED clock and the same
            // videoDurationSeconds-preferred end time. Losing the client accelerator
            // only delays the SHARED status flip by up to one cron tick (≤5 min),
            // which has no user-visible impact: per-viewer class-end is driven by the
            // viewer's own Mux Player `ended` event (overlay + `onStreamEnded` →
            // `classEndedLocally` comment unlock), not by the shared status field.
            // See the `classEndedLocally` comment block for the local-gate rationale.
        }

        // ── Admin-restart un-latch (the ONLY sanctioned un-latch) ──
        // Detect the shared-state edge ended/ending → live on the same class doc
        // (admin deliberately re-goes-live). Clear the per-viewer completion latch
        // so the viewer can rejoin the restarted live surface. The cron-lag window
        // can NEVER produce this edge — there the shared doc goes scheduled → live
        // → ended and never back to live — so this never spuriously un-latches a
        // genuinely-finished class. See adr-class-completion-absorbing-state.md.
        for (const c of all) {
            const prevS = prevClassStatesRef.current.get(c.id);
            const curS = c.state || c.status;
            if ((prevS === 'ended' || prevS === 'ending') && curS === 'live') {
                clearClassCompleted(c.id);
            }
            prevClassStatesRef.current.set(c.id, curS);
        }

        setClasses(all);
        // Auto-activate a class that is truly LIVE — but NOT one in the `ending`
        // drain window. If a user navigates away from an ended/ending class and
        // returns, we want them back on the fresh Upcoming view, not staring at
        // the draining player. Users already watching a live class that transitions
        // to `ending` keep their player via the block below (state-propagation
        // into the existing activeClass); this guard only affects viewers who
        // return AFTER activeClass has been cleared.
        //
        // Absorbing completion: exclude classes this viewer has already finished
        // (latch / shared-ended / trusted-clock past-end) from the auto-activate
        // candidate. This is the fix for the "class started over on re-entry"
        // bug — a finished viewer (or a fresh login during the cron-lag window)
        // is never re-pulled onto the live surface; re-entry routes to On Demand.
        // The "already watching" preserve branch below is untouched, so a viewer
        // actively on the thank-you overlay keeps their player, overlay, and the
        // open comment thread exactly as today.
        //
        // CONCURRENT CLASSES: auto-activate only when there is EXACTLY ONE candidate.
        // With two or more live at once, dropping the viewer into whichever class
        // happens to sort first (scheduledAt asc, ties by doc id) is arbitrary — and
        // worse, the replace below would keep yanking them back out of the one they
        // chose. So with 2+ we auto-activate nothing and the viewer picks from the
        // Schedule (live tiles grow an explicit "Join" CTA in that case only).
        // With 0 or 1 candidates this is identical to the previous `.find()`.
        //
        // The canSeeClass filter also stops a gated class from winning the pick and
        // then being cleared by the visibility guard — which left the viewer on
        // nothing instead of on the class actually meant for them.
        const liveCandidates = all.filter((c) => {
            const s = c.state || c.status;
            return s === 'live'
                && canSeeClass(c, { userId, isAdmin })
                && !isClassCompleteForViewer(c)
                // A release-day class never airs, so it never pulls anyone onto the
                // live player — even if its stored state ever reads 'live' (e.g. the
                // previous cron during rollout). Always true for every other class.
                && !isReleaseDayClass(c);
        });
        const live = liveCandidates.length === 1 ? liveCandidates[0] : null;
        if (live) {
            setActiveClass((prev) => {
                const prevS = prev?.state || prev?.status;
                const liveS = live.state || live.status;
                if (prev?.id === live.id) {
                    // Same class — keep the viewer's own object so their player isn't
                    // rebuilt, UNLESS the shared doc advanced (live → ending), which must
                    // propagate so LivePlayer enters the graceful drain.
                    return prevS === liveS ? prev : live;
                }
                // DIFFERENT class: never pull a viewer off one they're actively watching.
                // `prevStillExists` keeps the pre-existing behavior for a class deleted
                // mid-air (prev vanishes from `all` ⇒ fall through and re-home them).
                const prevStillExists = prev && all.some((c) => c.id === prev.id);
                if (prevStillExists && (prevS === 'live' || prevS === 'ending')) return prev;
                return live;
            });
        }
        // If user was watching a class that ended or was deleted, handle it
        setActiveClass((prev) => {
            if (!prev) return prev;
            const updated = all.find((c) => c.id === prev.id);
            if (!updated) return null; // class was deleted while away
            const updatedS = updated.state || updated.status;
            const prevS = prev.state || prev.status;
            if (updatedS === 'ended' && prevS !== 'ended') {
                // Class ended — keep active so users can comment post-class
                return { ...prev, state: 'ended', status: 'ended' };
            }
            if (updatedS === 'ending' && prevS === 'live') {
                // Drain window began — propagate endingAt so LivePlayer enters graceful-ending mode
                return { ...prev, state: 'ending', status: 'ending', endingAt: updated.endingAt || prev.endingAt };
            }
            return prev;
        });
        setLoading(false);
        // userId/isAdmin are now read inside (canSeeClass) — they MUST be deps or the
        // callback would close over stale identity and mis-gate visibility. They settle
        // once when Clerk resolves (and on the admin preview-as-user toggle), so this
        // costs one extra refresh at sign-in, not per-render churn.
    }, [userId, isAdmin]);

    useEffect(() => { loadClasses(); }, [loadClasses]);

    // Release-day classes open at the viewer's own 12:00 AM (services/classRelease.js).
    // If the page is open across that moment, re-render once so the tile flips to
    // "New today" by itself. One timer, set for the next opening only; display-only.
    const [releaseTick, setReleaseTick] = useState(0);
    useEffect(() => {
        const nowMs = serverNow();
        let next = Infinity;
        for (const c of classes) {
            if (!isReleaseDayClass(c)) continue;
            const [y, m, d] = c.releaseDate.split('-').map((n) => parseInt(n, 10));
            const at = new Date(y, m - 1, d).getTime();
            if (at > nowMs && at < next) next = at;
        }
        const wait = next - nowMs;
        if (!Number.isFinite(wait) || wait > 24 * 60 * 60 * 1000) return undefined;
        const t = setTimeout(() => setReleaseTick((n) => n + 1), wait + 1000);
        return () => clearTimeout(t);
    }, [classes, releaseTick]);

    // Sync the authoritative server clock on mount so the scheduled→live poll backup
    // and fire-at-airtime use it from the first tick. Coalesced + cached at the module
    // level; degrades to the device clock on failure (serverNow() === Date.now()).
    useEffect(() => { syncServerClock().catch(() => {}); }, []);

    // ── Aired-this-week recording refetch (2026-04-26 → widened to current week 2026-05-04) ──
    // The mux-webhook simultaneously (a) creates the classRecordings doc and
    // (b) sets vodReady: true on the source liveClasses doc. The class doc's
    // onSnapshot listener catches (b) instantly, but classRecordings is only
    // fetched once on mount (loadOnDemand), so a class that ends while the
    // user is on the Schedule tab would observe vodReady=true with no matching
    // recording in local state — leaving the tile stuck on "Class Recording
    // Finalizing" until hard refresh.
    //
    // Strategy: rate-limit refetches to once per 30s globally (NOT per class)
    // and cap total attempts at 6 per class (3 minutes of retry window). This
    // covers the realistic webhook-slow window without spamming Firestore.
    // After cap, the user can manually refresh; the tile copy makes it clear
    // a brief wait is expected.
    //
    // Window widened to current-week (Mon→Sun local) parity with the upcoming
    // filter — a Monday-aired class that hasn't fully finalized by Tuesday
    // morning still gets rate-limited refetch attempts since its tile is now
    // visible on Schedule all week.
    const recordingRefetchAttemptsRef = useRef(new Map()); // classId → attempt count
    const lastRecordingRefetchAtRef = useRef(0);
    useEffect(() => {
        if (!classes.length) return;
        const now = new Date();
        // Mirror the Monday-start week math used by `isAiredThisWeek` in the upcoming filter.
        const dayOfWeek = now.getDay();
        const daysSinceMonday = (dayOfWeek + 6) % 7;
        const weekStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysSinceMonday).getTime();
        const weekEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysSinceMonday + 7).getTime();
        const MAX_ATTEMPTS_PER_CLASS = 6;
        const MIN_REFETCH_INTERVAL_MS = 30000;
        const stale = classes.find((c) => {
            const attempts = recordingRefetchAttemptsRef.current.get(c.id) || 0;
            if (attempts >= MAX_ATTEMPTS_PER_CLASS) return false;
            const s = c.state || c.status;
            if (s !== 'ended' || !c.vodReady) return false;
            if (!c.scheduledAt) return false;
            const t = new Date(c.scheduledAt).getTime();
            if (t < weekStart || t >= weekEnd) return false;
            return !classRecordings.some((r) => r.classId === c.id);
        });
        if (stale && Date.now() - lastRecordingRefetchAtRef.current >= MIN_REFETCH_INTERVAL_MS) {
            lastRecordingRefetchAtRef.current = Date.now();
            const prevAttempts = recordingRefetchAttemptsRef.current.get(stale.id) || 0;
            recordingRefetchAttemptsRef.current.set(stale.id, prevAttempts + 1);
            fetchClassRecordings()
                .then((recs) => {
                    if (!recs) return;
                    setClassRecordings(recs);
                    // On success, reset attempt counter so future stale states (different
                    // class same session) start with a fresh budget.
                    if (recs.some((r) => r.classId === stale.id)) {
                        recordingRefetchAttemptsRef.current.delete(stale.id);
                    }
                })
                .catch(() => { /* swallow — next loadClasses tick will re-evaluate */ });
        }
    }, [classes, classRecordings]);

    // Re-check classes when user returns to the page
    // Uses both visibilitychange AND focus — some mobile browsers only fire one.
    //
    // COST OPTIMIZATION (D-069): debounce raised from 2s → 60s. The old 2s window
    // made phone-lock/unlock and quick tab-switch loops cause a full liveClasses
    // fetch almost every time. The onSnapshot listener on the active class doc
    // (see "Real-time listener" blocks below) already catches state transitions
    // within milliseconds while the listener is connected, so a 2s refetch-on-focus
    // was redundant for short absences. 60s preserves the "user returns after a
    // meaningful absence sees fresh data" contract — the only behavior lost is
    // sub-60s redundant refetches that served no user-visible purpose.
    useEffect(() => {
        let lastCheck = Date.now();
        const recheck = () => {
            if (Date.now() - lastCheck < 60000) return;
            lastCheck = Date.now();
            loadClasses();
        };
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') recheck();
        };
        const handleFocus = () => recheck();
        document.addEventListener('visibilitychange', handleVisibility);
        window.addEventListener('focus', handleFocus);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('focus', handleFocus);
        };
    }, [loadClasses]);

    // Periodic background check — catches status changes if real-time listener missed them
    // Only polls when on the live tab and not currently watching a live/ending stream.
    //
    // COST OPTIMIZATION (D-067): previously polled every 5s when the user was in
    // the waiting room (scheduled state) and every 15s otherwise. The 5s interval
    // was the single biggest Firestore read drain — one idle user in a waiting
    // room burned hundreds of reads per minute. The real-time listener at "Real-time
    // listener for scheduled → live transition" already flips the waiting room to
    // live within milliseconds of admin going live, making the 5s fallback
    // redundant for that specific case.
    //
    // We retain a single 60s fallback poll (12× slower than the old waiting-room
    // rate, 4× slower than general browsing) because the per-doc onSnapshot
    // cannot catch *new* classes being added by admin while the user is already
    // on the Upcoming tab. 60s ensures newly-added classes surface within a
    // minute without manual refresh, while cutting polling reads by 12×.
    useEffect(() => {
        if (activeTab !== 'live') return;
        const activeState = activeClass?.state || activeClass?.status;
        // Don't interrupt an active stream (live or ending drain window)
        if (activeState === 'live' || activeState === 'ending') return;
        const interval = setInterval(() => {
            loadClasses();
        }, 60000);
        return () => clearInterval(interval);
    }, [activeTab, activeClass?.status, activeClass?.state, loadClasses]);

    // Load siteConfig (for subscription flag) and user purchases
    useEffect(() => {
        fetchSiteContent().then((c) => { setSiteConfig(c || {}); setSiteConfigLoaded(true); });
        if (userId && !isAdmin && !isFriend) {
            getUserClassPurchases(userId).then((ids) => setPurchasedClassIds(ids));
        }
    }, [userId, isAdmin, isFriend]);

    // ── Deep-link: land on a specific tab from ?tab=<slug> or ?series=<token> ──
    // Responsibilities: (a) upgrade to the Series tab once we know it's available,
    // (b) apply a tab arriving from an IN-APP navigation, and (c) strip ?tab= so
    // refresh/back/re-share is clean. With no such param it's a complete no-op.
    // ?recording=/?class= win when present (resolveTabFromUrl returns null).
    //
    // The guard holds the last-handled NAVIGATION SIGNATURE, not a blanket boolean.
    // A boolean latched for the whole mount, so a link followed while already on
    // this page could never be honoured — the receiver-latch trap documented on the
    // ?recording= effect below, which bit us there on 2026-08-03.
    const tabDeepLinkHandledRef = useRef('');
    useEffect(() => {
        const navKey = (location && location.key) || 'default';
        // Initial location → use the snapshot taken during the first render, before
        // any sibling effect could rewrite the address bar (the address-bar sync
        // effect strips ?series= on mount; re-reading window.location on the
        // deferred pass is what broke member challenge links — see urlTabIntentRef).
        // A later in-app navigation → read the router's copy, which raw
        // replaceState never touches, so it reflects where we were just sent.
        const key = navKey === 'default'
            ? urlTabIntentRef.current
            : resolveTabFromUrl(location.search);
        const signature = `${navKey}|${key || ''}`;
        if (tabDeepLinkHandledRef.current === signature) return;
        if (!key) { tabDeepLinkHandledRef.current = signature; return; }
        if (key === 'series') {
            const seriesAvailable = isAdmin || siteConfig.enableSeriesTab;
            // Wait until siteConfig resolves before deciding — otherwise a real
            // ?tab=series link could be dropped on the first render. Once we know:
            // available → switch; off/hidden → leave the safe default (Schedule).
            // Deliberately does NOT latch here, so the deferred pass can decide.
            if (!seriesAvailable && !siteConfigLoaded) return;
            if (seriesAvailable) setActiveTab('series');
        } else {
            // On the initial load this is a no-op — the lazy initializer already
            // applied the same value. It matters only for an in-app navigation.
            setActiveTab(key);
        }
        tabDeepLinkHandledRef.current = signature;
        try {
            const url = new URL(window.location.href);
            url.searchParams.delete('tab');
            window.history.replaceState({}, '', url.pathname + url.search + url.hash);
        } catch (_) { /* non-critical — leave the URL as-is */ }
    }, [isAdmin, siteConfig, siteConfigLoaded, location]);

    // M-4: Keep a fresh reference to clerkUser for any async work that outlives a single
    // render. Closures that reach for `clerkUser` directly can go stale — the poll loop
    // below reads from this ref instead.
    const clerkUserRef = useRef(clerkUser);
    useEffect(() => { clerkUserRef.current = clerkUser; }, [clerkUser]);

    // ── Welcome modal (post-subscribe) ─────────────────────────────────
    // A member who has just paid gets a proper welcome instead of the old
    // receipt-style toast.
    //
    // The gate exists so the two confirmations never compete: while the modal
    // is up, the poll's success toast is dropped (the modal already said
    // welcome) and its "still finalizing" toast is DEFERRED until the modal
    // closes — a webhook lag is real information, it just must not land on top
    // of the welcome.
    const [welcomeOpen, setWelcomeOpen] = useState(false);
    const welcomeGateRef = useRef('off');       // 'off' | 'showing'
    const welcomeShownRef = useRef(false);
    const deferredToastRef = useRef(null);

    const showToastNow = useCallback((msg, ms) => {
        setSuccessToast(msg);
        setTimeout(() => setSuccessToast(''), ms);
    }, []);

    // dropIfWelcomed: this message is redundant once the modal has welcomed them.
    const announceToast = useCallback((msg, ms, dropIfWelcomed = false) => {
        if (welcomeGateRef.current !== 'off') {
            deferredToastRef.current = { msg, ms, dropIfWelcomed };
            return;
        }
        showToastNow(msg, ms);
    }, [showToastNow]);

    const flushDeferredToast = useCallback(() => {
        const d = deferredToastRef.current;
        deferredToastRef.current = null;
        if (!d) return;
        if (d.dropIfWelcomed && welcomeShownRef.current) return;
        showToastNow(d.msg, d.ms);
    }, [showToastNow]);

    // Check URL params for successful subscription redirect
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('subscribed') && userId) {
            // Record that this session came back from checkout. The welcome
            // effect below is the only reader; a member browsing normally never
            // has this marker, so the modal stays inert.
            if (!isAdmin) {
                markWelcomePending();
                // Only hand the toast over to the modal if the marker actually
                // took. Private-mode Safari throws on storage writes; without
                // this guard the gate would stay open forever and swallow the
                // confirmation the member is owed.
                if (hasWelcomePending()) welcomeGateRef.current = 'showing';
            }
            // L-2: If Stripe round-tripped a returnTab, restore the user to where they were
            // when they clicked subscribe (e.g. On Demand → back to On Demand). Only accept
            // known tab keys to avoid any stored-value surprises.
            const VALID_TABS = ['live', 'classes', 'shorts'];
            const returnTab = params.get('returnTab');
            if (returnTab && VALID_TABS.includes(returnTab)) {
                setActiveTab(returnTab);
            }
            // Poll Clerk metadata for subscription activation — webhook may lag.
            // C-3: Only claim "active" when we can actually confirm it. On timeout, tell the
            // user we're still finalizing rather than falsely claiming success.
            // M-4: Read clerkUser from ref so we always see the latest reference, even if
            // Clerk swaps the UserResource instance during the poll loop.
            let attempts = 0;
            let cancelled = false;
            const poll = async () => {
                if (cancelled) return;
                const user = clerkUserRef.current;
                if (user?.reload) await user.reload();
                if (cancelled) return;
                const fresh = clerkUserRef.current;
                if (fresh?.publicMetadata?.classSubscription) {
                    // Redundant if the welcome modal is (or is about to be) up.
                    announceToast('Welcome! Your subscription is active.', 4000, true);
                } else if (attempts >= 8) {
                    // Couldn't confirm — don't mislead the user.
                    announceToast("We're finalizing your subscription. Refresh in a moment — if it doesn't appear, please contact support.", 8000);
                } else {
                    attempts++;
                    setTimeout(poll, 2500);
                }
            };
            poll();
            window.history.replaceState({}, '', '/workouts');
            return () => { cancelled = true; };
        }
    }, [userId, isAdmin, announceToast]);

    // Open the welcome. Gated on the pending marker, so this is inert on a
    // normal Workouts visit. Also covers the /class/:id door, where this page
    // never sees ?subscribed=true itself: that page sets the marker and its
    // existing hand-off carries the member here.
    useEffect(() => {
        if (!userId || isAdmin) return;
        if (!hasWelcomePending()) return;
        welcomeGateRef.current = 'showing';
        welcomeShownRef.current = true;
        setWelcomeOpen(true);
    }, [userId, isAdmin]);

    // The modal can open before the live siteConfig fetch resolves. Painting
    // from the localStorage cache first (PERF-003) means an admin who has
    // customised this copy never sees their words swap in mid-view.
    const welcomeContent = useMemo(
        () => (siteConfigLoaded ? siteConfig : (getCachedSiteContent() || siteConfig)),
        [siteConfigLoaded, siteConfig],
    );

    // Button, ×, backdrop, Escape — all land here. The marker is cleared so a
    // remount in the same session can't re-open it.
    const handleWelcomeClose = useCallback(() => {
        setWelcomeOpen(false);
        welcomeGateRef.current = 'off';
        clearWelcomePending();
        flushDeferredToast();
    }, [flushDeferredToast]);

    // Load user's reminders
    useEffect(() => {
        if (!userId || isAdmin) return;
        getUserReminders(userId).then((reminders) => {
            setUserReminders(new Set(reminders.map((r) => r.classId)));
        });
    }, [userId, isAdmin]);

    // Load user's favorites (classes + videos)
    useEffect(() => {
        if (!userId) return;
        getUserFavorites(userId).then((favs) => setUserFavorites(favs));
        getUserVideoFavorites(userId).then((favs) => setUserVideoFavorites(favs));
    }, [userId]);

    // Real-time listener for scheduled → live transition
    useEffect(() => {
        const activeState = getClassState(activeClass);
        if (!activeClass || activeState !== 'scheduled' || !db) return;
        const unsubscribe = onSnapshot(firestoreDoc(db, 'liveClasses', activeClass.id), (snap) => {
            if (!snap.exists()) return;
            const data = snap.data();
            const newState = data.state || data.status;
            if (newState === 'live' || newState === 'ending') {
                // Flip to live immediately — LivePlayer shows the branded "Starting class…"
                // overlay during Mux ll-live propagation (~1-3s). Instant transition plus
                // alive feedback feels far better than a hardcoded wait.
                setActiveClass({ ...activeClass, ...data, id: activeClass.id, state: newState, status: newState });
                setClasses((prev) => prev.map((c) => c.id === activeClass.id ? { ...c, ...data, state: newState, status: newState } : c));
                unsubscribe();
            }
        });
        return () => unsubscribe();
    }, [activeClass]);

    // ── Fire-at-airtime (premiere start) ──
    // When a viewer holds a SCHEDULED, playable pre-recorded class, flip it to `live`
    // the instant airtime arrives — measured against the authoritative SERVER clock,
    // not the device clock. Eliminates the ≤60s lone-viewer lag where the start
    // otherwise waited on the 60s poll / ≤5-min cron / a peer's onSnapshot relay.
    //
    // Strictly ADDITIVE: the cron, the loadClasses poll backup, and the onSnapshot
    // listener all remain. This only makes the start earlier/more precise, never later.
    // It writes the SAME `status: 'live'` flip those paths already write (idempotent)
    // and NEVER writes a terminal state (honors the 2026-05-29 fix / LIVE-016). The
    // player's own seek-to-cohort-position decides WHERE the video starts. Degrades to
    // the device clock if the server-clock handshake hasn't completed.
    useEffect(() => {
        if (!activeClass || isAdmin) return undefined;
        const s = activeClass.state || activeClass.status;
        if (s !== 'scheduled') return undefined;
        if ((activeClass.classType || 'live') !== 'prerecorded') return undefined;
        if (!(activeClass.uploadStatus === 'ready' || activeClass.muxAssetId)) return undefined;
        if (!activeClass.scheduledAt) return undefined;
        const startMs = new Date(activeClass.scheduledAt).getTime();
        if (!Number.isFinite(startMs)) return undefined;
        const classId = activeClass.id;
        let cancelled = false;
        let timer = null;
        const fire = () => {
            if (cancelled) return;
            setActiveClass((prev) => (prev && prev.id === classId) ? { ...prev, state: 'live', status: 'live' } : prev);
            setClasses((prev) => prev.map((c) => c.id === classId ? { ...c, state: 'live', status: 'live' } : c));
            // DISPLAY ONLY — this device never writes the class's state (the
            // server cron does). See withAirtime.
        };
        const arm = () => {
            // Cap the armed delay (waiting room only opens ~15min out) — a safety
            // bound against absurdly long timers, not a functional limit.
            const delay = Math.min(Math.max(0, startMs - serverNow()), 20 * 60 * 1000);
            timer = setTimeout(fire, delay);
        };
        // Sync the clock first for accuracy, then arm. On failure, arm on the local clock.
        syncServerClock().catch(() => {}).then(() => { if (!cancelled) arm(); });
        return () => { cancelled = true; if (timer) clearTimeout(timer); };
    }, [activeClass, isAdmin]);

    // Real-time listener for live → ending / ended transitions (viewer).
    //
    // Three signals flow through this listener:
    //   1. `endingAt` (admin clicked End Class): propagate to activeClass so LivePlayer
    //      enters graceful-ending mode and drains the HLS buffer into the thank-you
    //      overlay at the natural end of the stream (when Mux fires `ended`), rather
    //      than being cut off mid-sentence.
    //   2. `state === 'ending'` (admin's End Class Phase 1): propagate the drain-window
    //      lifecycle. Player keeps rendering; reactions + presence continue.
    //   3. `state === 'ended'` (admin's delayed cleanup Phase 3): escape hatch if Mux
    //      never fires `ended`. LivePlayer's useEffect on status will clear
    //      gracefullyEndingRef and show the overlay.
    useEffect(() => {
        const activeState = getClassState(activeClass);
        // Guard: keep the listener alive during live OR ending; stop on ended/cancelled.
        if (!activeClass || (activeState !== 'live' && activeState !== 'ending') || isAdmin || !db) return;
        const unsubscribe = onSnapshot(firestoreDoc(db, 'liveClasses', activeClass.id), (snap) => {
            if (!snap.exists()) return;
            const data = snap.data();
            // Apply the same display-only airtime rule as loadClasses: while the
            // shared doc still says 'scheduled' (until the cron's next tick) an
            // aired class stays live here — never back to the waiting room.
            const shown = withAirtime({ ...activeClass, ...data });
            const newState = shown.state || shown.status;
            // Propagate endingAt the moment admin writes it (before state flips).
            if (data.endingAt && !activeClass.endingAt) {
                setActiveClass((prev) => prev ? { ...prev, endingAt: data.endingAt, state: newState, status: newState } : null);
            }
            // Also propagate the state transition itself (scheduled→live→ending→ended).
            if (newState && newState !== activeState) {
                setActiveClass((prev) => prev ? { ...prev, state: newState, status: newState, endingAt: prev?.endingAt || data.endingAt || null } : null);
                if (newState === 'ended') {
                    setClasses((prev) => prev.map((c) => c.id === activeClass.id ? { ...c, state: 'ended', status: 'ended' } : c));
                    unsubscribe();
                }
            }
        });
        return () => unsubscribe();
    }, [activeClass, isAdmin, loadClasses]);

    // Auto-clear an ended OR ending activeClass when the user navigates away from
    // the Live tab. Without this, switching to On Demand or Shorts and returning
    // would cause LivePlayer to remount with no play history (hasPlayedEverRef=false,
    // gracefullyEndingRef=false). With lifecycle='ending' normalized internally to
    // status='live', the player would mount against the already-sealed manifest and
    // start playing DVR content — giving the appearance of a live class happening
    // all over again, which is exactly the user-reported regression. `ended` was
    // already terminal here; broadening to `ending` means: once the class is in the
    // drain window AND the viewer leaves the Live tab, they've moved on. Returning
    // to Live shows the fresh Upcoming view. The recording surfaces in On Demand
    // once Mux processing completes.
    //
    // `live` and `scheduled` are still preserved so mid-class tab switches don't
    // kick viewers out. Admin's activeClass is also cleared by handleEndClass at
    // T+6s, but this effect serves as a defensive backup for them too.
    useEffect(() => {
        if (!activeClass) return;
        if (activeTab === 'live') return;
        const activeState = getClassState(activeClass);
        if (activeState === 'ended' || activeState === 'ending') {
            setActiveClass(null);
        }
    }, [activeTab, activeClass]);

    // ── Visibility guard (allowlist) ──
    // Never keep a class active that the current viewer isn't permitted to see.
    // Public classes (no visibleToUserIds) are a no-op, so this is zero-change for
    // normal classes. Belt-and-suspenders with the render-site gate below: covers
    // any path (auto-activate, realtime listener, 60s poll) that could otherwise
    // surface a gated class to a non-listed viewer.
    useEffect(() => {
        if (activeClass && !canSeeClass(activeClass, { userId, isAdmin })) {
            setActiveClass(null);
        }
    }, [activeClass, userId, isAdmin]);

    // Subscribe to viewer count (admin).
    // Prefers broadcastingClassId when admin is actively broadcasting; otherwise falls back to the
    // active class id so admin still gets a live count during the drain window and for pre-recorded
    // classes playing during their scheduled window (admin isn't broadcasting those — just observing).
    useEffect(() => {
        const activeState = getClassState(activeClass);
        const activeIsOngoing = activeState === 'live' || activeState === 'ending' || activeState === 'ended';
        const id = broadcastingClassId
            || (isAdmin && activeClass && activeIsOngoing ? activeClass.id : null);
        if (!isAdmin || !id) { setViewerCount(0); return; }
        const unsubscribe = subscribeToViewerCount(id, (count) => {
            setViewerCount(Math.max(0, count));
        });
        return () => unsubscribe();
    }, [isAdmin, broadcastingClassId, activeClass?.id, activeClass?.status, activeClass?.state]);

    // Scroll to broadcaster panel when admin opens it — and pulse briefly so they see it.
    // Requires a tick for the panel to mount before getBoundingClientRect is meaningful.
    useEffect(() => {
        if (!broadcastingClassId || !isAdmin) return;
        const raf = requestAnimationFrame(() => {
            const el = broadcasterRef.current;
            if (el) {
                const y = el.getBoundingClientRect().top + window.scrollY - pinnedHeaderOffset();
                window.scrollTo({ top: y, behavior: 'smooth' });
                setAdminPulse('broadcaster');
                setTimeout(() => setAdminPulse((p) => (p === 'broadcaster' ? null : p)), 1600);
            }
        });
        return () => cancelAnimationFrame(raf);
    }, [broadcastingClassId, isAdmin]);

    // Scroll to create/edit form when admin opens it. We key on `creating + editingClassId`
    // so switching from edit-mode to edit-mode (different class) re-triggers the scroll.
    useEffect(() => {
        if (!creating || !isAdmin) return;
        const raf = requestAnimationFrame(() => {
            const el = createFormRef.current;
            if (el) {
                const y = el.getBoundingClientRect().top + window.scrollY - pinnedHeaderOffset();
                window.scrollTo({ top: y, behavior: 'smooth' });
                setAdminPulse('createForm');
                setTimeout(() => setAdminPulse((p) => (p === 'createForm' ? null : p)), 1600);
            }
        });
        return () => cancelAnimationFrame(raf);
    }, [creating, editingClassId, isAdmin]);

    // Keep day-ribbon scroll state in sync — watch the ribbon element's scrollLeft / scrollWidth
    // so the chevron arrows and edge fades hide cleanly when you reach either end.
    // Also recompute cell width whenever the ribbon is resized so 7 cells always fit across.
    useEffect(() => {
        const el = ribbonScrollRef.current;
        if (!el) return;
        const updateScroll = () => {
            const canLeft = el.scrollLeft > 2;
            const canRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
            setRibbonScrollState((prev) => (prev.canLeft === canLeft && prev.canRight === canRight ? prev : { canLeft, canRight }));
        };
        const updateMetrics = () => {
            // Inner width available for cells: clientWidth - horizontal padding (16) - month label (~22) - gap after label.
            const inner = el.clientWidth - 16 - 22 - 6;
            // Target gap scales with cell size — wider cells get a bigger gap for breathing room.
            const targetGap = el.clientWidth > 520 ? 10 : 6;
            // 7 cells + 6 gaps between them = available - (6 * gap). Clamp to readable bounds.
            const proposed = Math.floor((inner - 6 * targetGap) / 7);
            const cellWidth = Math.max(58, Math.min(140, proposed));
            // If cells hit the min-clamp, gap stays tight too.
            const gap = cellWidth < 70 ? 6 : targetGap;
            setRibbonMetrics((prev) => (prev.cellWidth === cellWidth && prev.gap === gap ? prev : { cellWidth, gap }));
        };
        updateScroll();
        updateMetrics();
        el.addEventListener('scroll', updateScroll, { passive: true });
        window.addEventListener('resize', updateScroll);
        window.addEventListener('resize', updateMetrics);
        // ResizeObserver catches container-width changes that `window.resize` doesn't
        // (e.g. sidebar toggles, font loading shifts).
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateMetrics) : null;
        if (ro) ro.observe(el);
        return () => {
            el.removeEventListener('scroll', updateScroll);
            window.removeEventListener('resize', updateScroll);
            window.removeEventListener('resize', updateMetrics);
            if (ro) ro.disconnect();
        };
        // classes.length is a proxy for "ribbon re-rendered" — upcoming is derived from it.
        // activeTab change remounts the Upcoming pane so we re-attach listeners to the new element.
    }, [classes.length, activeTab]);

    // ── Day-ribbon initial scroll: align today's cell to the leftmost visible position.
    //
    // Background (2026-05-04 weekly-rhythm change): the ribbon now extends backward to
    // start-of-current-week so users can navigate to past days within the week. To
    // preserve the prior "today is anchored leftmost on default scroll" convention, we
    // programmatically scroll the ribbon so today is at the start of the visible window,
    // with past-this-week days reachable via left-chevron / drag.
    //
    // Fires once per ribbon mount: when ribbonScrollRef points to a new element (i.e.
    // the Upcoming pane re-mounted via tab switch), anchor and remember the element.
    // Subsequent re-renders against the SAME element preserve the user's manual scroll
    // position — so admin adding a class mid-session doesn't yank the user back to today.
    //
    // Uses useLayoutEffect to set scrollLeft BEFORE paint, avoiding any visible flash of
    // the un-anchored (scrollLeft=0) state.
    const ribbonAnchoredElementRef = useRef(null);
    useLayoutEffect(() => {
        if (activeTab !== 'live') return;
        const el = ribbonScrollRef.current;
        if (!el) return;
        // Already anchored this element? Don't yank the user's manual scroll position.
        if (ribbonAnchoredElementRef.current === el) return;
        // Wait for cellWidth to be measured (mount default of 58 is a placeholder; the
        // resize-observer effect computes the real cellWidth on first paint). If we
        // anchor before measurement, today's cell offset would be wrong.
        if (!ribbonMetrics.cellWidth) return;
        // Locate today's cell by data attribute. dateKey format is YYYY-MM-DD local.
        const today = new Date();
        const todayKey = dateKey(today);
        const cell = el.querySelector(`[data-day-key="${todayKey}"]`);
        if (!cell) {
            // No today cell? (Edge case: if weekDays generation ever produced a range
            // that excludes today.) Mark anchored and bail — preserves scrollLeft=0
            // default rather than flailing.
            ribbonAnchoredElementRef.current = el;
            return;
        }
        // Compute today's cell position relative to the ribbon's interior. Use
        // getBoundingClientRect for layout-agnostic positioning that doesn't depend on
        // offsetParent identity.
        const cellRect = cell.getBoundingClientRect();
        const containerRect = el.getBoundingClientRect();
        // Subtract container's 10px left padding (matches the inline `padding: '12px 10px'`
        // on the scroll container) so the cell sits flush at the visible left edge rather
        // than tucked behind the padding.
        const PADDING_LEFT = 10;
        const targetScrollLeft = el.scrollLeft + (cellRect.left - containerRect.left) - PADDING_LEFT;
        el.scrollLeft = Math.max(0, targetScrollLeft);
        ribbonAnchoredElementRef.current = el;
    }, [ribbonMetrics.cellWidth, ribbonMetrics.gap, classes.length, activeTab]);

    // Live presence — everyone (including admin) subscribes to the viewer list so they can see who's
    // in the room. Only non-admin users join the list themselves — admin is observing, not attending.
    // Pre-recorded classes playing during their scheduled window count as shared live moments too.
    // The waiting room (status='scheduled') is included so users who arrive early see each other
    // gathering — the shared-moment feeling starts before the class does.
    const presenceRef = React.useRef(null);
    useEffect(() => {
        const activeState = getClassState(activeClass);
        // Presence runs across scheduled (waiting room), live, and ending (drain) — not ended.
        const presenceActive = activeState === 'scheduled' || activeState === 'live' || activeState === 'ending';
        if (!activeClass || !presenceActive || !userId) {
            setLiveViewers([]);
            return;
        }

        // Everyone subscribes (so admin sees the list)
        const unsubscribe = subscribeToViewers(activeClass.id, (viewers) => {
            setLiveViewers(viewers);
        });

        // Only non-admin joins presence
        if (!isAdmin) {
            const firstName = clerkUser?.firstName || '';
            const lastInitial = (clerkUser?.lastName || '').charAt(0);
            presenceRef.current = { classId: activeClass.id, userId, firstName, lastInitial };
            joinPresence(activeClass.id, userId, firstName, lastInitial);
        }

        return () => {
            // Leave presence on cleanup (only if we joined)
            if (presenceRef.current) {
                const p = presenceRef.current;
                leavePresence(p.classId, p.userId, p.firstName, p.lastInitial);
                presenceRef.current = null;
            }
            unsubscribe();
            setLiveViewers([]);
        };
    }, [activeClass?.id, activeClass?.status, activeClass?.classType, isAdmin, userId, clerkUser?.firstName, clerkUser?.lastName]);

    // Also leave presence on page unload
    useEffect(() => {
        const handleUnload = () => {
            if (presenceRef.current) {
                const p = presenceRef.current;
                leavePresence(p.classId, p.userId, p.firstName, p.lastInitial);
            }
        };
        window.addEventListener('beforeunload', handleUnload);
        return () => window.removeEventListener('beforeunload', handleUnload);
    }, []);

    // ══════════════════════════════════════════════
    //  CLASSES / SHORTS — DATA LOADING
    //  Preloads on mount so data is ready when user switches tabs
    // ══════════════════════════════════════════════

    const isOnDemandTab = activeTab === 'classes' || activeTab === 'shorts';

    useEffect(() => {
        if (onDemandLoaded) return;
        async function loadOnDemand() {
            const [vids, recordings, completions] = await Promise.all([
                fetchFreeVideos(),
                fetchClassRecordings(),
                fetchUserCompletions(userId),
            ]);
            setFreeVideos(vids || []);
            setClassRecordings(recordings || []);
            // On Demand (class recordings + free) are programId-less completions.
            const { completedIds: ids, perVideo } = indexCompletions((completions || []).filter((c) => !c.programId));
            setCompletedIds(ids);
            setPerVideoCompletions(perVideo);
            setOnDemandLoaded(true);
            setOnDemandLoading(false);
            // Load like counts for shorts (non-blocking)
            if (vids && vids.length > 0) {
                getVideoLikeCounts(vids.map((v) => v.id)).then((counts) => setVideoLikeCounts(counts));
            }
        }
        loadOnDemand();
    }, [userId]);

    // ── Series: lazy load on first Series-tab open ──
    // Fetches the series list + the viewer's series completions (completions
    // carrying a programId === seriesId). Built into a per-series map so a day's
    // ✓ + the challenge progress bar render without a refetch.
    // NOTE: deps are intentionally [activeTab, seriesLoaded, userId] — NOT
    // seriesLoading. Listing seriesLoading would self-cancel: this effect SETS
    // seriesLoading(true) synchronously, which (if it were a dep) re-runs the
    // effect, whose cleanup flips `cancelled = true` and aborts the in-flight
    // fetch before it can clear the spinner → permanent spinner. `seriesLoaded`
    // alone is the correct re-entry guard (set true only after a load completes).
    useEffect(() => {
        if (activeTab !== 'series' || seriesLoaded) return;
        let cancelled = false;
        setSeriesLoading(true);
        (async () => {
            const [list, completions] = await Promise.all([
                fetchSeries(),
                userId ? fetchUserCompletions(userId) : Promise.resolve([]),
            ]);
            if (cancelled) return;
            setSeriesList(list || []);
            // Build { [seriesId]: { [videoId]: { count, lastCompletedAt } } } from
            // completions that carry a programId (series scope token).
            const byScope = {};
            (completions || []).forEach((c) => {
                if (!c.programId || !c.videoId) return;
                const scope = byScope[c.programId] || (byScope[c.programId] = {});
                const cur = scope[c.videoId];
                const at = c.completedAt || null;
                scope[c.videoId] = {
                    count: (cur?.count || 0) + 1,
                    lastCompletedAt: (cur && cur.lastCompletedAt && cur.lastCompletedAt > (at || '')) ? cur.lastCompletedAt : at,
                };
            });
            setSeriesCompletion(byScope);
            setSeriesLoaded(true);
            setSeriesLoading(false);
        })();
        return () => { cancelled = true; };
    }, [activeTab, seriesLoaded, userId]);

    // ── Series: load the open series' day videos ──
    // TWO sources, mixed: the Bunny collection (if set) and videos recorded
    // in-app (Mux), which live in `programVideos` scoped by seriesId. Either
    // source may be empty, so a challenge can be all-Bunny, all-recorded, or a
    // mix. `videoOrder[]` interleaves both id namespaces.
    // See docs/decisions/adr-series-recorded-videos.md.
    useEffect(() => {
        if (!selectedSeriesId) return;
        const s = seriesList.find((x) => x.id === selectedSeriesId);
        if (!s) { setSeriesVideos([]); return; }
        if (seriesVideosLoadedFor === s.id) return;   // already loaded for THIS series
        let cancelled = false;
        setSeriesVideosLoading(true);
        (async () => {
            const [vids, recorded] = await Promise.all([
                s.bunnyCollectionId ? fetchVideosFromCollection(s.bunnyCollectionId) : Promise.resolve([]),
                fetchProgramVideos(s.id),
            ]);
            if (cancelled) return;
            // READY recordings only — one still processing simply isn't a day yet
            // (it appears on its own once Mux finishes). Shaped like a class
            // recording so VideoPlayerModal plays it via mux-player unchanged.
            const recordedVideos = (recorded || [])
                .filter((pv) => pv.status === 'ready' && pv.muxPlaybackId)
                .map(shapeProgramVideo);
            setSeriesVideos(orderSeriesVideos([...(vids || []), ...recordedVideos], s.videoOrder));
            setSeriesVideosLoadedFor(s.id);
            setSeriesVideosLoading(false);
        })();
        return () => { cancelled = true; };
    }, [selectedSeriesId, seriesList, seriesVideosLoadedFor]);

    // ── Deep link: open ONE challenge from ?series=<slug-or-id> ──
    // The shareable address for a challenge detail page. Because
    // resolveTabFromUrl reports 'series' for this param, the existing tab effect
    // opens the Series tab, which triggers the existing lazy load — so this
    // effect only has to wait for the list, match, and select.
    //
    // Receiver pattern is the FIXED one (see adr-series-deep-link-and-sections.md
    // Decision 2): it subscribes to `location`, and its one-shot guard is keyed on
    // a navigation signature rather than a blanket boolean — so it can't disable
    // itself on a param-less load, and a repeat in-app navigation to the same
    // link re-opens the challenge. The param strip uses raw replaceState, which
    // bypasses the router and therefore can't re-trigger this effect.
    const seriesDeepLinkRef = useRef('');
    useEffect(() => {
        let token = '';
        try {
            const search = (location && location.search) || window.location.search;
            token = (new URLSearchParams(search).get('series') || '').trim();
        } catch (_) { token = ''; }
        if (!token) return;                       // no param ⇒ complete no-op
        const signature = `${(location && location.key) || ''}|${token}`;
        if (seriesDeepLinkRef.current === signature) return;
        if (!seriesLoaded) return;                // wait for the lazy-loaded list
        seriesDeepLinkRef.current = signature;

        const match = findSeriesByToken(seriesList, token);
        // Reaching here means the list loaded, which only happens once the Series
        // tab has been opened — i.e. it IS available to this viewer. Re-assert the
        // tab so a link followed from another tab (in-app navigation, after the
        // one-shot ?tab= effect has already latched) still lands on the challenge.
        setActiveTab('series');
        // Drafts stay admin-only, exactly as the list view gates them.
        if (match && (isAdmin || match.active)) {
            setSelectedSeriesId(match.id);
            setSeriesLinkMissed(false);
        } else {
            setSelectedSeriesId(null);
            setSeriesLinkMissed(true);
        }
        // NOTE: the param is deliberately NOT stripped here. Unlike ?tab= (view
        // state), a challenge link is content — keeping it means a refresh stays
        // on the challenge and the address bar is itself shareable. The sync
        // effect below owns the param from this point on.
    }, [location, seriesLoaded, seriesList, isAdmin]);

    // ── Keep the address bar in step with the open challenge ──
    // So the URL a member sees is always the one that reproduces their view: it
    // appears when a challenge opens (however they got there), and clears when
    // they go back to the list or leave the Series tab — otherwise a later
    // refresh would yank them back into a challenge they'd already left.
    //
    // replaceState ONLY: no history entries, no router involvement, so this
    // cannot disturb the ?recording= / ?tab= receivers or re-trigger the effect
    // above (whose one-shot guard is keyed on the router's location, which raw
    // replaceState does not touch).
    useEffect(() => {
        try {
            const url = new URL(window.location.href);
            const current = url.searchParams.get('series') || '';
            const open = (activeTab === 'series' && selectedSeriesId)
                ? (seriesList || []).find((x) => x.id === selectedSeriesId)
                : null;
            const want = open ? ((open.slug && String(open.slug).trim()) || open.id) : '';
            // Never clear a ?series= link the receiver above hasn't opened yet.
            // On mount activeTab is still the safe default, so without this the
            // effect erases the very link this load is trying to honour. Scoped
            // tightly: it only withholds the CLEAR, only on a load that actually
            // arrived with a ?series= link, and only until the receiver has run.
            // Browsing to a challenge from the Series list (no initial token) is
            // unaffected, so back-to-list still clears the param as before.
            if (!want && initialSeriesTokenRef.current && !seriesDeepLinkRef.current) return;
            if (current === want) return;   // already correct — never write needlessly
            if (want) url.searchParams.set('series', want);
            else url.searchParams.delete('series');
            window.history.replaceState({}, '', url.pathname + url.search + url.hash);
        } catch (_) { /* non-critical — the view is already correct either way */ }
    }, [activeTab, selectedSeriesId, seriesList]);

    // ── Track mobile breakpoint for the segmented control ──
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return undefined;
        const mq = window.matchMedia('(max-width: 600px)');
        const onChange = (e) => setIsMobile(e.matches);
        setIsMobile(mq.matches);
        // addEventListener('change') is the modern API; older Safari needs addListener.
        if (mq.addEventListener) mq.addEventListener('change', onChange);
        else mq.addListener(onChange);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', onChange);
            else mq.removeListener(onChange);
        };
    }, []);

    // ── Track AppNav's top-bar breakpoint (sticky `top` for the pinned tab bar) ──
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return undefined;
        const mq = window.matchMedia('(max-width: 768px)');
        const onChange = (e) => setIsNarrowNav(e.matches);
        setIsNarrowNav(mq.matches);
        if (mq.addEventListener) mq.addEventListener('change', onChange);
        else mq.addListener(onChange);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', onChange);
            else mq.removeListener(onChange);
        };
    }, []);

    // ── Detect when the tab bar is pinned ──
    // A 1px sentinel sits just ABOVE the sticky bar; once it scrolls under the app
    // top bar, the tab bar is pinned. The sentinel is outside the bar, so the bar
    // condensing can never move it — no stick/unstick flicker loop.
    useEffect(() => {
        const el = tabsSentinelRef.current;
        if (!el || typeof IntersectionObserver === 'undefined') return undefined;
        const navH = isNarrowNav ? 52 : 70;
        const io = new IntersectionObserver(([entry]) => {
            setTabsPinned(!entry.isIntersecting && entry.boundingClientRect.top < navH);
        }, { rootMargin: `-${navH}px 0px 0px 0px`, threshold: 0 });
        io.observe(el);
        return () => io.disconnect();
    }, [isNarrowNav]);

    // ══════════════════════════════════════════════
    //  LIVE CLASSES TAB — ACTIONS
    // ══════════════════════════════════════════════

    const loadDriveImages = async () => {
        if (driveImages.length > 0) { setShowImagePicker(true); return; }
        setImagesLoading(true);
        const imgs = await fetchProgramImages();
        setDriveImages(imgs || []);
        setImagesLoading(false);
        setShowImagePicker(true);
    };
    const selectImage = (img) => { setForm({ ...form, imageUrl: img.imageUrl }); setShowImagePicker(false); };

    const handleGoLive = async (classItem) => {
        await markClassLive(classItem.id);
        // Dual-write state + status on local state (same pattern Firestore uses
        // during migration window — classState.js reads tolerate either).
        setClasses((prev) => prev.map((c) => c.id === classItem.id ? { ...c, state: 'live', status: 'live', actualStartTime: Date.now() } : c));
        setActiveClass({ ...classItem, state: 'live', status: 'live', actualStartTime: Date.now() });
        // Log the go-live moment for post-incident forensics (D-057).
        // muxStreamId omitted from metadata since pre-recorded classes no longer
        // create a Mux Live Stream (2026-04-21 refactor); live classes still
        // have it available via the class doc if a forensic investigation needs it.
        logHealthEvent(classItem.id, 'stream_started', {
            classType: classItem.classType || 'live',
        }, 'info');
        // D-053 supersedes: go-live feedback is now driven by BroadcasterCueSequence
        // (gated on real pipeline signals, not a blind 10s countdown). Legacy setter
        // retained for any historical reader, but no longer drives UI.
        if ((classItem.classType || 'live') === 'live') setGoLiveAt(Date.now());
        // LL-live propagation is typically 2-5s instead of 5-15s on standard HLS,
        // so the admin's Viewer Preview can appear much sooner. "Starting class…"
        // overlay in LivePlayer covers the remaining cold-start window.
        setShowAdminPreview(false);
        setTimeout(() => setShowAdminPreview(true), 5000);
    };

    const handleEndClass = async (classItem) => {
        // ── Option A unification: pre-recorded RECORDING-phase route ──
        // If this fires during the pre-recorded recording window (admin is
        // broadcasting to Mux to create the VOD, but scheduledAt hasn't arrived
        // yet so activeClass is null), delegate to handleEndRecording — it runs
        // the same seal-before-stop ordering but DOESN'T flip the class state
        // to 'ending'/'ended' (the class is still scheduled for the future —
        // the auto-flip at scheduledAt will handle 'live' later). This is what
        // eliminates the Mux slate the pre-recorded path used to hit: /complete
        // fires BEFORE LiveBroadcaster's endingAt effect runs stopBroadcast, so
        // the manifest is sealed before any silent-source window opens.
        const isPrerecordedRecording =
            (classItem.classType === 'prerecorded')
            && (broadcastingClassId === classItem.id)
            && (!activeClass || activeClass.status !== 'live');
        if (isPrerecordedRecording) {
            return handleEndRecording(classItem);
        }

        // Graceful end — "seal-before-stop" architecture (refines the seal-first
        // flow from D-050 supersedure):
        //
        //   T+0s:
        //     a) Fire PUT ?action=complete and AWAIT it — seals the Mux HLS
        //        manifest with #EXT-X-ENDLIST BEFORE the broadcaster stops.
        //        Per Mux's API, /complete is a superset of /disable: both
        //        prevent encoder reconnection, and /complete additionally
        //        finalizes the asset. Wrapped in Promise.race with a 2000ms
        //        timeout so a slow/hung Mux call cannot freeze the admin UI —
        //        on timeout we fall through to the rest of the flow (viewer
        //        state still updates; the stranded-ending cron safety net
        //        will complete the seal within 5 minutes).
        //
        //   T+~400ms (after /complete resolves):
        //     b) Write state:'ending' + endingAt to Firestore → viewer listener
        //        propagates it → viewers' LivePlayer enters graceful-ending mode
        //        (gracefullyEndingRef=true, promotes the next Mux `ended` event
        //        to authoritative so the thank-you overlay appears cleanly).
        //     c) Set endingAt on local activeClass so LiveBroadcaster's endingAt
        //        effect fires stopBroadcast (5s WS flush + clean close — admin
        //        never sees "Connection interrupted"). By this point the Mux
        //        manifest is already sealed, so stopBroadcast runs against a
        //        closed manifest — no silent-source window for Mux to fill.
        //     d) Show the non-blocking "Wrapping up" banner so the admin sees
        //        confirmation and knows they can navigate freely.
        //
        //   Why seal BEFORE broadcaster stop (and not concurrent / after):
        //     MediaRecorder.stop() fires synchronously the moment endingAt
        //     flips in activeClass (via LiveBroadcaster's endingAt effect,
        //     ~10ms after the state update). If /complete fires after that,
        //     there's a ~400-800ms window where the source is silent
        //     (MediaRecorder stopped, flush buffer draining) but the Mux
        //     manifest is still open. Mux fills that gap in its HLS assembly
        //     buffer with its default "slate" placeholder image, which then
        //     gets baked into the VOD asset. Sealing BEFORE stopping closes
        //     that window entirely — by the time MediaRecorder.stop runs,
        //     Mux has already written #EXT-X-ENDLIST and will not add any
        //     more segments.
        //
        //     Any in-flight RTMP packets still en route to Mux when the seal
        //     lands are dropped (Mux closes the RTMP socket on /complete),
        //     but those packets carry content captured during the ~400ms
        //     between click and seal — typically silence, since the admin
        //     clicks End AFTER finishing their closing words. The full
        //     "thank you for joining" phrase was captured and delivered to
        //     Mux well before the click (MediaRecorder 250ms timeslice +
        //     ffmpeg pipeline at speed≈0.98x).
        //
        //   History: the prior flow did steps b-c FIRST and /complete last.
        //   That reduced slate from ~7s (split-phase D-050) to ~1s but left
        //   a residual gap. Moving /complete to the front closes the final
        //   window. If this change ever needs to be reverted, put /complete
        //   back below the setActiveClass block and accept the ~1s slate
        //   flash — do NOT reintroduce the T+12s split-phase flow.
        //
        //   Admin UI closes at T+~6.4s:
        //     - Gives LiveBroadcaster's stopBroadcast (5s WS flush) time to
        //       complete cleanly before unmounting.
        //     - Admin becomes free to navigate anywhere immediately after. The
        //       banner continues to communicate that wrap-up is still happening
        //       in the background.
        //
        //   Banner auto-dismiss (T+~15s): generous margin past admin UI close.
        //
        //   T+~120s safety escape:
        //     e) markClassEnded flips state to 'ended'. Any viewer whose Mux
        //        Player never fired `ended` (rare — stale cached segments) gets
        //        the thank-you overlay via the Firestore state flip instead.
        //     f) Refresh admin class list so if the admin is still on the page
        //        they see the class transition to 'ended'.
        //
        //   Tab-close / timeout safety (server-side):
        //     send-class-reminder cron sweeps for stranded state === 'ending'
        //     classes (endingAt > 2 min ago) and flips them to 'ended' plus
        //     fires the complete action. This covers both admin tab-close
        //     mid-flight AND the /complete timeout fallback above.
        //
        // Pattern #41 preserved: Mux `ended` alone is still never authoritative.
        // It becomes authoritative only when paired with `endingAt` + graceful
        // mode. A mid-broadcast transient `ended` (no endingAt) still silent-
        // reloads. See LivePlayer.js ended handler.
        const endingAt = Date.now();

        // Step a: Seal the Mux manifest FIRST — before MediaRecorder stops and
        // any silent-source gap can open. Wrapped in Promise.race with a 2s
        // timeout so the admin UI never freezes on a slow/hung Mux response.
        if (classItem.muxStreamId) {
            try {
                const token = await getToken();
                await Promise.race([
                    fetch(`/api/live-class?streamId=${classItem.muxStreamId}&action=complete`, {
                        method: 'PUT',
                        headers: { 'Authorization': `Bearer ${token}` },
                    }),
                    new Promise((_, reject) => setTimeout(() => reject(new Error('complete-timeout')), 2000)),
                ]);
            } catch (e) { console.warn('Non-critical: Mux complete signal failed or timed out:', e); }
        }

        // Steps b-c: Local UI + Firestore ending state (viewers enter graceful-end,
        // broadcaster's endingAt effect fires stopBroadcast against an already-
        // sealed manifest — no slate window).
        setActiveClass((prev) => prev ? { ...prev, state: 'ending', status: 'ending', endingAt } : null);
        setClasses((prev) => prev.map((c) => c.id === classItem.id ? { ...c, state: 'ending', status: 'ending', endingAt } : c));
        try {
            await markClassEnding(classItem.id, endingAt);
            await updateLiveClass(classItem.id, { viewerCount: 0 });
        } catch (e) { console.warn('markClassEnding failed:', e); }
        setViewerCount(0);
        logHealthEvent(classItem.id, 'stream_ended', { reason: 'admin', endingAt }, 'info');

        // Step d: Non-blocking banner — reassures admin that wrap-up is happening
        // in the background and they can navigate freely.
        setEndingBanner({
            title: 'Wrapping up',
            subtext: 'Finishing the stream for viewers. You can navigate away — we\'ll handle the rest.',
        });

        // T+6s: Close admin UI. LiveBroadcaster had 6s to flush its WS (5s flush
        // window + 1s margin) before unmounting. Admin is now free to navigate.
        setTimeout(() => {
            setActiveClass(null);
            setBroadcastingClassId(null);
        }, 6000);

        // T+15s: dismiss banner.
        setTimeout(() => setEndingBanner(null), 15000);

        // T+120s safety escape — flips state:'ended' for any stragglers whose
        // player never fired `ended` on its own. Admin UI already closed at T+6s;
        // just refresh class list in case admin is still on the page.
        setTimeout(async () => {
            try { await markClassEnded(classItem.id); } catch (e) { console.warn('markClassEnded failed (non-critical):', e); }
            setClasses((prev) => prev.map((c) => c.id === classItem.id ? { ...c, state: 'ended', status: 'ended' } : c));
            loadClasses();
        }, 120000);
    };

    // ── Pre-recorded RECORDING-phase end (Mux Direct Upload, 2026-04-21) ──
    // Architecture change: pre-recorded recordings no longer stream through the
    // Cloud Run RTMP relay. Instead, LiveBroadcaster collects MediaRecorder
    // chunks locally; on End Class, we trigger the stop which hands the Blob to
    // handleRecordingComplete (below). That handler POSTs to /api/mux-direct-
    // upload, PUTs the Blob to the signed URL, and the mux-webhook finalizes
    // the asset — identical downstream path to before but with a pristine,
    // artifact-free source.
    //
    // What changed from the prior seal-before-stop flow:
    //   • No /api/live-class?action=complete call — there's no live stream in
    //     flight to seal. The Mux live stream created at class scheduling time
    //     is now effectively unused for pre-recorded classes; handleStopRecording
    //     still /completes it as a legacy safety net (harmless no-op).
    //   • No 5s WS flush window — MediaRecorder stops, assembles Blob
    //     synchronously, hands it to handleRecordingComplete, upload begins.
    //   • No race between MediaRecorder.stop() and the Mux manifest seal —
    //     the Mux asset doesn't exist until the Direct Upload transcode, so
    //     there's no slate possibility at the tail.
    const handleEndRecording = async (classItem) => {
        // Guard mirrors the prior flow — LiveBroadcaster's onBroadcastEnd fires
        // after MediaRecorder.stop() completes. With Direct Upload the onBroadcastEnd
        // path is a no-op for pre-recorded (the upload flow handles everything),
        // but we still latch this flag so the legacy handleStopRecording fallback
        // can't double-fire if it's ever reached.
        recordingEndingRef.current = true;

        // Signal LiveBroadcaster to stop the recorder. The endingAt effect inside
        // LiveBroadcaster observes null → timestamp and fires stopBroadcast. For
        // pre-recorded, stopBroadcast assembles the in-memory chunks into a Blob
        // and calls onRecordingComplete (wired to handleRecordingComplete below).
        setRecordingEndingAt(Date.now());

        // Non-blocking banner — first-phase wording ("Finalizing recording") is
        // replaced by the upload banner as soon as handleRecordingComplete fires
        // (within ~100ms). Shown in case the recorder-stop → blob-assembly → upload-
        // handler chain takes longer than expected (shouldn't in practice).
        setEndingBanner({
            title: 'Finalizing recording',
            subtext: "Preparing upload. Keep this tab open while we finish.",
        });
        // Upload banner replaces the endingBanner on first progress tick. If the
        // upload never starts (edge case — no chunks captured), auto-dismiss after
        // 15s to avoid a stuck banner.
        setTimeout(() => setEndingBanner(null), 15000);
    };

    // ── Direct Upload handler: receives the recorded Blob from LiveBroadcaster,
    // uploads it to Mux, updates the class doc, and waits for the webhook to
    // finalize the asset. All failure modes surface in the banner with a Retry
    // button — the Blob is kept in state so retry doesn't require re-recording.
    //
    // Flow:
    //   1. POST /api/mux-direct-upload → { uploadId, uploadUrl }
    //   2. Update class doc: uploadStatus: 'uploading', muxUploadId
    //   3. XHR PUT blob to uploadUrl with progress tracking
    //   4. On success: update class doc uploadStatus: 'processing'
    //   5. onSnapshot listener for muxAssetId — webhook promotes to 'ready'
    //   6. When muxAssetId lands: refresh classes, clear upload state, close banner
    //
    // Why XHR (not fetch):
    //   - fetch() has no upload progress API in browsers (as of 2026). The stream
    //     response body covers download progress only. XHR's upload.onprogress
    //     is the standard way to report MB-by-MB upload progress to the admin.
    //   - We need to abort the in-flight upload if the admin closes the tab
    //     or navigates away (prevent orphaned Mux uploads sitting at 42%).
    //     XHR.abort() is straightforward; AbortController+fetch would also work
    //     but offers no progress API.
    const handleRecordingComplete = useCallback(async (classItem, blob, mimeType) => {
        if (!blob || blob.size === 0) {
            console.error('handleRecordingComplete: empty blob, nothing to upload');
            setEndingBanner({
                title: 'Recording failed',
                subtext: 'No video data was captured. Please try recording again.',
            });
            setTimeout(() => setEndingBanner(null), 8000);
            return;
        }

        const totalBytes = blob.size;
        setUploadingClass({
            classId: classItem.id,
            classTitle: classItem.title || '',
            progress: 0,
            loadedBytes: 0,
            totalBytes,
            phase: 'requesting',
            error: null,
            blob,
            mimeType,
        });
        // Endingbanner is superseded by the upload banner — clear it.
        setEndingBanner(null);
        // Close the broadcaster panel — LiveBroadcaster has already torn down its
        // recorder, camera, and audio graph inside finalizeAndTeardown. Keeping the
        // component mounted would render a stale "Start Broadcasting" control below
        // the upload banner. The upload progress is entirely driven by uploadingClass
        // state now; no broadcaster UI is needed while the PUT is in flight.
        setBroadcastingClassId(null);

        // Inner function so the Retry button can re-invoke this without
        // re-running the blob assembly (which it can't anyway — the recorder
        // is already destroyed).
        const runUpload = async (existingUploadUrl, existingUploadId) => {
            let uploadUrl = existingUploadUrl;
            let uploadId = existingUploadId;

            // Step 1: request a fresh signed upload URL from our backend (unless
            // we're retrying with an unexpired existing URL).
            if (!uploadUrl) {
                try {
                    const token = await getToken();
                    const res = await fetch('/api/mux-direct-upload', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                        body: JSON.stringify({ classId: classItem.id, classTitle: classItem.title || '' }),
                    });
                    if (!res.ok) {
                        const body = await res.text();
                        throw new Error(`Upload creation failed (${res.status}): ${body}`);
                    }
                    const data = await res.json();
                    uploadUrl = data.uploadUrl;
                    uploadId = data.uploadId;
                } catch (err) {
                    console.error('Failed to create Mux Direct Upload:', err);
                    setUploadingClass((prev) => prev ? { ...prev, phase: 'error', error: err.message || 'Could not start upload.' } : null);
                    return;
                }
            }

            // Step 2: mark class doc so the cron + admin UI know an upload is in flight.
            // Non-critical — if this fails, the upload can still succeed (webhook is
            // the authoritative signal), we just lose a bit of UI observability.
            try {
                await updateLiveClass(classItem.id, { uploadStatus: 'uploading', muxUploadId: uploadId });
            } catch (e) { console.warn('updateLiveClass (uploading) failed:', e); }

            // Step 3: PUT the Blob to Mux's signed URL with progress tracking.
            setUploadingClass((prev) => prev ? { ...prev, phase: 'uploading', error: null } : null);
            await new Promise((resolve) => {
                const xhr = new XMLHttpRequest();
                uploadXhrRef.current = xhr;
                xhr.open('PUT', uploadUrl, true);
                // Content-Type must match what Mux expects — set to the recorder's
                // mimeType. Mux is tolerant of video/webm and video/mp4; it
                // transcodes to the delivery formats regardless.
                xhr.setRequestHeader('Content-Type', mimeType || 'video/webm');
                xhr.upload.onprogress = (ev) => {
                    if (!ev.lengthComputable) return;
                    setUploadingClass((prev) => prev ? {
                        ...prev,
                        progress: ev.loaded / ev.total,
                        loadedBytes: ev.loaded,
                        totalBytes: ev.total,
                    } : null);
                };
                xhr.onload = () => {
                    uploadXhrRef.current = null;
                    if (xhr.status >= 200 && xhr.status < 300) {
                        resolve({ ok: true });
                    } else {
                        setUploadingClass((prev) => prev ? {
                            ...prev,
                            phase: 'error',
                            error: `Upload rejected (HTTP ${xhr.status}). Tap Retry to try again.`,
                        } : null);
                        resolve({ ok: false });
                    }
                };
                xhr.onerror = () => {
                    uploadXhrRef.current = null;
                    setUploadingClass((prev) => prev ? {
                        ...prev,
                        phase: 'error',
                        error: 'Network error during upload. Tap Retry to try again — your recording is saved locally until this page is closed.',
                    } : null);
                    resolve({ ok: false });
                };
                xhr.onabort = () => {
                    uploadXhrRef.current = null;
                    // Admin or cleanup triggered abort — don't surface as an error
                    // (the caller is responsible for UI after abort).
                    resolve({ ok: false, aborted: true });
                };
                xhr.send(blob);
            });

            // Step 4: if upload failed (phase === 'error' from xhr.onerror/onload), the
            // Retry button re-invokes runUpload. Only advance to 'processing' if the PUT
            // actually succeeded — read state via functional update so we see the freshest
            // phase (xhr.onerror may have flipped it between PUT completion and here).
            let didSucceed = false;
            setUploadingClass((prev) => {
                if (prev && prev.phase === 'uploading') {
                    didSucceed = true;
                    return { ...prev, phase: 'processing', progress: 1 };
                }
                return prev;
            });
            if (!didSucceed) return;

            // Step 5: mark class doc as 'processing' — webhook will promote to 'ready'.
            try {
                await updateLiveClass(classItem.id, { uploadStatus: 'processing' });
            } catch (e) { console.warn('updateLiveClass (processing) failed:', e); }

            // Step 6: wait for muxAssetId via onSnapshot. The webhook writes muxAssetId,
            // muxPlaybackId, videoDurationSeconds, and flips uploadStatus to 'ready'.
            // 5-min safety timeout so we don't leak the listener if the webhook never
            // fires (e.g. Mux transcode failure).
            const unsubscribeAssetWatch = onSnapshot(firestoreDoc(db, 'liveClasses', classItem.id), async (snap) => {
                if (!snap.exists()) return;
                const data = snap.data();
                if (data.muxAssetId) {
                    unsubscribeAssetWatch();
                    try {
                        const refreshed = await fetchLiveClasses();
                        setClasses(refreshed);
                    } catch (e) { /* non-critical */ }
                    // ── IDB cleanup (2026-04-23) ──
                    // Mux has the authoritative recording now (muxAssetId set by
                    // the webhook). The IDB chunks are no longer the source of
                    // truth — free the disk space. Safe to call even if IDB never
                    // held chunks for this classId (no-op). Error is swallowed
                    // because it's non-critical: the IDB entry will be offered
                    // for upload on next mount via the recovery prompt, where
                    // admin can discard it explicitly.
                    try { await bufferClearRecording(classItem.id); }
                    catch (e) { console.warn('bufferClearRecording failed (non-critical):', e); }
                    setUploadingClass(null);
                    setBroadcastingClassId(null);
                    setRecordingEndingAt(null);
                    recordingEndingRef.current = false;
                }
            }, () => { /* error handler — safety timeout below cleans up */ });
            setTimeout(() => {
                try { unsubscribeAssetWatch(); } catch (e) { /* already unsubscribed */ }
                // If still waiting after 5 min, close the banner anyway. The admin
                // can refresh to pick up the recording once the webhook eventually fires.
                setUploadingClass((prev) => (prev && prev.classId === classItem.id && prev.phase === 'processing') ? null : prev);
            }, 5 * 60 * 1000);
        };

        await runUpload();
    }, [getToken]);

    // ── Retry handler for failed uploads ──
    // Called from the upload banner's Retry button. Re-requests a fresh Mux upload
    // URL (the prior one may be in a half-consumed state) and re-runs the PUT with
    // the cached Blob. Keeps the admin from having to re-record on a transient failure.
    const retryUpload = useCallback(async () => {
        const state = uploadingClass;
        if (!state || !state.blob) return;
        const classItem = classes.find((c) => c.id === state.classId);
        if (!classItem) return;
        handleRecordingComplete(classItem, state.blob, state.mimeType);
    }, [uploadingClass, classes, handleRecordingComplete]);

    // ── Recovery-on-mount scan (2026-04-23) ──
    // IDB may hold chunks from a previous recording session that was interrupted
    // (tab closed before upload, component unmounted mid-record, upload failed
    // and admin refreshed). When admin mounts Workouts:
    //   1. Wait for `classes` to load so we can pair metadata with a class title
    //   2. Call bufferListRecordings() once per session
    //   3. If any records present, surface the first via pendingRecoveryRecording
    //   4. After admin acts (upload or discard), re-scan for the next one
    // recoveryScannedRef guards against re-scans on every classes-array refresh
    // (which happens on every tab focus). First-scan-only gives us the modal
    // the instant admin lands on the page; post-modal rescan covers the rare
    // case of multiple pending recoveries without showing a jarring modal stack.
    useEffect(() => {
        if (!isAdmin) return;
        if (!classes || classes.length === 0) return;
        if (pendingRecoveryRecording) return;
        if (uploadingClass) return; // don't fight an in-flight upload
        if (broadcastingClassId) return; // admin is actively recording right now
        if (recoveryScannedRef.current) return;
        recoveryScannedRef.current = true;

        (async () => {
            try {
                const records = await bufferListRecordings();
                if (!records || records.length === 0) return;
                // Pick the oldest first — if admin recorded two classes back-to-
                // back and both were interrupted, resolving them in capture order
                // avoids a later class silently overwriting an earlier one.
                records.sort((a, b) => (a.startedAt || 0) - (b.startedAt || 0));
                for (const rec of records) {
                    // Skip in-app PROGRAM recordings — they belong to ProgramRecorder's
                    // own flow (it tags them in localStorage), not the class recovery
                    // here (they'd otherwise surface as an "Unknown class"). Default-safe:
                    // class recordings have no marker. See adr-program-recorded-videos.md.
                    try { if (typeof localStorage !== 'undefined' && localStorage.getItem('mom-program-recording-' + rec.classId)) continue; } catch (_) { /* ignore */ }
                    const cls = classes.find((c) => c.id === rec.classId);
                    // Estimated captured duration: chunkCount × 250 ms timeslice.
                    // This is a close-enough lower bound (dropped chunks are rare
                    // now that writes are persistent; the true figure is close to
                    // wall clock from startedAt → lastChunkAt).
                    const estimatedSeconds = Math.max(
                        (rec.chunkCount || 0) * 0.25,
                        rec.lastChunkAt && rec.startedAt ? Math.round((rec.lastChunkAt - rec.startedAt) / 1000) : 0
                    );
                    setPendingRecoveryRecording({
                        classId: rec.classId,
                        classTitle: cls?.title || rec.classTitle || 'Unknown class',
                        chunkCount: rec.chunkCount || 0,
                        startedAt: rec.startedAt || 0,
                        lastChunkAt: rec.lastChunkAt || 0,
                        estimatedSeconds,
                        mimeType: rec.mimeType || 'video/webm',
                        classExists: !!cls,
                    });
                    return; // show one at a time
                }
            } catch (e) {
                console.warn('bufferListRecordings on mount failed (non-critical):', e);
            }
        })();
    }, [isAdmin, classes, pendingRecoveryRecording, uploadingClass, broadcastingClassId]);

    // ── Recovery: upload handler ──
    // Reads the composite Blob from IDB and hands it to handleRecordingComplete,
    // same path a freshly-finished recording would take. On failure we leave the
    // IDB entry intact — admin can retry. Busy flag gates double-clicks and
    // shows a spinner on the modal button.
    const handleRecoveryUpload = useCallback(async () => {
        if (!pendingRecoveryRecording || recoveryBusy) return;
        setRecoveryBusy(true);
        try {
            const classId = pendingRecoveryRecording.classId;
            const result = await bufferGetRecordingBlob(classId);
            if (!result.blob || result.blob.size === 0) {
                // Empty — just discard and move on. IDB cleanup below.
                try { await bufferClearRecording(classId); } catch (_) { /* ignore */ }
                setPendingRecoveryRecording(null);
                recoveryScannedRef.current = false; // rescan for any others
                return;
            }
            const cls = classes.find((c) => c.id === classId);
            if (!cls) {
                // Class was deleted upstream — discard is the only viable option.
                setRecoveryBusy(false);
                return;
            }
            // Clear the modal and hand off to the standard upload path. The
            // onSnapshot listener inside handleRecordingComplete will
            // bufferClearRecording once Mux confirms muxAssetId, so we do NOT
            // clear IDB here — failure mid-upload should leave the IDB intact
            // so the admin can retry from the upload-banner's Retry button.
            setPendingRecoveryRecording(null);
            recoveryScannedRef.current = false; // rescan after this completes
            handleRecordingComplete(cls, result.blob, result.mimeType || pendingRecoveryRecording.mimeType);
        } catch (e) {
            console.error('Recovery upload failed:', e);
            // Leave the modal up with an error we could surface; for now just
            // release busy and let admin try Discard.
        } finally {
            setRecoveryBusy(false);
        }
    }, [pendingRecoveryRecording, recoveryBusy, classes, handleRecordingComplete]);

    // ── Recovery: discard handler ──
    // Deletes the IDB chunks + metadata for this recording. Admin-initiated,
    // with explicit confirmation in the modal copy — no accidental data loss.
    const handleRecoveryDiscard = useCallback(async () => {
        if (!pendingRecoveryRecording || recoveryBusy) return;
        setRecoveryBusy(true);
        try {
            await bufferClearRecording(pendingRecoveryRecording.classId);
        } catch (e) {
            console.warn('bufferClearRecording on discard failed:', e);
        }
        setPendingRecoveryRecording(null);
        recoveryScannedRef.current = false; // rescan for any others
        setRecoveryBusy(false);
    }, [pendingRecoveryRecording, recoveryBusy]);

    // ── beforeunload guard during upload ──
    // The Blob lives in memory; closing the tab mid-upload discards it with no
    // recovery. Browser shows a generic "Changes you made may not be saved" dialog;
    // the custom message is ignored by modern browsers (spec compliance), but the
    // presence of a returnValue triggers the native confirm.
    useEffect(() => {
        if (!uploadingClass) return;
        if (uploadingClass.phase === 'processing' || uploadingClass.phase === 'done') return;
        const handler = (e) => {
            e.preventDefault();
            e.returnValue = '';
            return '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [uploadingClass]);

    // ── Upload banner auto-clear (backup to onSnapshot in handleRecordingComplete) ──
    // First real-test finding (2026-04-21): the onSnapshot listener attached inside
    // handleRecordingComplete occasionally misses the muxAssetId update — banner
    // stays stuck at "Processing" even though Mux finalized the asset and the
    // class played fine at scheduled time. Root cause unclear (Firestore listener
    // race? Clerk auth refresh? closure issue?) but rather than spelunking, we
    // take the belt-and-suspenders approach: whenever the `classes` array refreshes
    // and shows the target class now has muxAssetId (or uploadStatus === 'ready'),
    // clear the banner. Pairs with the periodic loadClasses refresh below which
    // ensures `classes` DOES refresh while we're in the processing phase.
    useEffect(() => {
        if (!uploadingClass) return;
        if (uploadingClass.phase !== 'processing') return;
        const target = classes.find((c) => c.id === uploadingClass.classId);
        if (!target) return;
        if (target.muxAssetId || target.uploadStatus === 'ready') {
            // ── IDB cleanup (2026-04-23) ──
            // Same reasoning as the onSnapshot listener path above — Mux has
            // the authoritative recording now, free the local disk space.
            // Fire-and-forget because this backup effect is best-effort.
            bufferClearRecording(uploadingClass.classId).catch((e) => {
                console.warn('bufferClearRecording failed in backup clear (non-critical):', e);
            });
            setUploadingClass(null);
            setRecordingEndingAt(null);
            recordingEndingRef.current = false;
        }
    }, [classes, uploadingClass]);

    // ── Periodic loadClasses refresh during upload processing ──
    // Drives the backup clear-effect above. 15s cadence means the banner clears
    // within 15s of the webhook writing muxAssetId — good enough given admin is
    // typically doing something else while waiting anyway. Only runs during
    // processing phase (not uploading — that has onprogress already, and not
    // requesting — trivially fast). Cleared when uploadingClass flips to null
    // or phase changes.
    useEffect(() => {
        if (!uploadingClass || uploadingClass.phase !== 'processing') return;
        const t = setInterval(() => { loadClasses(); }, 15000);
        return () => clearInterval(t);
    }, [uploadingClass, loadClasses]);

    // ── Per-viewer local comment unlock (Bug-2 fix 2026-04-21, revised) ──
    // First revision tried: one-shot timer at (scheduledAt + videoDurationSeconds + 2s)
    // that flipped Firestore state:'ended', letting the activeClass onSnapshot listener
    // propagate to ClassComments. That caused tail-truncation (~20-30s cut off) because
    // the viewer's actual playback lags the scheduled timeline by 5-30s due to HLS
    // cold-start, muted-autoplay → tap-for-sound pause, buffering, and late joiners.
    // When state flipped to 'ended' at wall-clock-end, the LivePlayer overlay gate
    // `status === 'ended' && !gracefullyEndingRef.current` triggered the thank-you
    // overlay BEFORE the viewer's Mux Player had reached the real end of the video —
    // cutting off the closing words.
    //
    // Root cause: server-scheduled end time ≠ per-viewer playback end time for a VOD.
    // Different viewers finish at different wall-clock moments.
    //
    // Fix: unlock comments LOCALLY for each viewer when THEIR Mux Player fires `ended`
    // naturally (LivePlayer already exposes this via onStreamEnded — line 722 of
    // LivePlayer.js). This gives per-viewer accuracy — comments unlock at exactly the
    // moment the thank-you overlay appears, with zero cut-off possible. The Firestore
    // state:'ended' write is handled by the existing 5-min cron + mount/focus-based
    // loadClasses backup — they can lag by up to 5 minutes without user-visible impact
    // because the comment gate is now local.
    //
    // Reset on activeClass change so switching classes (or coming back from ended →
    // scheduled) doesn't carry a stale unlock.
    const [classEndedLocally, setClassEndedLocally] = useState(false);
    useEffect(() => { setClassEndedLocally(false); }, [activeClass?.id]);

    // Latch the per-viewer completion the moment the viewer's own Mux player fires
    // `ended` (classEndedLocally). The latch (sessionStorage) survives an activeClass
    // clear / remount, so the viewer can't be re-pulled onto the live surface during
    // the cron-lag window (the "class started over on re-entry" bug). This is the
    // memory the bare `classEndedLocally` boolean lacked. READ-ONLY — never a
    // Firestore write. See services/classCompletion.js + the ADR.
    useEffect(() => {
        if (classEndedLocally && activeClass?.id) markClassCompleted(activeClass.id);
    }, [classEndedLocally, activeClass?.id]);

    // ── Release day vs set time (docs/plans/release-day-classes-plan.md) ──
    // Scheduled (pre-recorded) classes default to a RELEASE DAY: every member gets
    // the class at 12:00 AM on that date in her own time zone. "Set a time" keeps
    // the original timed airing. Live classes are always timed. A timed save only
    // writes releaseMode/releaseDate when it is turning a release-day class back
    // into a timed one — so a brand-new timed class's payload is exactly as before.
    const formIsReleaseDay = form.classType === 'prerecorded' && form.releaseMode !== 'time';
    const formWhenReady = formIsReleaseDay ? !!form.releaseDate : !!form.scheduledAt;
    const buildFormWhen = () => {
        if (formIsReleaseDay) {
            return { scheduledAt: releaseAnchorIso(form.releaseDate), releaseMode: 'day', releaseDate: form.releaseDate };
        }
        const when = { scheduledAt: new Date(form.scheduledAt).toISOString() };
        if (form.wasReleaseDay) { when.releaseMode = null; when.releaseDate = null; }
        return when;
    };

    const handleSaveClass = async () => {
        if (!form.title.trim() || !formWhenReady) return;
        setSaving(true);
        const classData = {
            title: form.title.trim(),
            description: form.description.trim(),
            ...buildFormWhen(),
            duration: parseInt(form.duration) || 45,
            imageUrl: form.imageUrl || '',
            price: parseInt(form.price) || 0,
            classType: form.classType || 'live',
            muscleGroups: form.muscleGroups || [],
            classic: form.classic || false,
            easeIn: form.easeIn || false,
            strongPlus: form.strongPlus || false,
            visibleToUserIds: form.visibleToUserIds || [],
        };
        if (editingClassId) {
            // Editing an existing class — updateLiveClass merges, so state
            // (draft / scheduled / etc) is preserved exactly as-is.
            await updateLiveClass(editingClassId, classData);
        } else {
            await createLiveClass(classData);
        }
        setForm({ title: '', description: '', scheduledAt: '', duration: 45, imageUrl: '', price: 1, classType: 'prerecorded', muscleGroups: [], classic: false, easeIn: false, strongPlus: false, visibleToUserIds: [], releaseMode: 'day', releaseDate: '', wasReleaseDay: false });
        setCreating(false);
        setEditingClassId(null);
        setSaving(false);
        await loadClasses();
    };

    // ── Save the form as a DRAFT (admin-only) ────────────────────────────
    // Only valid path for new class creation — editing a draft uses
    // handleSaveClass (which preserves the existing state via merge).
    // Drafts are invisible to all users until published.
    const handleSaveAsDraft = async () => {
        if (!form.title.trim() || !formWhenReady) return;
        setSaving(true);
        const classData = {
            title: form.title.trim(),
            description: form.description.trim(),
            ...buildFormWhen(),
            duration: parseInt(form.duration) || 45,
            imageUrl: form.imageUrl || '',
            price: parseInt(form.price) || 0,
            classType: form.classType || 'live',
            muscleGroups: form.muscleGroups || [],
            classic: form.classic || false,
            easeIn: form.easeIn || false,
            strongPlus: form.strongPlus || false,
            visibleToUserIds: form.visibleToUserIds || [],
            state: 'draft',
        };
        await createLiveClass(classData);
        setForm({ title: '', description: '', scheduledAt: '', duration: 45, imageUrl: '', price: 1, classType: 'prerecorded', muscleGroups: [], classic: false, easeIn: false, strongPlus: false, visibleToUserIds: [], releaseMode: 'day', releaseDate: '', wasReleaseDay: false });
        setCreating(false);
        setEditingClassId(null);
        setSaving(false);
        await loadClasses();
    };

    // ── Publish a draft class (state: 'draft' → 'scheduled') ─────────────
    // Optimistic local update mirroring the existing handleEditClass pattern;
    // reverts on failure. Closes the confirm modal regardless of outcome so
    // admin can retry from the tile if it failed.
    const confirmPublish = async () => {
        if (!publishConfirm) return;
        const cls = publishConfirm;
        setPublishing(true);
        // Optimistic flip — Schedule grid re-renders immediately
        setClasses((prev) => prev.map((c) =>
            c.id === cls.id ? { ...c, state: 'scheduled', status: 'scheduled', publishedAt: Date.now() } : c
        ));
        const ok = await publishDraftClass(cls.id);
        if (!ok) {
            // Revert
            setClasses((prev) => prev.map((c) =>
                c.id === cls.id ? { ...c, state: 'draft', status: 'draft', publishedAt: null } : c
            ));
        }
        setPublishConfirm(null);
        setPublishing(false);
        await loadClasses();
    };

    // Pre-fill form with existing class data for editing
    const handleEditClass = (cls) => {
        // Convert ISO date back to datetime-local format for the input.
        // datetime-local inputs interpret their value as VIEWER-LOCAL time, so we
        // must format using local date parts (not toISOString, which formats UTC
        // and would shift the displayed time by the viewer's tz offset — then
        // shift it again on save, producing a round-trip drift).
        let localDate = '';
        const isRelease = isReleaseDayClass(cls);
        if (cls.scheduledAt && !isRelease) {
            const d = new Date(cls.scheduledAt);
            const pad = (n) => String(n).padStart(2, '0');
            localDate = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
        }
        setForm({
            title: cls.title || '',
            description: cls.description || '',
            scheduledAt: localDate,
            duration: cls.duration || 45,
            imageUrl: cls.imageUrl || '',
            price: cls.price || 0,
            classType: cls.classType || 'live',
            muscleGroups: cls.muscleGroups || [],
            classic: cls.classic || false,
            easeIn: cls.easeIn || false,
            strongPlus: cls.strongPlus || false,
            visibleToUserIds: cls.visibleToUserIds || [],
            // An existing timed class stays timed when edited; a release-day class
            // reopens on its date. (The tile hands us the viewer projection, whose
            // scheduledAt is local midnight — releaseDate is the source of truth.)
            releaseMode: isRelease ? 'day' : 'time',
            releaseDate: isRelease ? cls.releaseDate : '',
            wasReleaseDay: isRelease,
        });
        setEditingClassId(cls.id);
        setCreating(true);
    };

    const handleCreateStream = async (classItem) => {
        // ── Pre-recorded fork (2026-04-21 refactor) ──
        // Pre-recorded classes no longer use the live-ingest path. Recording
        // happens locally in the browser (MediaRecorder → Blob) and the finished
        // Blob is shipped directly to Mux via /api/mux-direct-upload. There is
        // no Mux Live Stream object to create — doing so was cosmetic dead weight
        // that cluttered the Mux dashboard with never-used streams and added an
        // API round-trip before the recorder could open.
        //
        // For pre-recorded we simply flip broadcastingClassId — that's the only
        // signal LiveBroadcaster needs to open. The rtmpUrl/streamKey props it
        // receives will be undefined, which is correct: the pre-recorded fork
        // inside LiveBroadcaster skips the WebSocket entirely and never reads
        // those props (see LiveBroadcaster.js startBroadcast — prerecorded branch
        // bypasses the rtmpUrl/streamKey guard at line 648).
        //
        // Legacy pre-recorded classes in Firestore still carry muxStreamId /
        // muxStreamKey / rtmpUrl from before this refactor. Every consumer
        // (handleEndClass, handleDeleteClass, handleStopRecording, the cron
        // stranded-ending sweep) is already guarded by `if (muxStreamId)`, so
        // legacy classes continue to run their cleanup paths while new classes
        // skip them cleanly. No backfill required.
        if ((classItem.classType || 'live') === 'prerecorded') {
            setBroadcastingClassId(classItem.id);
            return;
        }

        // ── Live class path (unchanged) ──
        setStreamLoading(true);
        try {
            const token = await getToken();
            const response = await fetch('/api/live-class', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ classId: classItem.id, title: classItem.title, classType: classItem.classType || 'live' }),
            });
            if (!response.ok) {
                const errText = await response.text();
                console.error(`Stream creation API error ${response.status}:`, errText);
                setStreamLoading(false);
                return;
            }
            const data = await response.json();
            const playbackId = data.playbackId || data.muxPlaybackId;
            const streamId = data.streamId || data.muxStreamId;
            const streamKey = data.streamKey || data.muxStreamKey;
            if (playbackId && streamId) {
                await updateLiveClass(classItem.id, {
                    muxStreamId: streamId,
                    muxStreamKey: streamKey,
                    muxPlaybackId: playbackId,
                    rtmpUrl: data.rtmpUrl,
                });
                await loadClasses();
                setBroadcastingClassId(classItem.id);
            } else {
                console.error('Stream creation response missing required fields:', data);
            }
        } catch (e) {
            console.error('Stream creation failed:', e);
        }
        setStreamLoading(false);
    };

    const handleDeleteClass = async (classItem) => {
        if (classItem.muxStreamId) {
            try {
                const token = await getToken();
                await fetch(`/api/live-class?streamId=${classItem.muxStreamId}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` },
                });
            } catch (e) { /* non-critical */ }
        }
        await deleteLiveClass(classItem.id);
        if (activeClass?.id === classItem.id) setActiveClass(null);
        await loadClasses();
    };

    // Reminder toggle — gates the reminder save on a working push subscription
    // and fires immediate-send when the class is already inside a notification
    // window. Replaces the prior silent-failure path where "Reminder set ✓"
    // promised a push notification the user would never actually receive.
    //
    // Flow on Set:
    //   1. Browser doesn't support push (e.g. iOS Safari without PWA install,
    //      VAPID keys missing) → toast explains, NO save
    //   2. Push supported but not yet subscribed → trigger permission prompt;
    //      if user clicks Allow, subscribe + proceed; if they decline (or it's
    //      already permanently denied), toast explains and NO save
    //   3. Push subscribed → save the reminder
    //   4. If the class is inside an active notification window (3h or 15min),
    //      call /api/send-reminder-now to fire the push immediately. This
    //      closes Bug #3: the cron's window-match could otherwise miss a
    //      reminder set close to class time.
    //
    // Flow on Unset: removes the reminder doc + clears local state. The doc
    // delete also clears any notifiedTypes tracking — the next set is fresh.
    const handleToggleReminder = async (classItem) => {
        if (!userId) return; // defensive — UI shouldn't expose the button to anonymous users

        // ── Unset path ──
        if (userReminders.has(classItem.id)) {
            await removeClassReminder(userId, classItem.id);
            setUserReminders((prev) => { const n = new Set(prev); n.delete(classItem.id); return n; });
            return;
        }

        // ── Set path ──
        // Gate 1: browser must support web push at all
        if (!isPushSupported()) {
            setReminderToast({
                kind: 'error',
                message: 'Reminders need notification support. On iPhone, install Moments of Movement to your home screen first.',
            });
            return;
        }

        // Gate 2: must have an active subscription. If not, prompt the user
        // (this triggers the browser permission dialog if needed).
        const alreadySubscribed = await isSubscribedToPush();
        if (!alreadySubscribed) {
            const subscribed = await subscribeToPush(userId);
            if (!subscribed) {
                // Differentiate "permanently blocked" from "just dismissed"
                // so the message tells the user what to actually do.
                const perm = getPermissionState();
                const message = perm === 'denied'
                    ? 'Notifications are blocked for this site. Enable them in your browser settings to set reminders.'
                    : 'Reminders need notifications. Tap Allow on the prompt to set reminders.';
                setReminderToast({ kind: 'error', message });
                return;
            }
        }

        // Gate 3: save the reminder doc. Optimistic UI flip so the button
        // feedback is instant.
        const saved = await setClassReminder(userId, classItem.id, classItem.title, classItem.scheduledAt);
        if (!saved) {
            setReminderToast({
                kind: 'error',
                message: 'Couldn\'t save the reminder. Please try again.',
            });
            return;
        }
        setUserReminders((prev) => new Set(prev).add(classItem.id));

        // Gate 4: if the class is already inside a notification window, fire
        // the push immediately. Bug #3 fix — the cron's next tick may not
        // catch reminders set this late (or at all, for the 15min window).
        try {
            const minutesUntil = (new Date(classItem.scheduledAt).getTime() - Date.now()) / 60000;
            let immediateType = null;
            if (minutesUntil > 0) {
                if (minutesUntil >= 165 && minutesUntil <= 200) immediateType = '3h';
                else if (minutesUntil <= 30) immediateType = '15min';
            }
            if (immediateType) {
                const token = await getToken();
                await fetch('/api/send-reminder-now', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({ classId: classItem.id, type: immediateType }),
                });
            }
        } catch (e) {
            // Non-critical: the reminder is saved, the cron will catch it on
            // the next eligible window tick (if any window is still ahead).
            console.warn('Immediate reminder send failed (non-critical):', e?.message);
        }

        setReminderToast({
            kind: 'success',
            message: 'Reminder set! We\'ll notify you before class.',
        });
    };

    // ── Stop recording for pre-recorded class (legacy / fallback path) ──
    // Completes the Mux stream (triggers VOD processing) but does NOT mark the class as ended.
    // The class stays 'scheduled' — the cron auto-transitions it at scheduled time.
    //
    // NOTE (2026-04-20): The primary end-recording path is now handleEndRecording,
    // invoked from the admin's "End Class" button inside LiveBroadcaster via the
    // confirm modal. That path runs seal-before-stop ordering and eliminates the
    // Mux slate. This function is still called by LiveBroadcaster's onBroadcastEnd
    // for any non-graceful stop (e.g. tab close + reload, or the broadcaster's
    // internal error recovery). The recordingEndingRef guard below prevents a
    // duplicate /complete + listener when handleEndRecording is already driving
    // the flow.
    const handleStopRecording = async (classItem) => {
        // Guard: handleEndRecording is already running the hardened flow —
        // don't duplicate the Mux /complete call or the asset-ready listener.
        if (recordingEndingRef.current) return;
        if (classItem.muxStreamId) {
            try {
                const token = await getToken();
                await fetch(`/api/live-class?streamId=${classItem.muxStreamId}`, {
                    method: 'PUT',
                    headers: { 'Authorization': `Bearer ${token}` },
                });
            } catch (e) { console.warn('Non-critical: Mux complete signal failed:', e); }
        }
        // Set uploadStatus to 'processing' as a UI hint — but muxAssetId (set only by
        // the webhook) is the authoritative ready signal, so this write is non-critical
        // even if a race condition overwrites the webhook's 'ready' status
        await updateLiveClass(classItem.id, { uploadStatus: 'processing' });
        setBroadcastingClassId(null);
        await loadClasses();

        // Wait for muxAssetId — the Mux webhook fires asynchronously after recording
        // stops (typically 30s–2min). Without this, the admin sees "processing"
        // forever until they manually refresh.
        //
        // COST OPTIMIZATION (D-074): previously polled fetchLiveClasses() every 10s
        // for 5 minutes (30 full-collection scans on each class stop). Replaced with
        // a single onSnapshot on the specific class doc — fires within milliseconds
        // of the mux-webhook writing muxAssetId, no polling required. 5-min safety
        // timeout preserved so we don't leak the listener if the webhook never fires.
        const pollClassId = classItem.id;
        const unsubscribeAssetWatch = onSnapshot(firestoreDoc(db, 'liveClasses', pollClassId), async (snap) => {
            if (!snap.exists()) return;
            const data = snap.data();
            if (data.muxAssetId) {
                unsubscribeAssetWatch();
                // Refresh the classes list so the admin UI reflects the ready recording.
                // Matches the prior behavior where setClasses(refreshed) fired on first
                // seeing muxAssetId — avoids a second state-transition race.
                try {
                    const refreshed = await fetchLiveClasses();
                    setClasses(refreshed);
                } catch (e) { /* non-critical */ }
            }
        }, () => { /* error handler — swallow; 5-min safety timeout below still unsubscribes */ });
        // Safety timeout: if the webhook never fires (or the listener dropped),
        // release the subscription after 5 min to avoid a leaked listener.
        setTimeout(() => { try { unsubscribeAssetWatch(); } catch (e) { /* already unsubscribed */ } }, 5 * 60 * 1000);
    };

    // ── Purchase: $15/mo subscription ──
    // Goes straight to Stripe Checkout. Express consent is captured at
    // signup via Clerk's express-consent checkbox (see
    // `adr-waiver-clerk-express-consent.md`); no per-purchase modal is
    // needed and we keep checkout as low-friction as possible. Errors
    // surface in an inline toast (checkoutErrorToast) instead of the
    // previous in-modal error display.
    // `plan` is only passed by the plan sheet's Continue. With a yearly offer
    // available, a plain button tap opens the sheet instead of checking out;
    // without one, this is exactly the previous one-tap monthly checkout.
    const handleClassCheckout = async (classItem, _type = 'subscription', plan = null) => {
        if (purchaseLoading) return;
        if (annualOffer && !plan) {
            setPlanPickerFor({ classItem: classItem || null });
            return;
        }
        setPurchaseLoading(true);
        setCheckoutErrorToast('');
        try {
            const token = await getToken();
            const res = await fetch('/api/class-checkout', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    type: 'subscription',
                    classId: classItem?.id || '',
                    classTitle: classItem?.title || '',
                    userEmail,
                    // L-2: Preserve which tab the user was on so post-success redirect can
                    // return them to the right context (e.g. subscribed from On Demand →
                    // back to On Demand). Server whitelists this against known tab values.
                    returnTo: activeTab || '',
                    ...(plan ? { plan } : {}),
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data.sessionUrl) {
                throw new Error(data.error || "We couldn't start checkout. Please try again or contact support.");
            }
            // Redirect — React state no longer matters once this navigates away
            window.location.href = data.sessionUrl;
        } catch (err) {
            console.error('Subscription checkout failed:', err);
            setPlanPickerFor(null); // close the sheet so the error toast is visible
            setPurchaseLoading(false);
            setCheckoutErrorToast(err.message || 'Something went wrong. Please try again.');
        }
    };

    // ══════════════════════════════════════════════
    //  ON DEMAND TAB — ACTIONS
    // ══════════════════════════════════════════════

    // Additive manual completion (event-log model). Each call logs a NEW session
    // (a re-log credits a repeat) with a per-tap occurrence key, and optimistically
    // bumps the local history so the card updates without a refetch. Returns the
    // created record so VideoCard can offer a scoped Undo.
    const handleLogComplete = async (video) => {
        const occurrenceKey = `${video.id}_manual_${Date.now()}`;
        const rec = await markWorkoutComplete(userId, video.id, video.title, null, { occurrenceKey, source: 'manual' });
        if (rec) {
            setCompletedIds((prev) => new Set(prev).add(video.id));
            setPerVideoCompletions((prev) => {
                const cur = prev[video.id];
                const last = rec.completedAt || new Date().toISOString();
                return { ...prev, [video.id]: { count: (cur?.count || 0) + 1, lastCompletedAt: last } };
            });
        }
        return rec;
    };

    // Scoped undo of a single completion (by doc id). Returns success so the
    // persistent undo affordances (card "undo last" / modal "Your history") can
    // roll back their optimistic UI on failure.
    const handleUndoComplete = async (video, completionId) => {
        const ok = await deleteCompletionById(userId, completionId);
        if (!ok) return false;
        setPerVideoCompletions((prev) => {
            const cur = prev[video.id];
            if (!cur) return prev;
            const count = Math.max(0, (cur.count || 1) - 1);
            const next = { ...prev };
            if (count === 0) { delete next[video.id]; } else { next[video.id] = { ...cur, count }; }
            return next;
        });
        // Only clear the ✓ badge if this was the last remaining session.
        setCompletedIds((prev) => {
            const remaining = (perVideoCompletions[video.id]?.count || 1) - 1;
            if (remaining > 0) return prev;
            const n = new Set(prev); n.delete(video.id); return n;
        });
        return true;
    };

    // Lazy loader for the "Your history" remover (card + modal). Resolves the
    // id-backed session list just-in-time (the auto-complete path gives the
    // parent only a videoId). On Demand / free are programId-less, so scope = null.
    const handleLoadHistory = (video) => {
        if (!userId || !video?.id) return Promise.resolve([]);
        return fetchVideoCompletions(userId, video.id, null);
    };

    // Stable callback for VideoPlayerModal auto-complete (75% threshold).
    // useCallback with empty deps because the setters are React-guaranteed stable;
    // without memoization the inline arrow re-creates on every render and forces
    // VideoPlayerModal's auto-complete effect to re-run unnecessarily. See
    // docs/decisions/adr-auto-completion.md + adr-completions-event-log.md.
    const handleVideoAutoComplete = useCallback((videoId) => {
        setCompletedIds((prev) => prev.has(videoId) ? prev : new Set(prev).add(videoId));
        setPerVideoCompletions((prev) => {
            const cur = prev[videoId];
            return { ...prev, [videoId]: { count: (cur?.count || 0) + 1, lastCompletedAt: new Date().toISOString() } };
        });
    }, []);

    // ── Series completion handlers (scoped to the open series) ──
    // All three mutate the per-series map `seriesCompletion[seriesId][videoId]`.
    // Writes thread programId === seriesId + source 'series' so the completions
    // core stays untouched and streak/monthly stats count them automatically.
    const bumpSeriesCompletion = (seriesId, videoId, delta, at) => {
        setSeriesCompletion((prev) => {
            const scope = { ...(prev[seriesId] || {}) };
            const cur = scope[videoId];
            const count = Math.max(0, (cur?.count || 0) + delta);
            if (count === 0) { delete scope[videoId]; }
            else { scope[videoId] = { count, lastCompletedAt: at || cur?.lastCompletedAt || new Date().toISOString() }; }
            return { ...prev, [seriesId]: scope };
        });
    };
    // Silent auto-complete bump (fired by the modal's program path at 75%).
    const handleSeriesAutoComplete = useCallback((videoId) => {
        if (!selectedSeriesId) return;
        bumpSeriesCompletion(selectedSeriesId, videoId, +1, new Date().toISOString());
    }, [selectedSeriesId]);
    // Manual "Mark done" from a day row (resilience for missed auto-detect).
    const handleSeriesMarkDone = async (video) => {
        if (!userId || !selectedSeriesId) return;
        const occurrenceKey = `${video.id}_series_${Date.now()}`;
        const rec = await markWorkoutComplete(userId, video.id, video.title || '', selectedSeriesId, { occurrenceKey, source: 'series' });
        if (rec) bumpSeriesCompletion(selectedSeriesId, video.id, +1, rec.completedAt || new Date().toISOString());
        return rec;
    };
    // Scoped undo (by completion doc id) — wired into the modal's "Your history".
    const handleSeriesUndo = async (video, completionId) => {
        const ok = await deleteCompletionById(userId, completionId);
        if (!ok) return false;
        if (selectedSeriesId) bumpSeriesCompletion(selectedSeriesId, video.id, -1);
        return true;
    };

    // ── Favorite toggle ──
    // Uses setFavoriteState (no pre-read) so the Firestore write queues immediately
    // and survives a pull-to-refresh that lands right after the tap — previously the
    // initial getDoc round-trip could be aborted by refresh, losing the write.
    const handleToggleFavorite = async (classId) => {
        if (!userId || !classId) return;
        const wasFavorited = userFavorites.has(classId);
        const shouldFavorite = !wasFavorited;
        // Optimistic UI update
        setUserFavorites((prev) => {
            const next = new Set(prev);
            if (shouldFavorite) { next.add(classId); } else { next.delete(classId); }
            return next;
        });
        // Update likeCount in local state optimistically
        const delta = shouldFavorite ? 1 : -1;
        setClasses((prev) => prev.map((c) => c.id === classId ? { ...c, likeCount: Math.max(0, (c.likeCount || 0) + delta) } : c));
        setClassRecordings((prev) => prev.map((r) => r.classId === classId ? { ...r, likeCount: Math.max(0, (r.likeCount || 0) + delta) } : r));
        await setFavoriteState(userId, classId, shouldFavorite);
    };

    // ── Video (Shorts) favorite toggle ──
    const handleToggleVideoFavorite = async (videoId) => {
        if (!userId || !videoId) return;
        const wasFavorited = userVideoFavorites.has(videoId);
        const shouldFavorite = !wasFavorited;
        // Optimistic UI update
        setUserVideoFavorites((prev) => {
            const next = new Set(prev);
            if (shouldFavorite) { next.add(videoId); } else { next.delete(videoId); }
            return next;
        });
        const delta = shouldFavorite ? 1 : -1;
        setVideoLikeCounts((prev) => ({ ...prev, [videoId]: Math.max(0, (prev[videoId] || 0) + delta) }));
        await setVideoFavoriteState(userId, videoId, shouldFavorite);
    };

    // ── Toggle free-for-all flag on a recording (admin only) ──
    // Optimistic: flips local state immediately so the card re-renders without
    // a refetch. Mirrors the thumbnail update pattern.
    const handleToggleFreeRecording = async (recording) => {
        if (!recording || !recording.id) return;
        const next = !recording.isFree;
        setClassRecordings((prev) => prev.map((r) =>
            r.id === recording.id ? { ...r, isFree: next } : r
        ));
        const ok = await setClassRecordingFree(recording.id, next);
        if (!ok) {
            // Revert on failure
            setClassRecordings((prev) => prev.map((r) =>
                r.id === recording.id ? { ...r, isFree: !next } : r
            ));
        }
    };

    // ── Thumbnail picker ──
    const openThumbnailPicker = (item, isRecording = false) => {
        const playbackId = item.muxPlaybackId;
        const durationSeconds = item.videoDurationSeconds || item.durationSeconds || (item.duration || 0) * 60;
        if (!playbackId) return;
        setThumbnailPicker({ id: item.id, playbackId, durationSeconds, isRecording });
    };

    const handleThumbnailSelect = async (thumbnailUrl) => {
        if (!thumbnailPicker) return;
        const { id, isRecording } = thumbnailPicker;

        if (isRecording) {
            // Update classRecordings doc
            await updateClassRecordingThumbnail(id, thumbnailUrl);
            setClassRecordings((prev) => prev.map((r) =>
                r.id === id ? { ...r, thumbnailUrl, driveThumbnail: thumbnailUrl } : r
            ));
        } else {
            // Update liveClasses doc — also update classRecording if one exists for this class
            await updateLiveClass(id, { thumbnailUrl });
            setClasses((prev) => prev.map((c) => c.id === id ? { ...c, thumbnailUrl } : c));
            // Find matching recording by classId and update it too
            const matchingRec = classRecordings.find((r) => r.classId === id);
            if (matchingRec) {
                await updateClassRecordingThumbnail(matchingRec.id, thumbnailUrl);
                setClassRecordings((prev) => prev.map((r) =>
                    r.classId === id ? { ...r, thumbnailUrl, driveThumbnail: thumbnailUrl } : r
                ));
            }
        }
        setThumbnailPicker(null);
    };

    // Generate evenly spaced frame timestamps for the picker
    const getThumbnailTimestamps = (durationSeconds) => {
        if (!durationSeconds || durationSeconds < 10) return [0, 2, 5];
        const count = 8;
        // Start at 5% in, end at 90% — avoids black intro/outro frames
        const start = Math.floor(durationSeconds * 0.05);
        const end = Math.floor(durationSeconds * 0.90);
        const step = (end - start) / (count - 1);
        return Array.from({ length: count }, (_, i) => Math.round(start + i * step));
    };

    // ══════════════════════════════════════════════
    //  COMPUTED DATA
    // ══════════════════════════════════════════════

    // Upcoming = scheduled / waiting-room / live / ending (drain) PLUS this week's aired
    // classes. The "aired this week" inclusion (2026-05-04 — extends the prior 2026-04-26
    // aired-today window) keeps the entire current week's arc visible on Schedule until
    // Sunday→Monday rollover. The user's mental model is the cohort week: missing Monday's
    // class shouldn't push the user into the On Demand archive — Monday's tile stays
    // inline so they can catch up Tuesday. Week boundary: Monday 00:00 local → next
    // Monday 00:00 local. Aired-this-week tiles render with a calm "AIRED" treatment and
    // route clicks to the VOD recording (NEVER to LivePlayer — the live-lifecycle
    // invariant is that activeClass auto-clears when state flips to ended on a tab leave;
    // we MUST NOT trigger LivePlayer remount against a sealed manifest).
    //
    // Why `ending` is in upcoming, not past: the drain window is ~12s and viewers who
    // are mid-class are watching through the existing live player. Showing the tile as
    // still-live for those few seconds preserves visual continuity. Once it transitions
    // to `ended` it reclassifies to aired-this-week.
    //
    // See `docs/ux/schedule-weekly-rhythm.md` for the full rationale + acceptance.
    const now = new Date();
    // Monday-start local week. dayOfWeek: 0=Sun, 1=Mon, …, 6=Sat. daysSinceMonday:
    // Sun→6, Mon→0, Tue→1, …, Sat→5. Construct via local-component Date constructor
    // (year/month/day) rather than ms arithmetic so we land on local midnight even
    // across DST boundaries.
    const dayOfWeek = now.getDay();
    const daysSinceMonday = (dayOfWeek + 6) % 7;
    const weekStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysSinceMonday).getTime();
    const weekEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysSinceMonday + 7).getTime();
    const isAiredThisWeek = (c) => {
        const s = c.state || c.status;
        if (s !== 'ended') return false;
        if (!c.scheduledAt) return false;
        const t = new Date(c.scheduledAt).getTime();
        return t >= weekStart && t < weekEnd;
    };
    // Release-day classes (services/classRelease.js) are placed by their release
    // DATE, never by an instant: `scheduleClasses` swaps each one for the viewer's
    // projection (scheduledAt = her local 12:00 AM on that date + a `_release`
    // descriptor), so the day groups, ribbon, sort and anchor below put Monday's
    // class on Monday for everyone. A release-day class belongs to the week of its
    // date (Monday→Sunday local, same as aired classes) whatever its stored state.
    // Every other class passes through untouched — same object, same filters.
    const releaseWeekStartKey = localWeekStartKey(now);
    const scheduleClasses = classes.map((c) => (isReleaseDayClass(c) ? projectReleaseClass(c, now) : c));
    const isReleaseOnSchedule = (c) => {
        const s = c.state || c.status;
        if (s === 'draft' || s === 'cancelled') return false;
        return c._release.date >= releaseWeekStartKey;
    };
    const upcoming = scheduleClasses.filter((c) => {
        // Visibility allowlist: gated classes are invisible to non-listed users.
        // Public classes (no visibleToUserIds) are unaffected. See classVisibility.js.
        if (!canSeeClass(c, { userId, isAdmin })) return false;
        if (c._release) return isReleaseOnSchedule(c);
        const s = c.state || c.status;
        if (s === 'scheduled' || s === 'waiting_room_open' || s === 'live' || s === 'ending') return true;
        if (isAiredThisWeek(c)) return true;
        return false;
    }).sort((a, b) => (a.scheduledAt || '').localeCompare(b.scheduledAt || ''));
    // Past = pre-this-week ended + cancelled (any week). This week's aired classes show
    // inline in upcoming and are excluded here so admin's "Past classes" cleanup section
    // doesn't duplicate them. Cancelled classes (any week) and ended classes from prior
    // weeks remain here for admin delete access.
    const past = scheduleClasses.filter((c) => {
        if (!canSeeClass(c, { userId, isAdmin })) return false;
        if (c._release) {
            const rs = c.state || c.status;
            return rs === 'cancelled' || (rs !== 'draft' && !isReleaseOnSchedule(c));
        }
        const s = c.state || c.status;
        if (s !== 'ended' && s !== 'cancelled') return false;
        if (isAiredThisWeek(c)) return false;
        return true;
    }).sort((a, b) => (b.scheduledAt || '').localeCompare(a.scheduledAt || ''));

    // How many classes are live RIGHT NOW and visible to this viewer. Drives the
    // concurrent-classes affordance only: normally a live class auto-activates, so a
    // live tile needs no join button — but when 2+ are live we suppress auto-activation
    // (see loadClasses) and each live tile the viewer isn't in needs an explicit way in.
    // With 0 or 1 live classes this is ≤1, every gate below is false, and the tiles
    // render exactly as they always have.
    const liveVisibleCount = classes.filter((c) => {
        const s = c.state || c.status;
        return s === 'live' && canSeeClass(c, { userId, isAdmin }) && !isReleaseDayClass(c);
    }).length;

    // Drafts: admin-only staging area. Not in upcoming, not in past, not in
    // any cron query. Sorted by scheduledAt ascending so the next-to-air draft
    // is at top — matches the rhythm of upcoming.
    // The release-day class behind the open player, if any (released for her, or
    // admin, within the last week) — drives the community panel in the player.
    // null for every other video, so the player renders exactly as before.
    const togetherClass = (() => {
        if (!activeVideo || !activeVideo.isClassRecording || !userId) return null;
        const minKey = localDateKey(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7));
        return classes
            .filter((c) => isReleaseDayClass(c)
                && (c.muxAssetId === activeVideo.id || c.vodAssetId === activeVideo.id)
                && c.releaseDate >= minKey
                && (isAdmin || isReleasedForViewer(c))
                && canSeeClass(c, { userId, isAdmin }))
            .sort((a, b) => b.releaseDate.localeCompare(a.releaseDate))[0] || null;
    })();

    const drafts = scheduleClasses.filter((c) => (c.state || c.status) === 'draft')
        .sort((a, b) => (a.scheduledAt || '').localeCompare(b.scheduledAt || ''));

    // On demand: separate into Classes (recordings) and Shorts (free videos)
    // ── Lock-state matrix ─────────────────────────────────────────────────────
    //   subscriptionEnabled === false  → never locked (rollout / free-period mode)
    //   admin / friend / subscriber    → never locked
    //   r.isFree === true              → never locked (admin-marked try-before-buy)
    //   otherwise                       → locked
    //
    // `!== false` semantics on the subs flag are intentional: until `siteConfig`
    // resolves, `subscriptionEnabled` is `undefined` and we want to assume locked
    // (preserve pre-fix protection). We only unlock when admin has *explicitly*
    // turned subs off in Settings. Without this, recordings would flash as
    // unlocked during the ~200ms window between recordings loading and
    // siteConfig loading — a real paywall bypass when subs are actually on.
    //
    // Pre-fix bug: line ignored both `subscriptionEnabled` (so non-subs hit the
    // paywall during a rollout where subs are off) and `r.isFree` (so admin
    // had no way to surface a free recording).
    const subsGateActive = siteConfig.subscriptionEnabled !== false;
    // Lookup: source liveClasses.id → admin-selected scheduled duration (minutes).
    // Used to derive `filterDuration` on each recording so the On Demand → Classes
    // duration filter buckets reflect the curated admin durations (5/10/15/20/30/
    // 45/60/75/90) rather than Mux's exact recorded length, which produces noisy
    // 10/11/12/13 min buckets one minute apart. Reuses already-loaded `classes`
    // state — no new fetch. Note: `fetchLiveClasses` is bounded to past-30-days +
    // future (D-068 cost optimization) so older recordings will miss the lookup.
    // For those (and orphaned recordings where the source class was deleted), we
    // snap the actual recorded length to the nearest curated bucket below so the
    // filter is always clean regardless of data state.
    const classDurationByClassId = useMemo(() => {
        const map = new Map();
        for (const c of classes) {
            if (c.id && typeof c.duration === 'number' && c.duration > 0) {
                map.set(c.id, c.duration);
            }
        }
        return map;
    }, [classes]);
    // Curated class duration options (matches the admin "Duration" dropdown
    // in createLiveClass form — kept in sync with the <select> at line ~3093).
    const CURATED_DURATIONS = [5, 10, 15, 20, 30, 45, 60, 75, 90];
    const snapToCurated = (minutes) => {
        if (!minutes || minutes <= 0) return 0;
        return CURATED_DURATIONS.reduce((closest, b) =>
            Math.abs(b - minutes) < Math.abs(closest - minutes) ? b : closest
        );
    };
    const classVideos = classRecordings.filter((r) => canSeeClass(r, { userId, isAdmin })).map((r) => {
        const adminDuration = r.classId ? classDurationByClassId.get(r.classId) : null;
        // Prefer admin's scheduled value (already from the curated list); fall
        // back to actual recorded `length` snapped to the nearest curated
        // bucket. Snap also defends against legacy classes that may have been
        // saved with a non-curated `duration` value.
        const rawForFilter = adminDuration || r.length || 0;
        // Aired date for the On Demand "Aired {weekday} {month} {day}" badge.
        // Prefer the source class's air time (scheduledAt) when the class is still
        // loaded — for pre-recorded classes the recording can be uploaded days
        // before it airs, so createdAt would be wrong; scheduledAt is the true air
        // date. Fall back to the recording's createdAt (set when the recording was
        // finalized at end-of-airing) for orphaned/old recordings whose source
        // class has aged out of the 30-day window. Prefix reuses the admin-editable
        // airedTodayBadgeLabel so copy stays configurable. Consumed ONLY by the
        // On Demand VideoCard (gated on activeTab there) — never Shorts.
        // Re-aired recording (lastAiredAt, stamped by the cron): the badge shows the
        // LATEST airing — from that re-air class when it's still loaded, else from
        // the stamp. A recording that was never re-aired has neither field, so both
        // lines below reduce to exactly the original expressions.
        const lastAiredClass = r.lastAiredClassId ? classes.find((c) => c.id === r.lastAiredClassId) : null;
        const srcClass = lastAiredClass || (r.classId ? classes.find((c) => c.id === r.classId) : null);
        const airedIso = (r.lastAiredAt && !lastAiredClass)
            ? r.lastAiredAt
            : ((srcClass && srcClass.scheduledAt) || r.createdAt || null);
        let airedLabel = null;
        if (srcClass && isReleaseDayClass(srcClass)) {
            // Release-day class: its date IS the aired date — read as a calendar
            // date, never converted through a time zone (the stored instant is a
            // noon-UTC anchor, which would show the next day east of UTC+12).
            airedLabel = `${siteConfig.airedTodayBadgeLabel || 'Aired'} ${formatReleaseDate(srcClass.releaseDate, { weekday: 'short' })} ${formatReleaseDate(srcClass.releaseDate, { month: 'long' })} ${parseInt(srcClass.releaseDate.slice(8), 10)}`;
        } else if (airedIso) {
            const ad = new Date(airedIso);
            if (!Number.isNaN(ad.getTime())) {
                const wd = ad.toLocaleDateString('en-US', { weekday: 'short' });
                const mo = ad.toLocaleDateString('en-US', { month: 'long' });
                airedLabel = `${siteConfig.airedTodayBadgeLabel || 'Aired'} ${wd} ${mo} ${ad.getDate()}`;
            }
        }
        return {
            ...r,
            isLocked: subsGateActive && !isAdmin && !isFriend && !isSubscriber && !r.isFree,
            // Drives the duration filter buckets on the Classes On Demand tab only.
            // Badges, sorting, and shorts continue to use `length` (actual minutes).
            filterDuration: snapToCurated(rawForFilter),
            airedLabel,
        };
    });

    // ── Deep-link: open a specific On Demand recording from ?recording=<id> ──
    // This is the in-app landing target for the public class page (/class/:id):
    // when a signed-in member opens a shared class link, ClassPage hands them
    // here so they get the full experience (progress, favorites, comments).
    // Also supports direct member-to-member sharing of the in-app link.
    // Placed right after `classVideos` so we reuse the already-resolved
    // `isLocked` flag — a non-subscriber who somehow reaches this with a paid
    // recording gets the subscribe prompt (setLockedVideo), never the player.
    //   - waits for On Demand data (onDemandLoaded) so classVideos is populated
    //   - one-shot via ref so it can't re-fire on later renders / data refreshes
    //   - strips the param via replaceState so refresh/back don't re-trigger
    //   - not found (deleted / still finalizing) → silently lands on On Demand tab
    // When no ?recording param is present this effect is a complete no-op — zero
    // behavioral change for normal Workouts loads.
    // Guard holds the last-handled navigation signature (NOT a blanket boolean).
    // A boolean latched on the first param-less load, which silently disabled the
    // receiver for the rest of the mount — fine for cold loads, but it meant an
    // IN-APP navigation (notification bell while already on /workouts) did nothing
    // until you refreshed. Keying on location.key means each navigation is handled
    // exactly once, while a repeat tap on the same notification still re-opens it.
    const deepLinkHandledRef = useRef('');
    useEffect(() => {
        if (!onDemandLoaded) return;
        // Prefer the router's location — window.location alone doesn't re-render on
        // an in-app navigate(). Falls back to window.location for safety.
        const params = new URLSearchParams(location.search || window.location.search);
        const recId = params.get('recording');
        // ?class= is the notification deep-link fallback for comments posted on the
        // POST-CLASS thread, which carry no recording id at write time (the VOD's
        // muxAssetId lands minutes later via the Mux webhook). Resolving it HERE means
        // it resolves at click time, when the recording exists. Same open path, same
        // isLocked guard — only the lookup key differs.
        const clsId = params.get('class');
        const commentId = params.get('comment'); // optional — from a reply-notification deep-link
        if (!recId && !clsId) return;
        // Signature includes location.key so tapping the same notification twice
        // (each navigate() = a new history entry) re-opens rather than no-ops.
        const navSignature = `${location.key || ''}|${recId || ''}|${clsId || ''}|${commentId || ''}`;
        if (deepLinkHandledRef.current === navSignature) return;
        let match = recId
            ? classVideos.find((v) => v.id === recId)
            : classVideos.find((v) => v.classId === clsId);
        // Release-day class not yet filed into On Demand (between the member's
        // midnight and the release anchor): open it from the class doc, exactly as
        // its Schedule tile does. Waits (without latching) until the classes list
        // has loaded, so a link tapped on a cold load still resolves.
        if (!match && clsId) {
            const relCls = classes.find((c) => c.id === clsId);
            if (!relCls && loading) return;
            if (relCls && isReleaseDayClass(relCls) && (isAdmin || isReleasedForViewer(relCls)) && canSeeClass(relCls, { userId, isAdmin })) {
                const v = resolveReleaseVideo(relCls, classVideos);
                if (v) match = { ...v, isLocked: subsGateActive && !isAdmin && !isFriend && !isSubscriber && !(relCls.price === 0 || relCls.price === '0') };
            }
        }
        deepLinkHandledRef.current = navSignature;
        if (match) {
            setActiveTab('classes');
            // Only carry the comment highlight into the player when we actually open
            // it (not for the locked → subscribe-prompt path).
            if (match.isLocked) setLockedVideo(match);
            else { setActiveVideo(match); if (commentId) setDeepLinkCommentId(commentId); }
        }
        try {
            const url = new URL(window.location.href);
            url.searchParams.delete('recording');
            url.searchParams.delete('class');
            url.searchParams.delete('comment');
            window.history.replaceState({}, '', url.pathname + url.search + url.hash);
        } catch (_) { /* non-critical — leave the URL as-is */ }
    }, [onDemandLoaded, classVideos, location, classes, loading, isAdmin, isFriend, isSubscriber, subsGateActive, userId]);

    // Apply custom ordering to shorts (free videos) if configured
    let shortVideos = freeVideos.map((v) => ({ ...v, isFree: true, isClassRecording: false, likeCount: videoLikeCounts[v.id] || 0 }));
    const freeVideoOrder = siteConfig?.freeVideoOrder;
    if (freeVideoOrder && freeVideoOrder.length > 0) {
        const ordered = [];
        freeVideoOrder.forEach((id) => { const v = shortVideos.find((vid) => vid.id === id); if (v) ordered.push(v); });
        shortVideos.forEach((v) => { if (!freeVideoOrder.includes(v.id)) ordered.push(v); });
        shortVideos = ordered;
    }

    // Completed classes sink to the bottom of On Demand — EXCEPT a class re-aired
    // in the last 7 days that she hasn't done since around this airing: it stays up
    // with this week's classes. (Her last completion must be more than 2 days
    // before the airing — the margin covers a release-day re-air she starts at her
    // own midnight, before the noon-UTC stamp.) Never-re-aired videos have no
    // lastAiredAt, so for them this is exactly completedIds.has(v.id).
    const RECENT_AIRING_MS = 7 * 24 * 60 * 60 * 1000;
    const sinksAsDone = (v) => {
        if (!completedIds.has(v.id)) return false;
        if (!v.lastAiredAt) return true;
        const airedMs = Date.parse(v.lastAiredAt);
        if (!Number.isFinite(airedMs) || Date.now() - airedMs > RECENT_AIRING_MS) return true;
        const lastDone = perVideoCompletions[v.id]?.lastCompletedAt;
        const lastDoneMs = lastDone ? Date.parse(lastDone) : NaN;
        if (!Number.isFinite(lastDoneMs)) return true; // unknown ⇒ today's behaviour
        return lastDoneMs >= airedMs - 2 * 24 * 60 * 60 * 1000;
    };

    // Active tab determines which videos to filter
    const activeOdVideos = activeTab === 'classes' ? classVideos : shortVideos;
    // Classes tab keys filter buckets off the admin-scheduled `filterDuration`
    // (curated 5/10/15/20/30/45/60/75/90 list) so we don't surface noisy
    // one-minute-apart buckets driven by Mux's exact recorded length. Shorts
    // tab keeps the existing actual-length behavior — admins don't schedule
    // durations for shorts, only the encoded video length is meaningful.
    const durationKey = (v) => (activeTab === 'classes' ? v.filterDuration : v.length);
    const availableOdDurations = [...new Set(activeOdVideos.map(durationKey).filter(Boolean))].sort((a, b) => a - b);

    const filteredOnDemand = activeOdVideos
        .filter((v) => odLengthFilter.length === 0 || odLengthFilter.includes(durationKey(v)))
        .filter((v) => odGroupFilter.length === 0 || odGroupFilter.some((g) => v.groups?.includes(g)))
        .filter((v) => {
            if (odCompletedFilter === 'completed') return completedIds.has(v.id);
            return true;
        })
        .filter((v) => {
            if (odFavoritedFilter === 'favorited') {
                // Classes use classId in userFavorites, Shorts use video id in userVideoFavorites
                return v.isClassRecording ? userFavorites.has(v.classId) : userVideoFavorites.has(v.id);
            }
            return true;
        })
        .filter((v) => {
            if (odEaseInFilter === 'easein') return v.easeIn === true;
            return true;
        })
        .filter((v) => {
            if (odStrongPlusFilter === 'strongplus') return v.strongPlus === true;
            return true;
        })
        .sort((a, b) => {
            // Push completed videos to the bottom (unless filtering by completed)
            if (!odCompletedFilter) {
                const aComp = sinksAsDone(a) ? 1 : 0;
                const bComp = sinksAsDone(b) ? 1 : 0;
                if (aComp !== bComp) return aComp - bComp;
            }
            // Newest = most recently AIRED: a re-aired class sorts by its latest
            // airing (lastAiredAt); everything else by its original date as before.
            if (odSortBy === 'newest') return (b.lastAiredAt || b.createdTime || b.createdAt || '').localeCompare(a.lastAiredAt || a.createdTime || a.createdAt || '');
            if (odSortBy === 'length-asc') return (a.length || 0) - (b.length || 0);
            if (odSortBy === 'length-desc') return (b.length || 0) - (a.length || 0);
            return (a.title || '').localeCompare(b.title || '');
        });

    // Class thumbnail helper — shows duration in a branded pink pill when no cover image
    const ClassThumb = ({ cls, size = 56 }) => (
        <div style={{
            width: size, height: size, borderRadius: 14, flexShrink: 0,
            background: cls.status === 'live'
                ? 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))'
                : 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1,
        }}>
            <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: size * 0.34, fontWeight: 800, color: '#fff', lineHeight: 1 }}>
                {cls.duration || 45}
            </span>
            <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: size * 0.18, fontWeight: 600, color: 'rgba(255,255,255,0.8)', textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1 }}>
                min
            </span>
        </div>
    );

    // Relative day label helper (pure function, no side effects)
    const getRelativeDay = (isoString) => {
        if (!isoString) return '';
        const now = new Date();
        const d = new Date(isoString);
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const target = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        const diffDays = Math.round((target - today) / (1000 * 60 * 60 * 24));
        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Tomorrow';
        if (diffDays > 1 && diffDays <= 6) return d.toLocaleDateString('en-US', { weekday: 'long' });
        return '';
    };

    // ── Editorial schedule helpers ("Week with Danielle" layout) ──
    // Pure local YYYY-MM-DD key for day-grouping (timezone-agnostic within the browser's TZ)
    const dateKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    // Editorial day label — "Today" / "Tomorrow" / day name / full date. Used as the
    // headline for each day-group in the editorial stream and for the spotlight eyebrow.
    const getEditorialDayLabel = (date) => {
        const today = new Date();
        const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const todayMid = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const diffDays = Math.round((target - todayMid) / 86400000);
        const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
        const dateStr = date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
        if (diffDays === 0) return { primary: 'Today', secondary: dayName };
        if (diffDays === 1) return { primary: 'Tomorrow', secondary: dayName };
        if (diffDays > 1 && diffDays < 7) return { primary: dayName, secondary: dateStr };
        return { primary: dayName, secondary: dateStr };
    };

    // Static relative-time string for the class detail modal — computed once at
    // open time, not live-ticking. Premium feels calm, not anxious. (Stale-by-up-to-
    // a-minute is fine; the user closes the modal and sees the live tile.)
    const formatRelativeStart = (scheduledAt, classState) => {
        if (classState === 'live') return 'Live now';
        if (classState === 'ending') return 'Wrapping up';
        if (!scheduledAt) return '';
        const diffMs = new Date(scheduledAt).getTime() - Date.now();
        if (diffMs < 60000) return 'Starting now';
        const minutes = Math.floor(diffMs / 60000);
        if (minutes < 60) return `Starts in ${minutes} min`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) {
            const remainMin = minutes % 60;
            return remainMin > 0 ? `Starts in ${hours}h ${remainMin}m` : `Starts in ${hours}h`;
        }
        const days = Math.floor(hours / 24);
        return days === 1 ? 'Starts tomorrow' : `Starts in ${days} days`;
    };

    // Day ribbon range: starts at the most recent Monday (start of current week, local TZ),
    // extends through today + 30 days. On Monday this is exactly today→+30d (legacy
    // behavior preserved). Mid-week (Tue–Sun) it extends backward into this week so the
    // user can navigate to past days within the cohort week. The ribbon's initial scroll
    // position aligns today to the leftmost visible cell (see ribbon-scroll effect),
    // matching the prior convention of "today is anchored leftmost on default scroll."
    //
    // Each day is tagged with a class count derived from `upcoming` — which (post-2026-05-04)
    // includes aired-this-week classes, so past days within this week show their dot
    // indicator just like future days.
    //
    // `showMonthLabel` fires at the ribbon start AND at each calendar month boundary so
    // the vertical month abbreviation (APR, MAY, …) always precedes the first day of
    // whatever month is in view. `isMonthBoundary` is the mid-ribbon variant — it's the
    // only one that gets a left divider line (ribbon start doesn't need one).
    //
    // `isToday` is computed by dateKey comparison rather than index because today is no
    // longer guaranteed to be at index 0 (mid-week, today sits at index = daysSinceMonday).
    const weekDays = (() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayKey = dateKey(today);
        const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
        const dow = today.getDay();
        const backwardDays = (dow + 6) % 7; // Mon=0, Tue=1, …, Sun=6
        const totalDays = backwardDays + 30; // current-week-past + today + future-30
        const start = new Date(today);
        start.setDate(today.getDate() - backwardDays);
        return Array.from({ length: totalDays }, (_, i) => {
            const d = new Date(start);
            d.setDate(start.getDate() + i);
            const key = dateKey(d);
            const count = upcoming.filter((c) => c.scheduledAt && dateKey(new Date(c.scheduledAt)) === key).length;
            const isMonthBoundary = i > 0 && d.getDate() === 1;
            const showMonthLabel = i === 0 || isMonthBoundary;
            return {
                date: d, key, dayLetter: DOW[d.getDay()], isToday: key === todayKey, classCount: count,
                showMonthLabel, isMonthBoundary,
            };
        });
    })();

    // Group upcoming classes by day — used for two things:
    //   1. Counting classes per day in the week ribbon (day.classCount)
    //   2. Resolving the selected-day filter (grabbing one group's classes for the flat grid)
    // The grid itself no longer iterates over groups; it iterates over upcoming flat and draws
    // day labels inline on each "day-opening" tile.
    const dayGroups = (() => {
        const groups = new Map();
        upcoming.forEach((c) => {
            if (!c.scheduledAt) return;
            const d = new Date(c.scheduledAt);
            const key = dateKey(d);
            if (!groups.has(key)) groups.set(key, { key, date: d, classes: [] });
            groups.get(key).classes.push(c);
        });
        return Array.from(groups.values()).sort((a, b) => a.date - b.date);
    })();

    // Admin list date line. A release-day class shows its DATE (it has no time);
    // everything else is the original formatClassTime output.
    const classWhenLabel = (c) => (c && c._release
        ? `Release day · ${formatReleaseDate(c._release.date, { weekday: 'short', month: 'short', day: 'numeric' })}`
        : formatClassTime(c.scheduledAt));

    // Spotlight eyebrow — drives the small caps label above the hero title.
    // Three states: LIVE now / starting soon / scheduled day label.
    const getSpotlightEyebrow = (cls) => {
        if (!cls || !cls.scheduledAt) return '';
        // Release-day class: no start time, so no "Starting soon" — it's either
        // today's class (out now) or the next one to come out.
        if (cls._release) {
            if (cls._release.released) return siteConfig.releaseTodayEyebrow || "Today's class";
            const rel = getRelativeDay(cls.scheduledAt);
            return rel ? `Next up · ${rel}` : 'Next up';
        }
        const isLive = cls.status === 'live' && (cls.classType || 'live') === 'live';
        if (isLive) return 'Happening now · Live';
        if (isClassSoon(cls.scheduledAt)) return 'Starting soon';
        const rel = getRelativeDay(cls.scheduledAt);
        if (rel) return `Next up · ${rel}`;
        return 'Next up';
    };

    // ══════════════════════════════════════════════
    //  RENDER
    // ══════════════════════════════════════════════

    return (
        <div className="mom-page-shell" style={{ background: 'var(--mom-cream)', minHeight: '100vh', position: 'relative', zIndex: 1 }}>
            <AppNav demoMode={demoMode} />

            {/* ── Success toast ── */}
            {successToast && (
                <div style={{
                    position: 'fixed', top: 24, left: '50%', transform: 'translateX(-50%)',
                    padding: '12px 28px', borderRadius: 14, zIndex: 9999,
                    background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                    color: '#fff', fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)', animation: 'fadeIn 0.3s ease',
                    whiteSpace: 'nowrap',
                }}>
                    {successToast}
                </div>
            )}

            {/* ── Welcome modal (replaces the post-subscribe toast) ── */}
            <WelcomeModal
                open={welcomeOpen}
                content={welcomeContent}
                onClose={handleWelcomeClose}
            />

            <div style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 24px' }}>

                {/* ── Segmented Control: Live · On Demand · Shorts · (Series) ──
                    Tabs are grouped by access tier so a user can see at a glance which
                    content is paid vs free: a Membership cluster (Schedule + On Demand)
                    and a Free cluster (Shorts + Series), each captioned by a muted
                    micro-caps eyebrow + short accent rule (terracotta vs sage), separated
                    by whitespace inside ONE glass pill — no per-tab badges, no fills.
                    Single straight-across row at every width: desktop clusters are
                    content-sized; mobile clusters share the row (buttons flex to fit)
                    with tighter type/padding so 4 tabs don't overflow a phone. Tolerates
                    3 tabs (Series off) and 4 (on). Tier copy is admin-editable via
                    siteConfig (workoutsTier{Membership,Free}Label) with inline fallbacks. */}
                {/* Pinned-detection sentinel (see the tabsPinned effect). */}
                <div ref={tabsSentinelRef} aria-hidden="true" style={{ height: 1, marginBottom: -1 }} />
                {/* Sticky: the bar stays reachable while scrolling a long schedule.
                    The full-width wrapper is click-through (pointerEvents none) so
                    the empty space beside the pill never blocks content scrolling
                    beneath it; only the pill itself takes clicks. zIndex 50 sits
                    under AppNav (100) and every modal (>=1000). */}
                <div style={{
                    display: 'flex', justifyContent: 'center', marginBottom: 28,
                    position: 'sticky', top: (isNarrowNav ? 52 : 70) + 8, zIndex: 50,
                    pointerEvents: 'none',
                }}>
                    {(() => {
                        const allTabs = [
                            { key: 'live', label: siteConfig.workoutsTabUpcoming || 'Schedule', tier: 'membership' },
                            { key: 'classes', label: siteConfig.workoutsTabOnDemand || 'On Demand', tier: 'membership' },
                            { key: 'shorts', label: siteConfig.workoutsTabShorts || 'Shorts', tier: 'free' },
                            // Series tab: admins always see it; everyone else only
                            // when the admin has flipped the flag on. See adr-series.md.
                            ...((isAdmin || siteConfig.enableSeriesTab) ? [{ key: 'series', label: siteConfig.workoutsTabSeries || 'Series', tier: 'free' }] : []),
                        ];
                        const membershipTabs = allTabs.filter((t) => t.tier === 'membership');
                        const freeTabs = allTabs.filter((t) => t.tier === 'free');

                        const renderTabButton = (tab) => {
                            // Selected-tab ring/glow takes its zone's accent: terracotta
                            // for Membership, sage for Free — so the color story carries
                            // through to the active state, not just the zone outline.
                            const accent = tab.tier === 'free' ? 'var(--mom-success)' : 'var(--mom-primary)';
                            return (
                            <button key={tab.key} onClick={() => {
                                // Reset filters when switching to a content tab
                                if (tab.key !== activeTab && (tab.key === 'classes' || tab.key === 'shorts')) {
                                    setOdLengthFilter([]);
                                    setOdGroupFilter([]);
                                    setOdSortBy('newest');
                                    setOdCompletedFilter(null);
                                }
                                // Pinned bar: switching tabs from deep in a list would
                                // otherwise drop the member mid-way down the NEW tab (or on
                                // blank space if it's shorter). Return to where the bar
                                // pins — the top of the content — exactly as if they had
                                // scrolled up to the bar like before it was sticky.
                                if (tabsPinned && tab.key !== activeTab && tabsSentinelRef.current) {
                                    const pinAt = tabsSentinelRef.current.getBoundingClientRect().top + window.scrollY
                                        - ((isNarrowNav ? 52 : 70) + 8);
                                    window.scrollTo(window.scrollX, Math.max(0, pinAt));
                                }
                                setActiveTab(tab.key);
                            }}
                                style={{
                                    padding: isMobile ? '9px 4px' : '10px 28px', textAlign: 'center',
                                    flex: isMobile ? '1 1 0' : '0 0 auto', minWidth: 0, whiteSpace: 'nowrap',
                                    fontFamily: 'var(--mom-font-sans)', fontSize: isMobile ? 12 : 13, fontWeight: 600,
                                    color: activeTab === tab.key ? 'var(--mom-charcoal)' : 'var(--mom-warm-gray)',
                                    background: activeTab === tab.key ? 'var(--mom-white)' : 'transparent',
                                    border: 'none', cursor: 'pointer', borderRadius: 11,
                                    boxShadow: activeTab === tab.key ? `0 0 0 1.5px color-mix(in srgb, ${accent} 38%, transparent), 0 1px 3px rgba(0,0,0,0.06), 0 4px 14px color-mix(in srgb, ${accent} 10%, transparent), inset 0 1px 0 rgba(255,255,255,0.8)` : 'none',
                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                }}>
                                {tab.label}
                            </button>
                            );
                        };

                        const eyebrowBase = {
                            fontFamily: 'var(--mom-font-sans)',
                            fontSize: isMobile ? 8.5 : 9.5, fontWeight: 700,
                            letterSpacing: isMobile ? 1 : 1.4, textTransform: 'uppercase',
                            textAlign: 'center', whiteSpace: 'nowrap',
                            padding: '0 4px 4px', userSelect: 'none',
                        };

                        // No boxes / fills — color is a whisper, not a block. Each group
                        // is just a muted eyebrow + a short accent rule (terracotta for
                        // Membership, sage for Free) over its buttons; the groups are
                        // separated by whitespace inside ONE glass pill. On mobile the
                        // clusters stay in the same row and flex-share the width (by
                        // button count) so 4 tabs fit a phone in one straight line.
                        const renderCluster = (tabs, eyebrow, accent) => (
                            <div style={{
                                display: 'flex', flexDirection: 'column', alignItems: 'center',
                                flex: isMobile ? `${tabs.length} 1 0` : '0 0 auto', minWidth: 0,
                                width: 'auto',
                            }}>
                                {/* Tier caption stays visible while pinned (owner
                                    preference: the Membership/Free labels are worth the
                                    extra height), so the bar never changes size on scroll. */}
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <div style={{ ...eyebrowBase, color: `color-mix(in srgb, ${accent} 58%, var(--mom-charcoal))` }}>{eyebrow}</div>
                                <div aria-hidden="true" style={{
                                    width: 20, height: 2, borderRadius: 2, marginBottom: 8,
                                    background: accent, opacity: 0.55,
                                }} />
                                </div>
                                <div style={{ display: 'flex', gap: 4, width: isMobile ? '100%' : 'auto' }}>
                                    {tabs.map(renderTabButton)}
                                </div>
                            </div>
                        );

                        return (
                            <div style={{
                                display: 'flex', flexDirection: 'row',
                                alignItems: 'center',
                                gap: isMobile ? 10 : 26,
                                padding: isMobile ? '7px 8px 9px' : '8px 20px 10px',
                                borderRadius: 16,
                                width: isMobile ? '100%' : 'auto',
                                pointerEvents: 'auto',
                                // Pinned: denser glass + a lifted shadow so class tiles
                                // scrolling beneath read as BEHIND the bar, not through it.
                                background: tabsPinned ? 'rgba(255,255,255,0.86)' : 'rgba(255,255,255,0.55)',
                                backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)',
                                border: '1px solid rgba(255,255,255,0.6)',
                                boxShadow: tabsPinned
                                    ? '0 2px 6px rgba(0,0,0,0.05), 0 10px 30px rgba(62,47,47,0.12)'
                                    : '0 2px 8px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.03)',
                                transition: 'background 0.2s ease, box-shadow 0.2s ease',
                            }}>
                                {renderCluster(membershipTabs, siteConfig.workoutsTierMembershipLabel || 'Membership', 'var(--mom-primary)')}
                                {freeTabs.length > 0 && renderCluster(freeTabs, siteConfig.workoutsTierFreeLabel || 'Free', 'var(--mom-success)')}
                            </div>
                        );
                    })()}
                </div>

                {/* ── Class Guide — quiet orientation for the two membership tabs ──
                    Collapsed to a single line; opens into a calm glass panel covering
                    Schedule / On Demand / Ease In / Strong+. Rendered from ONE
                    definition for both tabs so the two can never drift apart, with
                    maxWidth matched to each tab's content container so it lines up
                    with the cards beneath. Admin-hideable via showClassGuide (default
                    on). See src/components/ClassGuide.js. */}
                {(activeTab === 'live' || activeTab === 'classes') && siteConfig.showClassGuide !== false
                    // Stand down while the member is in a class (waiting room, live, or
                    // the thank-you drain) — the player owns the screen at that moment and
                    // orientation copy above it is noise. activeClass clears on tab-leave
                    // and after the class ends (LIVE-017), so the guide comes back on its own.
                    && !(activeTab === 'live' && activeClass) && (
                    <ClassGuide
                        siteConfig={siteConfig}
                        isMobile={isMobile}
                        strongPlusColor={STRONG_PLUS_COLOR}
                        maxWidth={activeTab === 'live' ? 800 : 1200}
                    />
                )}

                {/* ══════════════════════════════════════════
                    LIVE TAB
                ══════════════════════════════════════════ */}
                {activeTab === 'live' && (
                    <div style={{ maxWidth: 800, margin: '0 auto' }}>
                        {loading ? (
                            <div style={{ textAlign: 'center', padding: 60 }}>
                                <div style={{ width: 28, height: 28, borderRadius: '50%', border: '3px solid var(--mom-cream-dark)', borderTopColor: 'var(--mom-primary)', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-warm-gray)' }}>Loading classes...</p>
                            </div>
                        ) : (
                            <>
                                {/* ── Active viewer: live, ending (drain), or ended ── */}
                                {activeClass && (activeClass.status === 'live' || activeClass.status === 'ending' || activeClass.status === 'ended') && !isAdmin && canSeeClass(activeClass, { userId, isAdmin }) && (
                                    <div ref={activeClassRef} style={{ marginBottom: 32 }}>
                                        {userCanAccessClass(activeClass.id) ? (
                                            <LivePlayer playbackId={activeClass.muxPlaybackId} title={activeClass.title} state={activeClass.state} status={activeClass.status} scheduledAt={activeClass.scheduledAt} classId={activeClass.id} isAdmin={false} onStreamEnded={() => setClassEndedLocally(true)} classType={activeClass.classType || 'live'} isFavorited={userFavorites.has(activeClass.id)} onToggleFavorite={userId ? () => handleToggleFavorite(activeClass.id) : undefined} userId={userId} endingAt={activeClass.endingAt || null} />
                                        ) : (
                                            <div style={{
                                                position: 'relative', width: '100%', paddingBottom: '56.25%',
                                                background: 'linear-gradient(135deg, var(--mom-charcoal), var(--mom-charcoal-light))',
                                                borderRadius: 20, overflow: 'hidden',
                                            }}>
                                                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#fff', textAlign: 'center', padding: 24 }}>
                                                    <span style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 'clamp(16px, 3vw, 22px)', fontWeight: 700, fontStyle: 'italic', marginBottom: 4 }}>moments of movement</span>
                                                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 'clamp(8px, 1.5vw, 10px)', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: 24 }}>with Danielle</span>
                                                    <p style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 18, fontStyle: 'italic', marginBottom: 12 }}>This class is live now!</p>
                                                    <button onClick={() => handleClassCheckout(activeClass, 'subscription')} disabled={purchaseLoading}
                                                        style={{ padding: '12px 28px', borderRadius: 12, fontSize: 15, fontWeight: 700, background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))', color: '#fff', border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)', opacity: purchaseLoading ? 0.5 : 1, boxShadow: '0 4px 16px rgba(0,0,0,0.2)' }}>
                                                        {purchaseLoading ? 'Redirecting...' : `${priceLabel} · ${subscribeCta} for Access`}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        {/* Presence chip row — visible through live + drain window (hidden once ended). */}
                                        {(activeClass.status === 'live' || activeClass.status === 'ending') && (
                                            <ViewerPresenceRow viewers={liveViewers} onExpand={() => setPresenceSheetOpen(true)} />
                                        )}
                                        <div style={{ padding: '12px 0', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                                            <div>
                                                <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 20, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 4 }}>{activeClass.title}</h2>
                                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-warm-gray)' }}>{formatClassTime(activeClass.scheduledAt)} · {activeClass.duration || 45} min</p>
                                            </div>
                                            {userId && (
                                                <button onClick={() => handleToggleFavorite(activeClass.id)}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                                                    <svg width="22" height="22" viewBox="0 0 24 24" fill={userFavorites.has(activeClass.id) ? 'var(--mom-primary)' : 'none'} stroke="var(--mom-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'all 0.2s ease' }}>
                                                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                                                    </svg>
                                                    {(activeClass.likeCount || 0) > 0 && (
                                                        <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: 'var(--mom-primary)' }}>{activeClass.likeCount}</span>
                                                    )}
                                                </button>
                                            )}
                                        </div>

                                        {/* ── Post-class comments — visible when class ends OR is in the drain window.
                                            Drain-window unlock (Fix 3, revises D-061): once the instructor has said
                                            goodbye, viewers want to comment immediately. The previous gate waited for
                                            status==='ended' (T+120s cleanup), which felt like a 2-minute lockout after
                                            the class actually finished. Opening at 'ending' lets commenting happen while
                                            buffered audio naturally winds down. Works for both live and pre-recorded.

                                            Pre-recorded local unlock (2026-04-21): for pre-recorded classes, Firestore
                                            state stays 'live' until the 5-min cron tick catches it, but the viewer's
                                            Mux Player fires `ended` at THEIR actual video end. We capture that via
                                            LivePlayer's onStreamEnded callback → classEndedLocally state, and unlock
                                            comments for this viewer immediately. Per-viewer accuracy eliminates the
                                            tail-truncation bug that server-scheduled timing caused (see useState
                                            classEndedLocally declaration above). ── */}
                                        {(activeClass.status === 'ended' || activeClass.status === 'ending' || activeClass.state === 'ended' || activeClass.state === 'ending' || classEndedLocally) && userId && (
                                            <div style={{ marginTop: 8 }}>
                                                {/* Class description is intentionally NOT rendered in the post-class
                                                    view. Pre-class, the description appears in the Schedule tile's
                                                    detail modal (where the viewer decided to join) and on the
                                                    locked-video paywall. Post-class, the viewer has just experienced
                                                    the class — repeating the description pushes the comments and
                                                    favorite controls below the fold without adding value. The
                                                    description still lives on the class doc and is rendered in
                                                    every other surface (On Demand player, admin views, etc.). */}
                                                <ClassComments
                                                    classId={activeClass.id}
                                                    recordingId={null}
                                                    userId={userId}
                                                    userName={clerkUser ? `${clerkUser.firstName || ''} ${(clerkUser.lastName || '').charAt(0)}.`.trim() : ''}
                                                    isAdmin={isAdmin}
                                                    commentCount={activeClass.commentCount || 0}
                                                    defaultExpanded={true}
                                                    getToken={getToken}
                                                />
                                                <button onClick={() => { setActiveClass(null); loadClasses(); }}
                                                    style={{
                                                        marginTop: 20, width: '100%', padding: '12px 24px',
                                                        borderRadius: 14, fontSize: 14, fontWeight: 600,
                                                        background: 'var(--mom-cream)', color: 'var(--mom-charcoal)',
                                                        border: 'none', cursor: 'pointer',
                                                        fontFamily: 'var(--mom-font-sans)',
                                                        transition: 'background 0.15s ease',
                                                    }}>
                                                    Back to classes
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* ── Admin: BroadcasterCueSequence (D-053) ──
                                    Replaces the retired GoLiveCue blind-countdown. 5 steps that gate on
                                    REAL pipeline confirmations: WS open → stabilized → Mux connected →
                                    countdown → GO. By the time step 5 fires, viewers truly are live on
                                    Mux's CDN. Only mounted for true-live classes (pre-recorded assets
                                    stream an already-processed VOD with no propagation gap). */}
                                {isAdmin && broadcastingClassId && (classes.find((c) => c.id === broadcastingClassId)?.classType || 'live') === 'live' && (
                                    <BroadcasterCueSequence
                                        active={cueActive}
                                        wsOpen={cueWsOpen}
                                        stabilized={cueStabilized}
                                        muxConnected={cueMuxConnected}
                                        onLive={() => {
                                            // Step 5 'GO' — promote the class to live. This is the authoritative
                                            // moment, gated on the full pipeline being healthy end-to-end.
                                            const cls = classes.find((c) => c.id === broadcastingClassId);
                                            if (cls && (cls.state === 'scheduled' || cls.status === 'scheduled') && (cls.classType || 'live') === 'live') {
                                                handleGoLive(cls);
                                            }
                                        }}
                                        onDismiss={() => {
                                            // Cue has finished its 2s hold on step 5 — reset for next session.
                                            setCueActive(false);
                                            setCueWsOpen(false);
                                            setCueStabilized(false);
                                            setCueMuxConnected(false);
                                        }}
                                    />
                                )}

                                {/* ── Admin: Broadcaster / Recorder ── */}
                                {/* Same component for both live (broadcasting) and pre-recorded (recording).
                                    Wrapped in a ref'd/pulsed div so the Workouts page can scroll to it and
                                    briefly highlight it — without this, clicking "Open Broadcaster" from a
                                    class card deep in the list silently mounts the panel at the top with no
                                    visible feedback. */}
                                {isAdmin && broadcastingClassId && (
                                    <div
                                        ref={broadcasterRef}
                                        key={`broadcaster-${broadcastingClassId}`}
                                        style={{
                                            marginBottom: 24,
                                            borderRadius: 16,
                                            padding: adminPulse === 'broadcaster' ? 8 : 0,
                                            background: adminPulse === 'broadcaster'
                                                ? 'linear-gradient(135deg, color-mix(in srgb, var(--mom-primary) 14%, transparent), color-mix(in srgb, var(--mom-primary) 4%, transparent))'
                                                : 'transparent',
                                            boxShadow: adminPulse === 'broadcaster'
                                                ? '0 0 0 2px color-mix(in srgb, var(--mom-primary) 45%, transparent), 0 8px 28px color-mix(in srgb, var(--mom-primary) 18%, transparent)'
                                                : 'none',
                                            transition: 'all 0.5s ease',
                                        }}>
                                        <LiveBroadcaster
                                            rtmpUrl={classes.find((c) => c.id === broadcastingClassId)?.rtmpUrl}
                                            streamKey={classes.find((c) => c.id === broadcastingClassId)?.muxStreamKey}
                                            classType={classes.find((c) => c.id === broadcastingClassId)?.classType || 'live'}
                                            // classId wires the IDB recording-buffer service (2026-04-23):
                                            // MediaRecorder chunks are scoped per-class so a prior aborted
                                            // recording on a different class doesn't pollute this session's
                                            // Blob assembly, and recovery-on-mount can pair surviving chunks
                                            // with the correct class doc. Live classes ignore this prop.
                                            classId={broadcastingClassId}
                                            // endingAt: two sources folded together —
                                            //   • Live classes: activeClass.endingAt (set by handleEndClass)
                                            //   • Pre-recorded RECORDING phase: recordingEndingAt (set by handleEndRecording)
                                            // In both cases, transitioning from null → timestamp triggers LiveBroadcaster's
                                            // endingAt effect, which fires stopBroadcast (5s WS flush) against an already-
                                            // sealed Mux manifest.
                                            endingAt={(activeClass?.id === broadcastingClassId ? (activeClass?.endingAt || null) : null) || recordingEndingAt}
                                            // Option A unification (extended 2026-04-20 to live classes): the End Class
                                            // button inside LiveBroadcaster fires this callback for BOTH class types →
                                            // we open the confirm modal → on confirm, handleEndClass routes live classes
                                            // through the live seal-first path and delegates pre-recorded RECORDING phase
                                            // to handleEndRecording. The compact End Class button above the Viewer Preview
                                            // (rendered when admin views an active live/pre-recorded class) also calls
                                            // setEndClassConfirm and routes to the same confirm modal — kept as a defensive
                                            // fallback for observer admins or cases where LiveBroadcaster isn't mounted.
                                            onEndClassRequest={() => {
                                                const cls = classes.find((c) => c.id === broadcastingClassId);
                                                if (cls) setEndClassConfirm(cls);
                                            }}
                                            onBroadcastStart={() => {
                                                // D-053: for LIVE classes, the status flip to 'live' is deferred
                                                // to the cue's step 5 (onLive). Firing handleGoLive here would
                                                // race the cue and flip status before the 3s countdown completes.
                                                // For pre-recorded classes, no cue is shown and this callback
                                                // remains a no-op (recording stays 'scheduled' until Stop).
                                                const cls = classes.find((c) => c.id === broadcastingClassId);
                                                if (cls && (cls.classType || 'live') !== 'live') {
                                                    // Reserved for future pre-recorded-specific actions.
                                                }
                                            }}
                                            onBroadcastEnd={() => {
                                                const cls = classes.find((c) => c.id === broadcastingClassId);
                                                // Pre-recorded path post-2026-04-21 uses Mux Direct Upload —
                                                // handleRecordingComplete (wired to onRecordingComplete below)
                                                // handles the entire upload + asset-ready chain. handleStopRecording
                                                // is only retained as a legacy safety net; its recordingEndingRef
                                                // guard makes it a no-op when handleEndRecording is driving the flow.
                                                // Calling it here still guards against non-graceful stops where
                                                // recordingEndingRef was never latched (e.g. WS error recovery).
                                                if (cls?.classType === 'prerecorded') {
                                                    handleStopRecording(cls);
                                                }
                                            }}
                                            // Pre-recorded Direct Upload: LiveBroadcaster hands us the assembled
                                            // Blob here after MediaRecorder.stop(). handleRecordingComplete runs
                                            // the /api/mux-direct-upload → XHR PUT → webhook-wait chain. See
                                            // comments above handleRecordingComplete for detail.
                                            onRecordingComplete={(blob, mimeType) => {
                                                const cls = classes.find((c) => c.id === broadcastingClassId);
                                                if (cls) handleRecordingComplete(cls, blob, mimeType);
                                            }}
                                            onWsOpen={() => {
                                                // D-053 step 1 → 2: WebSocket to relay is open. Cue activates
                                                // and advances from 'connecting' to 'stabilizing'. Only drives
                                                // UI for live classes; pre-recorded skips the cue entirely.
                                                setCueActive(true);
                                                setCueWsOpen(true);
                                            }}
                                            onStabilized={() => {
                                                // D-053 step 2 → 3: ~3 consecutive healthy fps/speed samples.
                                                setCueStabilized(true);
                                            }}
                                            onMuxConnected={() => {
                                                // D-053 step 3 → 4: relay's RTMP → Mux handshake is complete.
                                                setCueMuxConnected(true);
                                            }}
                                        />
                                    </div>
                                )}

                                {/* ── Admin: Viewer Preview + Viewer Count + End Class ──
                                    Works for both live and pre-recorded across the live → ending (drain) → ended
                                    lifecycle so admin sees who's in the room regardless of class type. End Class
                                    button only renders during 'live' (not 'ending' — drain is already underway). */}
                                {activeClass && (activeClass.status === 'live' || activeClass.status === 'ending' || activeClass.status === 'ended') && isAdmin && (
                                    <div ref={activeClassRef} style={{ marginBottom: 24 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 700, color: 'var(--mom-warm-gray)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Viewer Preview</p>
                                                <div style={{
                                                    display: 'flex', alignItems: 'center', gap: 6,
                                                    padding: '5px 14px', borderRadius: 20,
                                                    background: viewerCount > 0 ? 'color-mix(in srgb, var(--mom-primary) 12%, transparent)' : 'var(--mom-cream)',
                                                    border: viewerCount > 0 ? '1px solid var(--mom-primary)' : '1px solid var(--mom-cream-dark)',
                                                }}>
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={viewerCount > 0 ? 'var(--mom-primary)' : 'var(--mom-warm-gray)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
                                                        <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                                    </svg>
                                                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 700, color: viewerCount > 0 ? 'var(--mom-primary)' : 'var(--mom-warm-gray)' }}>
                                                        {viewerCount} {viewerCount === 1 ? 'viewer' : 'viewers'}
                                                    </span>
                                                </div>
                                            </div>
                                            {activeClass.status === 'live' && (
                                                <button onClick={() => setEndClassConfirm(activeClass)}
                                                    style={{ padding: '8px 20px', borderRadius: 10, fontSize: 13, fontWeight: 700, background: '#dc2626', color: '#fff', border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)' }}>
                                                    {/* For pre-recorded classes the asset auto-plays and the cron auto-
                                                        transitions to ended at scheduledAt+duration — clicking this button
                                                        cuts the class SHORT before the natural end. Relabel to "End Early"
                                                        so admin sees it as a kill-switch, not a normal-end button (otherwise
                                                        easy to misread as "the natural way to end the class"). True-live
                                                        classes still need an explicit end every time, so they keep "End Class". */}
                                                    {(activeClass.classType || 'live') === 'prerecorded' ? 'End Early' : 'End Class'}
                                                </button>
                                            )}
                                        </div>
                                        {/* Pre-recorded classes play a ready Mux asset — no broadcast propagation delay,
                                            so admin sees the preview immediately (no wait like true-live needs).
                                            Draining ('ending') and ended states also show the player so admin watches
                                            the drain window alongside viewers. */}
                                        {showAdminPreview || activeClass.status === 'ending' || activeClass.status === 'ended' || (activeClass.classType === 'prerecorded' && activeClass.status === 'live') ? (
                                            <LivePlayer playbackId={activeClass.muxPlaybackId} title={activeClass.title} state={activeClass.state} status={activeClass.status} scheduledAt={activeClass.scheduledAt} classId={activeClass.id} isAdmin={true} classType={activeClass.classType || 'live'} endingAt={activeClass.endingAt || null} />
                                        ) : (
                                            <div style={{
                                                position: 'relative', width: '100%', paddingBottom: '56.25%',
                                                background: 'linear-gradient(135deg, var(--mom-charcoal), var(--mom-charcoal-light))',
                                                borderRadius: 20, overflow: 'hidden',
                                            }}>
                                                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                                    <div style={{ width: 28, height: 28, borderRadius: '50%', border: '3px solid rgba(255,255,255,0.2)', borderTopColor: '#fff', animation: 'spin 0.8s linear infinite', marginBottom: 12 }} />
                                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>Connecting to stream...</p>
                                                </div>
                                            </div>
                                        )}
                                        {/* Presence chip row — who's watching right now (live + drain). Admin observes only. */}
                                        {(activeClass.status === 'live' || activeClass.status === 'ending') && (
                                            <ViewerPresenceRow viewers={liveViewers} onExpand={() => setPresenceSheetOpen(true)} />
                                        )}
                                    </div>
                                )}

                                {/* ── Waiting room (scheduled, viewer) ── */}
                                {activeClass && activeClass.status === 'scheduled' && !isAdmin && (
                                    <div ref={activeClassRef} style={{ marginBottom: 32 }}>
                                        <LivePlayer playbackId={activeClass.muxPlaybackId} title={activeClass.title} state="scheduled" status="scheduled" scheduledAt={activeClass.scheduledAt} classId={activeClass.id} isAdmin={false} classType={activeClass.classType || 'live'} />
                                        {/* Presence chips — also render during the waiting room so arrivals see each
                                            other gathering before the class starts. Same component, same data source. */}
                                        <ViewerPresenceRow viewers={liveViewers} onExpand={() => setPresenceSheetOpen(true)} />
                                        <div style={{ padding: '12px 0' }}>
                                            <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 20, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 4 }}>{activeClass.title}</h2>
                                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-warm-gray)', marginBottom: 10 }}>{formatClassTime(activeClass.scheduledAt)} · {activeClass.duration || 45} min</p>
                                            <button onClick={() => setActiveClass(null)}
                                                style={{ padding: '8px 20px', borderRadius: 10, fontSize: 12, fontWeight: 600, background: 'none', color: 'var(--mom-warm-gray)', border: '1px solid var(--mom-cream-dark)', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)' }}>
                                                Leave Waiting Room
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* ── Admin: Create / Schedule Class ──
                                    Form wrapper carries a ref so clicking "Edit" on any class card
                                    (which sets creating=true and editingClassId) auto-scrolls here
                                    and pulses so admin has immediate visual feedback. */}
                                {isAdmin && (
                                    <div style={{ marginBottom: 24 }}>
                                        {!creating ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                <button className="mom-btn mom-btn-primary" onClick={() => setCreating(true)}
                                                    style={{ width: '100%', padding: '14px 24px', borderRadius: 14, fontSize: 15, fontWeight: 700, fontFamily: 'var(--mom-font-sans)' }}>
                                                    + Schedule a Class
                                                </button>
                                                {/* Re-air: schedule a class that's already been recorded. Opens on the
                                                    picker step (no seed) since the admin is starting from "I need
                                                    something on this date", not from a specific video. */}
                                                <button className="mom-btn"
                                                    onClick={() => { setReAirSeed(null); setReAirOpen(true); }}
                                                    style={{
                                                        width: '100%', padding: '13px 24px', borderRadius: 14,
                                                        fontSize: 14, fontWeight: 600, fontFamily: 'var(--mom-font-sans)',
                                                        background: 'var(--mom-white)', color: 'var(--mom-charcoal)',
                                                        border: '1px solid var(--mom-cream-dark)', cursor: 'pointer',
                                                    }}>
                                                    Re-air a past class
                                                </button>
                                            </div>
                                        ) : (
                                            <div
                                                ref={createFormRef}
                                                style={{
                                                    background: 'var(--mom-white)', borderRadius: 16, padding: 20,
                                                    border: adminPulse === 'createForm'
                                                        ? '2px solid var(--mom-primary)'
                                                        : '1px solid var(--mom-cream-dark)',
                                                    boxShadow: adminPulse === 'createForm'
                                                        ? '0 0 0 4px color-mix(in srgb, var(--mom-primary) 18%, transparent), 0 8px 28px color-mix(in srgb, var(--mom-primary) 14%, transparent)'
                                                        : 'none',
                                                    transition: 'all 0.5s ease',
                                                }}>
                                                <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 18, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 16 }}>{editingClassId ? 'Edit Class' : 'Schedule a Class'}</h3>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                                    {/* Class type selector */}
                                                    <div>
                                                        <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: 'var(--mom-charcoal)', marginBottom: 6, display: 'block' }}>Class Type</label>
                                                        <div style={{ display: 'inline-flex', gap: 0, borderRadius: 10, overflow: 'hidden', border: '1.5px solid var(--mom-cream-dark)' }}>
                                                            {[
                                                                { key: 'live', label: 'Live' },
                                                                { key: 'prerecorded', label: 'Scheduled' },
                                                            ].map((opt) => (
                                                                <button key={opt.key} onClick={() => setForm({ ...form, classType: opt.key })}
                                                                    style={{
                                                                        padding: '8px 20px', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer',
                                                                        fontFamily: 'var(--mom-font-sans)',
                                                                        background: form.classType === opt.key ? 'var(--mom-charcoal)' : 'var(--mom-white)',
                                                                        color: form.classType === opt.key ? 'var(--mom-white)' : 'var(--mom-warm-gray)',
                                                                        transition: 'all 0.15s ease',
                                                                    }}>
                                                                    {opt.label}
                                                                </button>
                                                            ))}
                                                        </div>
                                                        {/* Release day vs set time — Scheduled (pre-recorded) only.
                                                            Release day is the default (owner decision 2026-10-13). */}
                                                        {form.classType === 'prerecorded' && (
                                                            <div style={{ marginTop: 10 }}>
                                                                <div style={{ display: 'inline-flex', gap: 0, borderRadius: 10, overflow: 'hidden', border: '1.5px solid var(--mom-cream-dark)' }}>
                                                                    {[
                                                                        { key: 'day', label: 'Release day' },
                                                                        { key: 'time', label: 'Set a time' },
                                                                    ].map((opt) => {
                                                                        const on = (form.releaseMode === 'time' ? 'time' : 'day') === opt.key;
                                                                        return (
                                                                            <button key={opt.key} type="button" onClick={() => setForm({ ...form, releaseMode: opt.key })}
                                                                                style={{
                                                                                    padding: '7px 16px', fontSize: 12.5, fontWeight: 600, border: 'none', cursor: 'pointer',
                                                                                    fontFamily: 'var(--mom-font-sans)',
                                                                                    background: on ? 'var(--mom-primary)' : 'var(--mom-white)',
                                                                                    color: on ? 'var(--mom-white)' : 'var(--mom-warm-gray)',
                                                                                    transition: 'all 0.15s ease',
                                                                                }}>
                                                                                {opt.label}
                                                                            </button>
                                                                        );
                                                                    })}
                                                                </div>
                                                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11.5, color: 'var(--mom-warm-gray)', marginTop: 6, marginBottom: 0 }}>
                                                                    {formIsReleaseDay
                                                                        ? 'Members get it at 12:00 AM on that day, in their own time zone. No live airing.'
                                                                        : 'Airs at one moment for everyone, with the live waiting room and reminders.'}
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <input className="mom-input" placeholder="Class title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                                                    <textarea className="mom-input" placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} style={{ resize: 'vertical' }} />
                                                    {/* ── Date + Time + Duration row ───────────────────────────────
                                                        Date stays as native <input type="date"> — same calendar
                                                        popup behavior as before. Time is split into hour / minute /
                                                        AM-PM controls so the layout reads like a clock instead of
                                                        cramming everything into a datetime-local spinner.
                                                        Source of truth (form.scheduledAt) is unchanged: the four
                                                        UI parts read/write to the same `YYYY-MM-DDTHH:MM` string. */}
                                                    {/* Release day: a date only (no time — each member gets it at her
                                                        own midnight). Shown instead of the date + time row below. */}
                                                    {formIsReleaseDay && (() => {
                                                        const todayKey = localDateKey(new Date());
                                                        const alreadyOut = !!form.releaseDate && form.releaseDate <= todayKey;
                                                        return (
                                                            <div>
                                                                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                                                    <div style={{ flex: '2 1 220px', minWidth: 0 }}>
                                                                        <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: 'var(--mom-charcoal)', marginBottom: 4, display: 'block' }}>Release day</label>
                                                                        <input className="mom-input" type="date"
                                                                            value={form.releaseDate || ''}
                                                                            onChange={(e) => setForm({ ...form, releaseDate: e.target.value })} />
                                                                    </div>
                                                                    <div style={{ flex: '1 1 120px', minWidth: 0 }}>
                                                                        <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: 'var(--mom-charcoal)', marginBottom: 4, display: 'block' }}>Duration</label>
                                                                        <select className="mom-input" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })}>
                                                                            {[5, 10, 15, 20, 30, 45, 60, 75, 90].map((d) => <option key={d} value={d}>{d} min</option>)}
                                                                        </select>
                                                                    </div>
                                                                </div>
                                                                {form.releaseDate && (
                                                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontStyle: 'italic', color: 'var(--mom-warm-gray)', marginTop: 8, marginBottom: 0 }}>
                                                                        Out {formatReleaseDate(form.releaseDate)} — first thing, in each member's own time zone.
                                                                    </p>
                                                                )}
                                                                {alreadyOut && (
                                                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-primary-dark)', marginTop: 6, marginBottom: 0 }}>
                                                                        This day has already started for some members, so they'll see it right away.
                                                                    </p>
                                                                )}
                                                            </div>
                                                        );
                                                    })()}
                                                    {!formIsReleaseDay && (() => {
                                                        const parts = parseScheduledAt(form.scheduledAt);
                                                        const updatePart = (key, value) => {
                                                            const newParts = { ...parts, [key]: value };
                                                            // First-time defaulting: when admin picks a date and the
                                                            // time hasn't been touched yet, seed time to 6:00 AM so
                                                            // form.scheduledAt becomes a complete string immediately.
                                                            // Without this, buildScheduledAt would return '' (because
                                                            // any missing part voids the whole string), silently
                                                            // dropping the date the admin just picked. Subsequent date
                                                            // changes don't re-default since hour12 is already set.
                                                            if (key === 'date' && value && !parts.hour12) {
                                                                newParts.hour12 = '6';
                                                                newParts.minute = '00';
                                                                newParts.ampm = 'AM';
                                                            }
                                                            setForm({ ...form, scheduledAt: buildScheduledAt(newParts) });
                                                        };
                                                        // Dynamic minute fallback — if the existing class's minute
                                                        // isn't on the standard 5-min grid (e.g. 6:07), surface it
                                                        // as an additional dropdown option so editing is lossless.
                                                        const minuteOptions = parts.minute && !FORM_MINUTE_OPTIONS.includes(parts.minute)
                                                            ? [...FORM_MINUTE_OPTIONS, parts.minute].sort()
                                                            : FORM_MINUTE_OPTIONS;
                                                        const timeDisabled = !parts.date;
                                                        return (
                                                            <div>
                                                                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                                                    {/* Date */}
                                                                    <div style={{ flex: '1 1 180px', minWidth: 0 }}>
                                                                        <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: 'var(--mom-charcoal)', marginBottom: 4, display: 'block' }}>Date</label>
                                                                        <input className="mom-input" type="date"
                                                                            value={parts.date}
                                                                            onChange={(e) => updatePart('date', e.target.value)} />
                                                                    </div>

                                                                    {/* Time: hour : minute  AM/PM */}
                                                                    <div style={{ flex: '2 1 280px', minWidth: 0 }}>
                                                                        <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: 'var(--mom-charcoal)', marginBottom: 4, display: 'block' }}>
                                                                            Time {timeDisabled && <span style={{ fontWeight: 500, color: 'var(--mom-warm-gray)' }}>(pick a date first)</span>}
                                                                        </label>
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                                                            <select className="mom-input"
                                                                                value={parts.hour12}
                                                                                disabled={timeDisabled}
                                                                                onChange={(e) => updatePart('hour12', e.target.value)}
                                                                                style={{ width: 76, opacity: timeDisabled ? 0.5 : 1 }}>
                                                                                <option value="">Hr</option>
                                                                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((h) => (
                                                                                    <option key={h} value={String(h)}>{h}</option>
                                                                                ))}
                                                                            </select>
                                                                            <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 16, fontWeight: 600, color: timeDisabled ? 'var(--mom-warm-gray)' : 'var(--mom-charcoal)' }}>:</span>
                                                                            <select className="mom-input"
                                                                                value={parts.minute}
                                                                                disabled={timeDisabled}
                                                                                onChange={(e) => updatePart('minute', e.target.value)}
                                                                                style={{ width: 76, opacity: timeDisabled ? 0.5 : 1 }}>
                                                                                <option value="">Min</option>
                                                                                {minuteOptions.map((m) => (
                                                                                    <option key={m} value={m}>{m}</option>
                                                                                ))}
                                                                            </select>
                                                                            <div style={{ display: 'inline-flex', borderRadius: 10, overflow: 'hidden', border: '1.5px solid var(--mom-cream-dark)', opacity: timeDisabled ? 0.5 : 1 }}>
                                                                                {['AM', 'PM'].map((opt) => (
                                                                                    <button key={opt} type="button"
                                                                                        onClick={() => !timeDisabled && updatePart('ampm', opt)}
                                                                                        disabled={timeDisabled}
                                                                                        style={{
                                                                                            padding: '8px 14px',
                                                                                            fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 700,
                                                                                            background: parts.ampm === opt ? 'var(--mom-primary)' : 'transparent',
                                                                                            color: parts.ampm === opt ? 'var(--mom-white)' : 'var(--mom-charcoal)',
                                                                                            border: 'none',
                                                                                            cursor: timeDisabled ? 'not-allowed' : 'pointer',
                                                                                            transition: 'all 0.15s ease',
                                                                                        }}>
                                                                                        {opt}
                                                                                    </button>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    {/* Duration (unchanged) */}
                                                                    <div style={{ flex: '1 1 120px', minWidth: 0 }}>
                                                                        <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: 'var(--mom-charcoal)', marginBottom: 4, display: 'block' }}>Duration</label>
                                                                        <select className="mom-input" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })}>
                                                                            {[5, 10, 15, 20, 30, 45, 60, 75, 90].map((d) => <option key={d} value={d}>{d} min</option>)}
                                                                        </select>
                                                                    </div>
                                                                </div>
                                                                {/* Live confirmation line — calm sentence-form translation
                                                                    of whatever's selected, including the viewer's local
                                                                    timezone abbreviation. Renders only when scheduledAt
                                                                    is fully assembled (all four parts set). */}
                                                                {form.scheduledAt && (
                                                                    <p style={{
                                                                        fontFamily: 'var(--mom-font-sans)', fontSize: 12,
                                                                        fontStyle: 'italic', color: 'var(--mom-warm-gray)',
                                                                        marginTop: 8, marginBottom: 0,
                                                                    }}>
                                                                        {formatScheduledAtForDisplay(form.scheduledAt)}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        );
                                                    })()}
                                                    {/* Access cell — was previously the third sibling of the flex row
                                                        I replaced above. Lifted out into its own row so the new time
                                                        picker has breathing room. Behavior is unchanged. */}
                                                    <div>
                                                        <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: 'var(--mom-charcoal)', marginBottom: 4, display: 'block' }}>Access</label>
                                                        <select className="mom-input" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}>
                                                            <option value={1}>Subscribers</option>
                                                            <option value={0}>Free</option>
                                                        </select>
                                                    </div>
                                                    {/* Cover image picker */}
                                                    <div>
                                                        <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: 'var(--mom-charcoal)', marginBottom: 4, display: 'block' }}>Cover Image (optional)</label>
                                                        {form.imageUrl ? (
                                                            <div style={{ position: 'relative', marginBottom: 8 }}>
                                                                <img src={form.imageUrl} alt="" style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 12 }} />
                                                                <button onClick={() => setForm({ ...form, imageUrl: '' })} style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: 8, padding: '4px 10px', fontSize: 11, cursor: 'pointer' }}>Remove</button>
                                                            </div>
                                                        ) : (
                                                            <button onClick={loadDriveImages} disabled={imagesLoading}
                                                                style={{ width: '100%', padding: '10px', borderRadius: 10, border: '1.5px dashed var(--mom-cream-dark)', background: 'var(--mom-cream)', color: 'var(--mom-warm-gray)', fontFamily: 'var(--mom-font-sans)', fontSize: 13, cursor: 'pointer' }}>
                                                                {imagesLoading ? 'Loading...' : 'Choose from library'}
                                                            </button>
                                                        )}
                                                        {showImagePicker && (
                                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: 8, marginTop: 8, maxHeight: 200, overflowY: 'auto' }}>
                                                                {driveImages.map((img) => (
                                                                    <img key={img.id} src={img.thumbnailUrl} alt="" onClick={() => selectImage(img)}
                                                                        style={{ width: '100%', height: 60, objectFit: 'cover', borderRadius: 8, cursor: 'pointer', border: form.imageUrl === img.imageUrl ? '2px solid var(--mom-primary)' : '2px solid transparent' }} />
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {/* Muscle group tags */}
                                                    <div>
                                                        <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: 'var(--mom-charcoal)', marginBottom: 6, display: 'block' }}>Muscle Groups (optional)</label>
                                                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                                            {[
                                                                { key: 'upper', label: 'Upper Body' },
                                                                { key: 'lower', label: 'Lower Body' },
                                                                { key: 'core', label: 'Core' },
                                                                { key: 'stretch', label: 'Stretch' },
                                                            ].map((g) => {
                                                                const selected = (form.muscleGroups || []).includes(g.key);
                                                                return (
                                                                    <button key={g.key} type="button"
                                                                        onClick={() => setForm({ ...form, muscleGroups: selected ? form.muscleGroups.filter((k) => k !== g.key) : [...(form.muscleGroups || []), g.key] })}
                                                                        style={{
                                                                            padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
                                                                            fontFamily: 'var(--mom-font-sans)',
                                                                            background: selected ? 'var(--mom-primary)' : 'var(--mom-cream-dark)',
                                                                            color: selected ? 'var(--mom-white)' : 'var(--mom-charcoal)',
                                                                            transition: 'all 0.15s ease',
                                                                        }}>
                                                                        {g.label}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: 'var(--mom-warm-gray)', marginTop: 4 }}>Auto-detected from title if not set</p>
                                                    </div>
                                                    {/* Ease In tag — marks class as beginner-friendly */}
                                                    <div>
                                                        <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: 'var(--mom-charcoal)', marginBottom: 6, display: 'block' }}>Difficulty Level (optional)</label>
                                                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                                            {/* Classic — the signature class (owner, 2026-10-13). Brand pink.
                                                                Listed first because that's its place on the Schedule:
                                                                Classic always sorts left, Strong+ always right. */}
                                                            <button type="button"
                                                                onClick={() => setForm({ ...form, classic: !form.classic })}
                                                                style={{
                                                                    padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
                                                                    fontFamily: 'var(--mom-font-sans)',
                                                                    background: form.classic ? 'var(--mom-primary)' : 'var(--mom-cream-dark)',
                                                                    color: form.classic ? 'var(--mom-white)' : 'var(--mom-charcoal)',
                                                                    transition: 'all 0.15s ease',
                                                                }}>
                                                                Classic
                                                            </button>
                                                            <button type="button"
                                                                onClick={() => setForm({ ...form, easeIn: !form.easeIn })}
                                                                style={{
                                                                    padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
                                                                    fontFamily: 'var(--mom-font-sans)',
                                                                    background: form.easeIn ? 'var(--mom-primary)' : 'var(--mom-cream-dark)',
                                                                    color: form.easeIn ? 'var(--mom-white)' : 'var(--mom-charcoal)',
                                                                    transition: 'all 0.15s ease',
                                                                }}>
                                                                Ease In
                                                            </button>
                                                            {/* Strong+ — heavier-lifting tag. Independent of Ease In by product
                                                                decision: admin may apply either, both, or neither. */}
                                                            <button type="button"
                                                                onClick={() => setForm({ ...form, strongPlus: !form.strongPlus })}
                                                                style={{
                                                                    padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
                                                                    fontFamily: 'var(--mom-font-sans)',
                                                                    background: form.strongPlus ? STRONG_PLUS_COLOR : 'var(--mom-cream-dark)',
                                                                    color: form.strongPlus ? 'var(--mom-white)' : 'var(--mom-charcoal)',
                                                                    transition: 'all 0.15s ease',
                                                                }}>
                                                                Strong+
                                                            </button>
                                                        </div>
                                                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: 'var(--mom-warm-gray)', marginTop: 4 }}>Classic is the signature class; Ease In is great for newcomers; Strong+ flags heavier lifting. Each shows a badge on the class. On a day with two classes, Classic sits on the left and Strong+ on the right.</p>
                                                    </div>
                                                    {/* Visibility allowlist (admin) — limit which users can see this class.
                                                        Off ⇒ visibleToUserIds: [] ⇒ public (normal behavior). */}
                                                    <ClassAudiencePicker
                                                        getToken={getToken}
                                                        value={form.visibleToUserIds || []}
                                                        onChange={(ids) => setForm((f) => ({ ...f, visibleToUserIds: ids }))}
                                                    />
                                                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                                        <button className="mom-btn mom-btn-primary" onClick={handleSaveClass} disabled={saving || !form.title.trim() || !formWhenReady}
                                                            style={{ flex: 1, minWidth: 140, padding: '12px', borderRadius: 12, fontSize: 14, fontWeight: 700, fontFamily: 'var(--mom-font-sans)', opacity: (saving || !form.title.trim() || !formWhenReady) ? 0.5 : 1 }}>
                                                            {saving ? 'Saving...' : editingClassId ? 'Update Class' : 'Save Class'}
                                                        </button>
                                                        {/* Save as Draft — only when creating new. Editing a draft uses
                                                            Update Class above (preserves state via merge). */}
                                                        {!editingClassId && (
                                                            <button onClick={handleSaveAsDraft} disabled={saving || !form.title.trim() || !formWhenReady}
                                                                style={{
                                                                    flex: 1, minWidth: 140, padding: '12px', borderRadius: 12, fontSize: 14, fontWeight: 700,
                                                                    fontFamily: 'var(--mom-font-sans)', cursor: 'pointer',
                                                                    background: 'none', color: 'var(--mom-primary)',
                                                                    border: '1.5px solid var(--mom-primary)',
                                                                    opacity: (saving || !form.title.trim() || !formWhenReady) ? 0.5 : 1,
                                                                }}>
                                                                {saving ? 'Saving...' : 'Save as Draft'}
                                                            </button>
                                                        )}
                                                        <button className="mom-btn" onClick={() => { setCreating(false); setEditingClassId(null); setForm({ title: '', description: '', scheduledAt: '', duration: 45, imageUrl: '', price: 1, classType: 'prerecorded', muscleGroups: [], classic: false, easeIn: false, strongPlus: false, visibleToUserIds: [], releaseMode: 'day', releaseDate: '', wasReleaseDay: false }); }}
                                                            style={{ padding: '12px 20px', borderRadius: 12, fontSize: 14, fontFamily: 'var(--mom-font-sans)' }}>
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* ══════════════════════════════════════════
                                    DRAFTS SECTION (admin-only)
                                    Hidden from every user-facing view (upcoming/past/cron all
                                    use closed allow-lists that exclude `state: 'draft'`).
                                    Lives between the create-class form and the public Schedule
                                    grid — admin's private staging area for review-before-publish.
                                ══════════════════════════════════════════ */}
                                {isAdmin && drafts.length > 0 && (
                                    <div style={{ marginBottom: 32 }}>
                                        <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 18, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 4 }}>
                                            Drafts <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--mom-warm-gray)', fontStyle: 'normal' }}>({drafts.length})</span>
                                        </h3>
                                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-warm-gray)', marginBottom: 14 }}>
                                            Hidden from everyone except you. Record / preview / edit, then Publish to put on the schedule.
                                        </p>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                            {drafts.map((cls) => {
                                                const isPrerecorded = (cls.classType || 'live') === 'prerecorded';
                                                const hasRecording = !!cls.muxPlaybackId;
                                                return (
                                                    <div key={cls.id} style={{
                                                        background: 'var(--mom-white)',
                                                        border: '1px dashed var(--mom-warm-gray)',
                                                        borderRadius: 14, padding: '14px 16px',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                        gap: 12, flexWrap: 'wrap',
                                                    }}>
                                                        <div style={{ flex: 1, minWidth: 220 }}>
                                                            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
                                                                <span style={{
                                                                    padding: '2px 10px', borderRadius: 999, fontSize: 9, fontWeight: 800,
                                                                    background: 'var(--mom-warm-gray)', color: 'var(--mom-white)',
                                                                    fontFamily: 'var(--mom-font-sans)', letterSpacing: '0.08em',
                                                                }}>DRAFT</span>
                                                                <span style={{
                                                                    padding: '2px 10px', borderRadius: 999, fontSize: 10, fontWeight: 600,
                                                                    background: 'var(--mom-cream)', color: 'var(--mom-charcoal)',
                                                                    fontFamily: 'var(--mom-font-sans)',
                                                                }}>{isPrerecorded ? 'Pre-recorded' : 'Live'}</span>
                                                                {isPrerecorded && hasRecording && (
                                                                    <span style={{
                                                                        padding: '2px 10px', borderRadius: 999, fontSize: 10, fontWeight: 700,
                                                                        background: 'color-mix(in srgb, var(--mom-success, #6b9b7a) 18%, transparent)',
                                                                        color: 'var(--mom-success, #6b9b7a)',
                                                                        fontFamily: 'var(--mom-font-sans)',
                                                                    }}>✓ Recorded</span>
                                                                )}
                                                            </div>
                                                            <h4 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 16, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 4 }}>
                                                                {cls.title || 'Untitled draft'}
                                                            </h4>
                                                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-warm-gray)' }}>
                                                                {cls.scheduledAt ? classWhenLabel(cls) : 'No scheduled time'} · {cls.duration || 45} min
                                                            </p>
                                                        </div>
                                                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                                            <button onClick={() => handleEditClass(cls)}
                                                                style={{ padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, background: 'none', color: 'var(--mom-charcoal)', border: '1px solid var(--mom-cream-dark)', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)' }}>
                                                                Edit
                                                            </button>
                                                            {isPrerecorded && !hasRecording && (
                                                                <button onClick={() => handleCreateStream(cls)}
                                                                    style={{ padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, background: 'none', color: 'var(--mom-charcoal)', border: '1px solid var(--mom-cream-dark)', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)' }}>
                                                                    Record Class
                                                                </button>
                                                            )}
                                                            {isPrerecorded && hasRecording && (
                                                                <button onClick={() => setPreviewDraft(cls)}
                                                                    style={{ padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, background: 'none', color: 'var(--mom-charcoal)', border: '1px solid var(--mom-cream-dark)', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)' }}>
                                                                    Preview
                                                                </button>
                                                            )}
                                                            {/* Watch as Viewer — mounts the real LivePlayer (same component
                                                                real viewers get) so admin can verify live-class playback
                                                                behaviors (lock, rotate-fullscreen, exit-on-end) without
                                                                publishing the class. Apples-to-apples test surface. */}
                                                            {isPrerecorded && hasRecording && (
                                                                <button onClick={() => setLivePlayerTestDraft(cls)}
                                                                    style={{ padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, background: 'none', color: 'var(--mom-charcoal)', border: '1px dashed var(--mom-warm-gray)', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)' }}>
                                                                    Watch as Viewer
                                                                </button>
                                                            )}
                                                            <button onClick={() => setPublishConfirm(cls)}
                                                                style={{ padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))', color: 'var(--mom-white)', border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)' }}>
                                                                Publish
                                                            </button>
                                                            <button onClick={() => setDeleteConfirm(cls)}
                                                                style={{ padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, background: 'none', color: 'var(--mom-warm-gray)', border: '1px solid var(--mom-cream-dark)', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)' }}>
                                                                Delete
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* ══════════════════════════════════════════
                                    EDITORIAL LAYOUT — "The Week with Danielle"
                                    Week ribbon (clickable, scrollable) + unified tile grid.
                                    All access-control logic (userCanAccessClass, isClassSoon,
                                    isSubscriber, isClassFree, isAdmin, isFriend) is PRESERVED
                                    verbatim — this is a visual reorganization only.
                                ══════════════════════════════════════════ */}

                                {/* ── Day Ribbon: 30-day clickable scroll navigator ──
                                    Shows ~7 days at once on desktop (cells are fixed-width and
                                    use justify-content: flex-start so they pack left rather than
                                    distributing across the container). Horizontal scroll reveals
                                    the rest of the month: on mobile via native swipe, on desktop
                                    via chevron arrow buttons + edge fade gradients.
                                    A vertical month abbreviation (APR, MAY…) always precedes the
                                    first day of whatever month is in view. */}
                                {upcoming.length > 0 && (
                                    // Ribbon fills the container width (same as the tiles + banner
                                    // below). Cells are sized responsively via ribbonMetrics so
                                    // exactly 7 fit across on any viewport wide enough to show
                                    // them at a readable size; narrower viewports clamp cells and
                                    // reveal the rest via swipe/chevron.
                                    <div style={{ position: 'relative', marginBottom: 24 }}>
                                        {/* Left edge fade — only when there's content hidden to the left */}
                                        {ribbonScrollState.canLeft && (
                                            <div style={{
                                                position: 'absolute', top: 0, bottom: 0, left: 0, width: 44,
                                                pointerEvents: 'none', zIndex: 2, borderTopLeftRadius: 16, borderBottomLeftRadius: 16,
                                                background: 'linear-gradient(to right, rgba(250,245,241,0.95), rgba(250,245,241,0))',
                                            }} />
                                        )}
                                        {/* Right edge fade */}
                                        {ribbonScrollState.canRight && (
                                            <div style={{
                                                position: 'absolute', top: 0, bottom: 0, right: 0, width: 44,
                                                pointerEvents: 'none', zIndex: 2, borderTopRightRadius: 16, borderBottomRightRadius: 16,
                                                background: 'linear-gradient(to left, rgba(250,245,241,0.95), rgba(250,245,241,0))',
                                            }} />
                                        )}

                                        {/* Left chevron — modern glass pill, appears only when scrollable */}
                                        {ribbonScrollState.canLeft && (
                                            <button
                                                aria-label="Scroll to earlier days"
                                                onClick={() => {
                                                    const el = ribbonScrollRef.current;
                                                    if (el) el.scrollBy({ left: -256, behavior: 'smooth' });
                                                }}
                                                style={{
                                                    position: 'absolute', top: '50%', left: 6,
                                                    transform: 'translateY(-50%)', zIndex: 3,
                                                    width: 32, height: 32, borderRadius: '50%',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    background: 'rgba(255,255,255,0.9)',
                                                    backdropFilter: 'blur(12px) saturate(1.4)',
                                                    WebkitBackdropFilter: 'blur(12px) saturate(1.4)',
                                                    border: '1px solid rgba(255,255,255,0.6)',
                                                    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                                                    color: 'var(--mom-primary)', cursor: 'pointer', padding: 0,
                                                    fontFamily: 'var(--mom-font-sans)',
                                                }}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                                            </button>
                                        )}
                                        {/* Right chevron */}
                                        {ribbonScrollState.canRight && (
                                            <button
                                                aria-label="Scroll to later days"
                                                onClick={() => {
                                                    const el = ribbonScrollRef.current;
                                                    if (el) el.scrollBy({ left: 256, behavior: 'smooth' });
                                                }}
                                                style={{
                                                    position: 'absolute', top: '50%', right: 6,
                                                    transform: 'translateY(-50%)', zIndex: 3,
                                                    width: 32, height: 32, borderRadius: '50%',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    background: 'rgba(255,255,255,0.9)',
                                                    backdropFilter: 'blur(12px) saturate(1.4)',
                                                    WebkitBackdropFilter: 'blur(12px) saturate(1.4)',
                                                    border: '1px solid rgba(255,255,255,0.6)',
                                                    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                                                    color: 'var(--mom-primary)', cursor: 'pointer', padding: 0,
                                                    fontFamily: 'var(--mom-font-sans)',
                                                }}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                                            </button>
                                        )}

                                        <div
                                            ref={ribbonScrollRef}
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'flex-start',
                                                alignItems: 'center',
                                                gap: ribbonMetrics.gap, padding: '12px 10px',
                                                borderRadius: 16,
                                                background: 'rgba(255,255,255,0.45)',
                                                backdropFilter: 'blur(16px) saturate(1.4)',
                                                WebkitBackdropFilter: 'blur(16px) saturate(1.4)',
                                                border: '1px solid rgba(255,255,255,0.55)',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                                                overflowX: 'auto', overflowY: 'hidden',
                                                WebkitOverflowScrolling: 'touch',
                                                scrollBehavior: 'smooth',
                                                // Hide scrollbar for cleaner aesthetic while keeping native swipe
                                                scrollbarWidth: 'none', msOverflowStyle: 'none',
                                            }}>
                                            {weekDays.map((day) => {
                                                const hasClasses = day.classCount > 0;
                                                const isSelected = hasClasses && selectedDay === day.key;
                                                const isHovered = hasClasses && hoveredDayKey === day.key && !isSelected;

                                                // Click toggles the filter: same day clears, different day replaces.
                                                // Days with no classes are inert so we don't mislead users.
                                                const handleDayClick = () => {
                                                    if (!hasClasses) return;
                                                    setSelectedDay((prev) => (prev === day.key ? null : day.key));
                                                };

                                                // Today is intentionally NOT distinguished in the ribbon — the leftmost
                                                // position is always today, so an extra visual marker was redundant and
                                                // a filled pink cell was reading as "has a class" even when today was
                                                // empty. The month label ("APR") before day 1 anchors the calendar.
                                                //
                                                // Only two interactive states remain:
                                                //   - selected  → inset primary ring (wins over hover)
                                                //   - hovered   → subtle primary tint background
                                                let cellBg = 'transparent';
                                                if (isHovered) {
                                                    cellBg = 'color-mix(in srgb, var(--mom-primary) 8%, transparent)';
                                                }

                                                const cellShadow = isSelected
                                                    ? 'inset 0 0 0 2px var(--mom-primary)'
                                                    : 'none';

                                                const dateColor = isSelected
                                                    ? 'var(--mom-primary)'
                                                    : hasClasses
                                                        ? 'var(--mom-charcoal)'
                                                        : 'var(--mom-warm-gray)';

                                                const dayLetterColor = isSelected
                                                    ? 'var(--mom-primary)'
                                                    : 'var(--mom-warm-gray)';
                                                return (
                                                    <React.Fragment key={day.key}>
                                                        {/* Vertical month label — present at ribbon start AND at each month boundary.
                                                            The mid-ribbon boundary variant gets a thin left divider; the start
                                                            variant doesn't (nothing to divide from). Typography scales with
                                                            cell size so the label feels proportionate on wider ribbons. */}
                                                        {day.showMonthLabel && (
                                                            <div style={{
                                                                flexGrow: 0, flexShrink: 0, flexBasis: 'auto',
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                paddingLeft: day.isMonthBoundary ? 8 : 0,
                                                                paddingRight: 4,
                                                                borderLeft: day.isMonthBoundary ? '1px solid var(--mom-cream-dark)' : 'none',
                                                                marginLeft: day.isMonthBoundary ? 2 : 0,
                                                                alignSelf: 'stretch',
                                                            }}>
                                                                <span style={{
                                                                    fontFamily: 'var(--mom-font-sans)',
                                                                    fontSize: ribbonMetrics.cellWidth > 80 ? 12 : 10,
                                                                    fontWeight: 800,
                                                                    letterSpacing: '0.18em', textTransform: 'uppercase',
                                                                    color: 'var(--mom-primary)',
                                                                    writingMode: 'vertical-rl',
                                                                    transform: 'rotate(180deg)',
                                                                    whiteSpace: 'nowrap',
                                                                }}>
                                                                    {day.date.toLocaleDateString('en-US', { month: 'short' })}
                                                                </span>
                                                            </div>
                                                        )}
                                                        <div
                                                            data-day-key={day.key}
                                                            onClick={hasClasses ? handleDayClick : undefined}
                                                            role={hasClasses ? 'button' : undefined}
                                                            tabIndex={hasClasses ? 0 : undefined}
                                                            aria-pressed={hasClasses ? isSelected : undefined}
                                                            onKeyDown={hasClasses ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleDayClick(); } } : undefined}
                                                            style={{
                                                                // Responsive cell width computed so 7 cells always fit across the
                                                                // ribbon's measured inner width. flex-grow/shrink both 0 so cells
                                                                // pack left rather than distributing via the flex container.
                                                                flexGrow: 0, flexShrink: 0,
                                                                flexBasis: ribbonMetrics.cellWidth,
                                                                width: ribbonMetrics.cellWidth,
                                                                minWidth: ribbonMetrics.cellWidth,
                                                                display: 'flex', flexDirection: 'column', alignItems: 'center',
                                                                // Padding scales gently with cell size — bigger cells get a taller
                                                                // hit area for a more tactile feel.
                                                                padding: ribbonMetrics.cellWidth > 80 ? '12px 4px' : '9px 4px',
                                                                borderRadius: ribbonMetrics.cellWidth > 80 ? 14 : 12,
                                                                background: cellBg,
                                                                boxShadow: cellShadow,
                                                                transition: 'background 0.2s ease, box-shadow 0.2s ease',
                                                                cursor: hasClasses ? 'pointer' : 'default',
                                                                userSelect: 'none',
                                                                outline: 'none',
                                                            }}
                                                            onMouseEnter={hasClasses ? () => setHoveredDayKey(day.key) : undefined}
                                                            onMouseLeave={hasClasses ? () => setHoveredDayKey((k) => (k === day.key ? null : k)) : undefined}
                                                        >
                                                            <span style={{
                                                                fontFamily: 'var(--mom-font-sans)',
                                                                fontSize: ribbonMetrics.cellWidth > 80 ? 12 : 10,
                                                                fontWeight: 700,
                                                                letterSpacing: '0.12em', textTransform: 'uppercase',
                                                                color: dayLetterColor,
                                                                marginBottom: ribbonMetrics.cellWidth > 80 ? 6 : 4,
                                                            }}>
                                                                {day.dayLetter}
                                                            </span>
                                                            <span style={{
                                                                fontFamily: 'var(--mom-font-serif)',
                                                                fontSize: ribbonMetrics.cellWidth > 80 ? 24 : 18,
                                                                fontWeight: 700,
                                                                fontStyle: 'italic',
                                                                color: dateColor,
                                                                lineHeight: 1,
                                                            }}>
                                                                {day.date.getDate()}
                                                            </span>
                                                            <span style={{
                                                                width: 5, height: 5, borderRadius: '50%',
                                                                marginTop: ribbonMetrics.cellWidth > 80 ? 8 : 6,
                                                                background: hasClasses ? 'var(--mom-primary)' : 'transparent',
                                                            }} />
                                                        </div>
                                                    </React.Fragment>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* ── Subscription Upsell Banner — UNCHANGED CONDITION ── */}
                                {siteConfig.subscriptionEnabled && !isSubscriber && !isAdmin && !isFriend && upcoming.length > 0 && (
                                    <div style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
                                        padding: '18px 28px', borderRadius: 16, marginBottom: 28,
                                        background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                                        border: 'none',
                                        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                                    }}>
                                        <div style={{ flex: 1, minWidth: 200 }}>
                                            <p style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 16, fontStyle: 'italic', color: '#fff', marginBottom: 2 }}>{siteConfig.upcomingBannerHeadline || `Unlimited classes for ${priceLabel}`}</p>
                                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>{siteConfig.upcomingBannerSubtitle || 'Every live class + full replay library, all included.'}</p>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                                            <button onClick={() => navigate('/join')}
                                                style={{ padding: '10px 22px', borderRadius: 12, fontSize: 13, fontWeight: 700, background: 'transparent', color: '#fff', border: '1.5px solid rgba(255,255,255,0.55)', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)', whiteSpace: 'nowrap' }}>
                                                {tellMeMoreCta}
                                            </button>
                                            <button onClick={() => handleClassCheckout(null, 'subscription')} disabled={purchaseLoading}
                                                style={{ padding: '10px 24px', borderRadius: 12, fontSize: 13, fontWeight: 700, background: '#fff', color: 'var(--mom-primary-dark)', border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)', opacity: purchaseLoading ? 0.5 : 1, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', whiteSpace: 'nowrap' }}>
                                                {subscribeCta}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* ── Flat Tile Grid with per-tile day labels ──
                                    One continuous grid. Each tile carries its own small day label
                                    at the top, rendered ONLY when the previous tile was a different
                                    day. This lets the grid pack naturally — a lone Sunday class +
                                    Tuesday's first class land side-by-side in the same row.

                                    Click a day in the ribbon to filter to just that day's classes;
                                    a "Show all" pill above the grid clears the filter.

                                    Spotlight ("NEXT UP") is applied to the first tile ONLY in the
                                    full chronological view — filtered views intentionally skip it
                                    since the first tile is no longer "next up", it's just "first
                                    class on this day".

                                    All access-control, admin, and reminder logic is PRESERVED
                                    verbatim — the only change is the layout wrapper. */}
                                {dayGroups.length > 0 && (() => {
                                    // Resolve the filter: if selectedDay points to a day that no
                                    // longer has classes (e.g. admin deleted them), silently fall
                                    // back to the full view rather than rendering an empty grid.
                                    const filteredGroup = selectedDay ? dayGroups.find((g) => g.key === selectedDay) : null;
                                    const isFiltered = !!filteredGroup;
                                    // Each day renders as its own section with a header above its grid
                                    // (2026-04-26 day-grouping rework). Previously a flat grid packed tiles
                                    // tightly across days with inline labels — premium for density but
                                    // confused users when a day's last tile wrapped to a new row alone with
                                    // a hidden label slot above it. Per-day sections make the calendar
                                    // rhythm explicit: today's classes live together, tomorrow's together,
                                    // etc. Filtered view collapses to a single section (the pill above is
                                    // the day context — header would be redundant).
                                    const groupsToRender = isFiltered ? [filteredGroup] : dayGroups;

                                    // Spotlight tracker persists across day sections — first eligible
                                    // tile chronologically wins, regardless of which day it falls on.
                                    // Pre-assigned when filtered so the first tile in the filtered view
                                    // stays in its regular frame.
                                    let spotlightAssigned = isFiltered;

                                    return (
                                        <div style={{ marginBottom: 32 }}>
                                            {/* "Show all" pill — appears only when a day filter is active.
                                                Acts as both a status badge ("Showing Sunday, April 19") and
                                                the clear-affordance. Premium glass styling mirrors the ribbon. */}
                                            {isFiltered && (() => {
                                                const filterLabel = getEditorialDayLabel(filteredGroup.date);
                                                return (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
                                                        <span style={{
                                                            display: 'inline-flex', alignItems: 'center', gap: 8,
                                                            padding: '7px 14px', borderRadius: 999,
                                                            background: 'color-mix(in srgb, var(--mom-primary) 10%, transparent)',
                                                            border: '1px solid color-mix(in srgb, var(--mom-primary) 25%, transparent)',
                                                            fontFamily: 'var(--mom-font-sans)', fontSize: 12,
                                                        }}>
                                                            <span style={{ fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--mom-primary)', fontSize: 10 }}>
                                                                Showing
                                                            </span>
                                                            <span style={{ fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontWeight: 700, color: 'var(--mom-charcoal)', fontSize: 13, letterSpacing: '-0.005em' }}>
                                                                {filterLabel.primary}
                                                            </span>
                                                            <span style={{ color: 'var(--mom-warm-gray)', fontWeight: 600, fontSize: 11 }}>
                                                                {filterLabel.secondary}
                                                            </span>
                                                        </span>
                                                        <button
                                                            onClick={() => setSelectedDay(null)}
                                                            style={{
                                                                display: 'inline-flex', alignItems: 'center',
                                                                padding: '7px 14px', borderRadius: 999,
                                                                background: 'var(--mom-white)',
                                                                border: '1px solid var(--mom-cream-dark)',
                                                                fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600,
                                                                color: 'var(--mom-charcoal)', cursor: 'pointer',
                                                                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                                                                transition: 'all 0.2s ease',
                                                            }}
                                                            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--mom-primary)'; e.currentTarget.style.color = 'var(--mom-primary)'; }}
                                                            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--mom-cream-dark)'; e.currentTarget.style.color = 'var(--mom-charcoal)'; }}
                                                        >
                                                            Show full calendar
                                                        </button>
                                                    </div>
                                                );
                                            })()}

                                            {groupsToRender.map((group) => {
                                                const dayLabel = isFiltered ? null : getEditorialDayLabel(group.date);
                                                // Classic always first, Strong+ always last (owner, 2026-10-13).
                                                // Stable sort => a day with no Classic/Strong+ tags keeps its
                                                // exact chronological order.
                                                const dayClasses = orderByDifficulty(group.classes);
                                                // Exactly two classes => the "pick one or double up" pair card
                                                // (ClassChoicePair). Every other day renders the grid as before.
                                                const isPair = dayClasses.length === 2;
                                                // Set inside the tile map below when the spotlight lands on a
                                                // pair tile - the eyebrow + frame then move up to the pair.
                                                // wrapDayTiles runs AFTER the map (it receives the map's
                                                // result as its argument), so it always sees the final value.
                                                let pairSpotEyebrow = '';
                                                const wrapDayTiles = (tiles) => (isPair ? (
                                                    <ClassChoicePair
                                                        spotlightEyebrow={pairSpotEyebrow}
                                                        kicker={siteConfig.classChoiceKicker || 'Two options'}
                                                        heading={siteConfig.classChoiceHeading || 'Two ways to move today. Pick one or double up.'}
                                                        isMobile={isMobile}
                                                    >
                                                        {tiles}
                                                    </ClassChoicePair>
                                                ) : (
                                                    <div className="mom-upcoming-grid">{tiles}</div>
                                                ));
                                                return (
                                            <div key={group.key} data-section-key={group.key} style={{ marginBottom: 28 }}>
                                                {/* Editorial day header — section-level, only in the unfiltered
                                                    view. When filtered, the "Showing [day]" pill above is the
                                                    explicit day context and a duplicate header would feel noisy. */}
                                                {dayLabel && (
                                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 14, paddingLeft: 2 }}>
                                                        <span style={{
                                                            fontFamily: 'var(--mom-font-serif)', fontSize: 18, fontWeight: 700, fontStyle: 'italic',
                                                            color: 'var(--mom-charcoal)', letterSpacing: '-0.01em', lineHeight: 1,
                                                        }}>
                                                            {dayLabel.primary}
                                                        </span>
                                                        <span style={{
                                                            fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 700,
                                                            letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--mom-warm-gray)',
                                                        }}>
                                                            {dayLabel.secondary}
                                                        </span>
                                                    </div>
                                                )}
                                                {wrapDayTiles(dayClasses.map((cls) => {
                                                    // NEXT UP spotlight goes only to upcoming-state classes — never to a
                                                    // class that has aired or is winding down. Without this guard the first
                                                    // chronological tile would inherit the spotlight even after airing.
                                                    const sForSpotlight = cls.state || cls.status;
                                                    // ── Release-day class (services/classRelease.js) ──
                                                    // `rel` is null for every other class, and each use below is
                                                    // gated on it, so non-release tiles evaluate exactly as before.
                                                    // Opens at the viewer's own 12:00 AM; admin can always play it.
                                                    const rel = cls._release || null;
                                                    const relVideo = rel ? resolveReleaseVideo(cls, classVideos) : null;
                                                    const relOpen = !!rel && rel.released && !!relVideo;
                                                    const relLocked = relOpen && !isAdmin && !isFriend && !isSubscriber && !isClassFree(cls.id);
                                                    const relPlayable = (relOpen && !relLocked) || (isAdmin && !!relVideo);
                                                    const relCompleted = !!relVideo && completedIds.has(relVideo.id);
                                                    const eligibleForSpotlight = rel
                                                        ? (!rel.released || (rel.isToday && !relCompleted))
                                                        : (sForSpotlight === 'scheduled' || sForSpotlight === 'waiting_room_open' || sForSpotlight === 'live');
                                                    const spotlightHit = !spotlightAssigned && eligibleForSpotlight;
                                                    if (spotlightHit) spotlightAssigned = true;
                                                    // In a pair the spotlight frame + eyebrow belong to the PAIR, so
                                                    // the tile itself renders unframed. Outside a pair this is
                                                    // exactly the previous isSpotlight.
                                                    if (spotlightHit && isPair) pairSpotEyebrow = getSpotlightEyebrow(cls);
                                                    const isSpotlight = spotlightHit && !isPair;
                                                    // Pair tiles are half-width on a phone - tighter type + padding.
                                                    const compactTile = isPair && isMobile;
                                                                const timeLabel = new Date(cls.scheduledAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                                                                // Drain window ('ending') still reads as LIVE for the ~12s it lasts —
                                                                // viewers mid-class are watching through buffered segments and the
                                                                // tile should match. Pre-recorded never carries the LIVE badge.
                                                                const sLive = cls.state || cls.status;
                                                                const isLiveNow = (sLive === 'live' || sLive === 'ending') && (cls.classType || 'live') === 'live';
                                                                const spotlightEyebrow = isSpotlight ? getSpotlightEyebrow(cls) : '';

                                                                // ── AIRED-THIS-WEEK tile state (2026-05-04, extends 2026-04-26 aired-today) ──
                                                                // A class that ended within the current week (Mon→Sun, local TZ)
                                                                // stays on Schedule until Sunday→Monday rollover with a calm "AIRED"
                                                                // treatment. Click routes to the VOD recording via VideoPlayerModal —
                                                                // NEVER to LivePlayer (the activeClass auto-clear invariant must hold).
                                                                const isAired = !rel && isAiredThisWeek(cls);
                                                                // Primary match: the recording created BY this class (its classId
                                                                // points back here). Fallback on vodAssetId for a RE-AIR — that
                                                                // class has a new id, but the recording it aired still carries the
                                                                // ORIGINAL class's classId, so the primary lookup finds nothing and
                                                                // the tile would sit on "Recording Finalizing" forever. The cron
                                                                // stamps vodAssetId on every pre-recorded class at end-of-air, so
                                                                // this resolves to the same recording members already have.
                                                                // Normal classes match on the first clause — fallback never runs.
                                                                const matchingRecording = isAired
                                                                    ? (classVideos.find((v) => v.classId === cls.id)
                                                                        || (cls.vodAssetId ? classVideos.find((v) => v.id === cls.vodAssetId) : null)
                                                                        || null)
                                                                    : null;
                                                                // The webhook writes vodReady on the source liveClasses doc when
                                                                // the recording is queryable. We require BOTH vodReady AND a
                                                                // resolved classRecordings entry — vodReady alone is true on
                                                                // the early partial asset.ready (~10s of footage); the full
                                                                // recording isn't ready until the doc materialises.
                                                                const recordingReady = isAired && cls.vodReady === true && !!matchingRecording;
                                                                const recordingFinalizing = isAired && !recordingReady;
                                                                const airedCompleted = recordingReady && completedIds.has(matchingRecording.id);
                                                                const airedLocked = isAired && recordingReady && !isAdmin && !isFriend && !isSubscriber && !isClassFree(cls.id);
                                                                const airedClickable = recordingReady && !airedLocked;
                                                                // AIRED badge label (admin-configurable copy with sane default)
                                                                const airedBadgeLabel = (siteConfig.airedTodayBadgeLabel || 'AIRED').toUpperCase();
                                                                const finalizingLabel = siteConfig.recordingFinalizingLabel || 'Class Recording Finalizing';

                                                                // ── Class detail modal click target (2026-04-26) ──
                                                                // Whole-tile click opens the class detail modal for scheduled +
                                                                // waiting-room-open tiles. Live, ending, and aired-today tiles are
                                                                // intentionally excluded — live auto-routes via loadClasses, ending
                                                                // is a 12s drain (modal info is unhelpful), aired routes to the
                                                                // recording (existing airedClickable wrapper). Non-subscribers see
                                                                // the same modal with a Subscribe primary CTA instead of Set
                                                                // reminder — info gating philosophy mirrors the locked-video modal.
                                                                const detailClickable = rel
                                                                    ? !rel.released
                                                                    : (sLive === 'scheduled' || sLive === 'waiting_room_open');
                                                                // Release-day eyebrow + corner badge. Coming up: "WEDNESDAY'S CLASS".
                                                                // Released today: "NEW TODAY". Earlier this week: "MONDAY'S CLASS".
                                                                const relDayLabel = rel ? (siteConfig.releaseDayLabelTemplate || "{day}'s class").replace('{day}', rel.weekday) : '';
                                                                const relEyebrow = rel
                                                                    ? (rel.released && rel.isToday ? (siteConfig.releaseNewTodayLabel || 'New today') : relDayLabel)
                                                                    : '';
                                                                const relDimmed = !!rel && rel.released && rel.isPastDay;

                                                                // Inner tile content (shared between spotlight and regular wrappers)
                                                                const tileInner = (
                                                                    <div style={{
                                                                        display: 'flex', flexDirection: 'column', height: '100%',
                                                                        ...(isPair ? { flex: 1 } : {}),
                                                                        background: 'var(--mom-white)', borderRadius: isSpotlight ? 14 : 16,
                                                                        overflow: 'hidden',
                                                                    }}>
                                                                        {/* Media area — image OR mini splash composition.
                                                                            Subtle opacity drop (88%) when aired-today signals
                                                                            "this happened" without graveyard energy. Title and
                                                                            actions stay full-strength. */}
                                                                        {/* Hero image priority: thumbnailUrl (admin picker selection / Mux
                                                                            auto-seeded default post-upload) → imageUrl (admin form-set
                                                                            marketing image) → branded splash. Picker selections always win
                                                                            because they're the most intentional override. To revert from a
                                                                            frame to a marketing image, edit the class to clear thumbnailUrl. */}
                                                                        <div style={{ position: 'relative', width: '100%', aspectRatio: '16/10', overflow: 'hidden', opacity: (isAired || relDimmed) ? 0.88 : 1 }}>
                                                                            {(cls.thumbnailUrl || cls.imageUrl) ? (
                                                                                <img src={cls.thumbnailUrl || cls.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} referrerPolicy="no-referrer" />
                                                                            ) : (
                                                                                // Branded fallback splash — no title here (title lives in the
                                                                                // content area below so it isn't shown twice). Composition:
                                                                                // gradient + centered "moments of movement" wordmark + tiny
                                                                                // "with Danielle" tagline underneath, duration pill anchored
                                                                                // bottom-right. Keeps the hero visually rich without competing
                                                                                // with the class title.
                                                                                <div style={{
                                                                                    width: '100%', height: '100%',
                                                                                    background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                                                                                    position: 'relative', overflow: 'hidden',
                                                                                    display: 'flex', flexDirection: 'column',
                                                                                    alignItems: 'center', justifyContent: 'center',
                                                                                    padding: '12px 14px',
                                                                                }}>
                                                                                    <span style={{
                                                                                        fontFamily: 'var(--mom-font-serif)', fontSize: 17, fontStyle: 'italic',
                                                                                        color: 'rgba(255,255,255,0.92)', fontWeight: 600,
                                                                                        letterSpacing: '-0.005em', textAlign: 'center', lineHeight: 1.1,
                                                                                    }}>
                                                                                        moments of movement
                                                                                    </span>
                                                                                    <span style={{
                                                                                        marginTop: 6,
                                                                                        fontFamily: 'var(--mom-font-sans)', fontSize: 9, fontWeight: 600,
                                                                                        letterSpacing: '0.2em', textTransform: 'uppercase',
                                                                                        color: 'rgba(255,255,255,0.55)',
                                                                                    }}>
                                                                                        with Danielle
                                                                                    </span>
                                                                                    {/* Duration pill — kept */}
                                                                                    <span style={{
                                                                                        position: 'absolute', bottom: 8, right: 8,
                                                                                        fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700,
                                                                                        letterSpacing: '0.06em', textTransform: 'uppercase',
                                                                                        color: 'rgba(255,255,255,0.95)',
                                                                                        background: 'rgba(0,0,0,0.22)', backdropFilter: 'blur(6px)',
                                                                                        padding: '3px 9px', borderRadius: 6,
                                                                                    }}>
                                                                                        {cls.duration || 45} min
                                                                                    </span>
                                                                                </div>
                                                                            )}

                                                                            {/* Corner badges — top right stack.
                                                                                Aired-today gets a checkmark when watched (parity with VideoCard
                                                                                completion overlay) and a play-circle hover affordance to
                                                                                signal "this is now a recording you can watch". */}
                                                                            <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                                                                                {isLiveNow && (
                                                                                    <span style={{
                                                                                        padding: '3px 10px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                                                                                        background: '#dc2626', color: '#fff',
                                                                                        letterSpacing: '0.08em', textTransform: 'uppercase',
                                                                                        fontFamily: 'var(--mom-font-sans)',
                                                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                                                                    }}>LIVE</span>
                                                                                )}
                                                                                {(airedCompleted || (rel && relCompleted)) && (
                                                                                    <span title="Completed" style={{
                                                                                        width: 22, height: 22, borderRadius: '50%',
                                                                                        background: 'var(--mom-success, #6b9b7a)', color: '#fff',
                                                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                                        fontSize: 13, fontWeight: 700,
                                                                                        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                                                                                    }}>✓</span>
                                                                                )}
                                                                                {cls.classic && (
                                                                                    <span style={{
                                                                                        padding: '3px 9px', borderRadius: 6, fontSize: compactTile ? 9 : 10, fontWeight: 700,
                                                                                        background: 'var(--mom-primary)', color: '#fff',
                                                                                        letterSpacing: '0.06em', textTransform: 'uppercase',
                                                                                        fontFamily: 'var(--mom-font-sans)',
                                                                                        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                                                                                    }}>CLASSIC</span>
                                                                                )}
                                                                                {cls.easeIn && (
                                                                                    <span style={{
                                                                                        padding: '3px 9px', borderRadius: 6, fontSize: compactTile ? 9 : 10, fontWeight: 700,
                                                                                        background: 'var(--mom-success, #6b9b7a)', color: '#fff',
                                                                                        letterSpacing: '0.06em', textTransform: 'uppercase',
                                                                                        fontFamily: 'var(--mom-font-sans)',
                                                                                        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                                                                                    }}>EASE IN</span>
                                                                                )}
                                                                                {cls.strongPlus && (
                                                                                    <span style={{
                                                                                        padding: '3px 9px', borderRadius: 6, fontSize: compactTile ? 9 : 10, fontWeight: 700,
                                                                                        background: STRONG_PLUS_COLOR, color: '#fff',
                                                                                        letterSpacing: '0.06em', textTransform: 'uppercase',
                                                                                        fontFamily: 'var(--mom-font-sans)',
                                                                                        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                                                                                    }}>STRONG+</span>
                                                                                )}
                                                                            </div>

                                                                            {/* Top-left badge slot — spotlight (NEXT UP) OR aired badge.
                                                                                Mutually exclusive: aired-today never gets the spotlight, so
                                                                                they cleanly share this position. */}
                                                                            {isSpotlight && spotlightEyebrow && (
                                                                                <div style={{
                                                                                    position: 'absolute', top: 8, left: 8,
                                                                                    padding: '4px 10px', borderRadius: 6,
                                                                                    background: 'rgba(255,255,255,0.92)',
                                                                                    backdropFilter: 'blur(8px)',
                                                                                    fontFamily: 'var(--mom-font-sans)', fontSize: 9, fontWeight: 700,
                                                                                    letterSpacing: '0.1em', textTransform: 'uppercase',
                                                                                    color: 'var(--mom-primary-dark)',
                                                                                    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                                                                                }}>
                                                                                    {spotlightEyebrow}
                                                                                </div>
                                                                            )}
                                                                            {isAired && (
                                                                                <div style={{
                                                                                    position: 'absolute', top: 8, left: 8,
                                                                                    padding: '4px 10px', borderRadius: 6,
                                                                                    background: 'rgba(255,255,255,0.94)',
                                                                                    backdropFilter: 'blur(8px)',
                                                                                    fontFamily: 'var(--mom-font-sans)', fontSize: 9, fontWeight: 700,
                                                                                    letterSpacing: '0.1em', textTransform: 'uppercase',
                                                                                    color: 'var(--mom-charcoal)',
                                                                                    boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                                                                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                                                                }}>
                                                                                    {airedClickable && (
                                                                                        <span style={{
                                                                                            width: 0, height: 0,
                                                                                            borderLeft: '7px solid var(--mom-primary)',
                                                                                            borderTop: '4px solid transparent',
                                                                                            borderBottom: '4px solid transparent',
                                                                                            display: 'inline-block',
                                                                                        }} aria-hidden="true" />
                                                                                    )}
                                                                                    <span>{airedBadgeLabel} {timeLabel}</span>
                                                                                </div>
                                                                            )}
                                                                            {/* Release-day class that's out for this viewer (and not the
                                                                                spotlight, which carries its own eyebrow in this slot). */}
                                                                            {rel && rel.released && !isSpotlight && (
                                                                                <div style={{
                                                                                    position: 'absolute', top: 8, left: 8,
                                                                                    padding: '4px 10px', borderRadius: 6,
                                                                                    background: 'rgba(255,255,255,0.94)',
                                                                                    backdropFilter: 'blur(8px)',
                                                                                    fontFamily: 'var(--mom-font-sans)', fontSize: 9, fontWeight: 700,
                                                                                    letterSpacing: '0.1em', textTransform: 'uppercase',
                                                                                    color: rel.isToday ? 'var(--mom-primary-dark)' : 'var(--mom-charcoal)',
                                                                                    boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                                                                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                                                                }}>
                                                                                    {relPlayable && (
                                                                                        <span style={{
                                                                                            width: 0, height: 0,
                                                                                            borderLeft: '7px solid var(--mom-primary)',
                                                                                            borderTop: '4px solid transparent',
                                                                                            borderBottom: '4px solid transparent',
                                                                                            display: 'inline-block',
                                                                                        }} aria-hidden="true" />
                                                                                    )}
                                                                                    <span>{relEyebrow}</span>
                                                                                </div>
                                                                            )}
                                                                        </div>

                                                                        {/* Content area */}
                                                                        <div style={{ padding: compactTile ? '10px 10px 11px' : '12px 14px 14px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                                                                            {/* Time eyebrow — primary color for upcoming, warm-gray for aired
                                                                                so the eye doesn't read "this is starting soon" on a class
                                                                                that already happened. The AIRED badge in the media area is
                                                                                the dominant time signifier for that state. */}
                                                                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: compactTile ? 10 : 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: (isAired || relDimmed) ? 'var(--mom-warm-gray)' : 'var(--mom-primary)', marginBottom: 4 }}>
                                                                                {rel ? relEyebrow : timeLabel}
                                                                                {cls.duration ? <span style={{ color: 'var(--mom-warm-gray)', fontWeight: 600, marginLeft: 6 }}>· {cls.duration} min</span> : null}
                                                                            </p>
                                                                            {/* Title */}
                                                                            <p style={{
                                                                                fontFamily: 'var(--mom-font-serif)', fontSize: compactTile ? 14 : 16, fontStyle: 'italic', fontWeight: 600,
                                                                                color: 'var(--mom-charcoal)', marginBottom: compactTile ? 8 : 10, lineHeight: 1.25,
                                                                                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                                                            }}>{cls.title}</p>
                                                                            {/* Release-day class that's out: who has taken it (ClassTogether.js). */}
                                                                            {rel && rel.released && userId && relVideo && (
                                                                                <TakenOnTile classId={cls.id} getToken={getToken} copy={siteConfig} />
                                                                            )}

                                                                            {/* Viewer actions — LOGIC PRESERVED VERBATIM.
                                                                                onClick stopPropagation: when the parent tile is clickable
                                                                                (scheduled/waiting-room → opens detail modal), bare button
                                                                                clicks would otherwise bubble up. Stopping propagation here
                                                                                keeps Join / Subscribe / Set reminder as direct one-tap
                                                                                actions independent of the modal-on-tile-click. */}
                                                                            {!isAdmin && !rel && cls.status === 'scheduled' && (
                                                                                <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 'auto' }}>
                                                                                    {isClassSoon(cls.scheduledAt) && activeClass?.id !== cls.id && (
                                                                                        userCanAccessClass(cls.id) ? (
                                                                                            <button onClick={() => setActiveClass(cls)}
                                                                                                style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, background: 'var(--mom-primary)', color: 'var(--mom-white)', border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)' }}>
                                                                                                Join Waiting Room
                                                                                            </button>
                                                                                        ) : !isSubscriber ? (
                                                                                            <button onClick={() => handleClassCheckout(cls, 'subscription')} disabled={purchaseLoading}
                                                                                                style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))', color: 'var(--mom-white)', border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)', opacity: purchaseLoading ? 0.5 : 1, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                                                                                                {purchaseLoading ? 'Redirecting...' : `${priceLabel} · ${subscribeCta}`}
                                                                                            </button>
                                                                                        ) : null
                                                                                    )}
                                                                                    {!isClassSoon(cls.scheduledAt) && !userCanAccessClass(cls.id) && !isSubscriber && (
                                                                                        <button onClick={() => handleClassCheckout(cls, 'subscription')} disabled={purchaseLoading}
                                                                                            style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))', color: 'var(--mom-white)', border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)', opacity: purchaseLoading ? 0.5 : 1, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                                                                                            {purchaseLoading ? 'Redirecting...' : `${priceLabel} Unlimited`}
                                                                                        </button>
                                                                                    )}
                                                                                    {userCanAccessClass(cls.id) && !isClassSoon(cls.scheduledAt) && (
                                                                                        <span style={{ padding: '6px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: 'color-mix(in srgb, var(--mom-primary) 10%, transparent)', color: 'var(--mom-primary)', fontFamily: 'var(--mom-font-sans)' }}>
                                                                                            {isClassFree(cls.id) ? 'Free class' : isSubscriber ? 'Included' : 'Purchased ✓'}
                                                                                        </span>
                                                                                    )}
                                                                                    <button onClick={() => handleToggleReminder(cls)}
                                                                                        style={{
                                                                                            padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)',
                                                                                            background: userReminders.has(cls.id) ? 'color-mix(in srgb, var(--mom-primary) 12%, transparent)' : 'var(--mom-cream)',
                                                                                            color: userReminders.has(cls.id) ? 'var(--mom-primary)' : 'var(--mom-warm-gray)',
                                                                                        }}>
                                                                                        {userReminders.has(cls.id) ? 'Reminder set ✓' : 'Set reminder'}
                                                                                    </button>
                                                                                </div>
                                                                            )}
                                                                            {/* Concurrent live classes — explicit join.
                                                                                A live tile normally carries no join CTA because the single
                                                                                live class auto-activates the viewer into it. When 2+ are
                                                                                live at once we deliberately DON'T auto-activate (picking one
                                                                                arbitrarily is wrong), so each live class the viewer isn't
                                                                                currently in needs a way in. The schedule list renders below
                                                                                the player, so this doubles as the switch affordance.
                                                                                Gated on liveVisibleCount > 1 ⇒ with 0 or 1 live classes this
                                                                                renders nothing and the tile is byte-identical to before. */}
                                                                            {!isAdmin && cls.status === 'live' && liveVisibleCount > 1
                                                                                && activeClass?.id !== cls.id && userCanAccessClass(cls.id) && (
                                                                                <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 'auto' }}>
                                                                                    <button onClick={() => setActiveClass(cls)}
                                                                                        style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, background: 'var(--mom-primary)', color: 'var(--mom-white)', border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)' }}>
                                                                                        {siteConfig.liveJoinClassLabel || 'Join this class'}
                                                                                    </button>
                                                                                </div>
                                                                            )}
                                                                            {!isAdmin && !rel && userCanAccessClass(cls.id) && !isClassSoon(cls.scheduledAt) && cls.scheduledAt && !isAired && (
                                                                                <p style={{ fontSize: 11, color: 'var(--mom-warm-gray)', fontFamily: 'var(--mom-font-sans)', marginTop: 6, marginBottom: 0 }}>
                                                                                    Waiting room opens at {new Date(new Date(cls.scheduledAt).getTime() - 15 * 60 * 1000).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                                                                </p>
                                                                            )}

                                                                            {/* Release-day viewer actions. Before her midnight: what she has
                                                                                (Included / Free) or Subscribe. After: Let's move (opens the
                                                                                player), Subscribe if locked, or a calm "Coming soon" if the
                                                                                video isn't uploaded yet. No waiting room, no per-class reminder. */}
                                                                            {!isAdmin && rel && (
                                                                                <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 'auto', alignItems: 'center' }}>
                                                                                    {!rel.released && !userCanAccessClass(cls.id) && !isSubscriber && (
                                                                                        <button onClick={() => handleClassCheckout(cls, 'subscription')} disabled={purchaseLoading}
                                                                                            style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))', color: 'var(--mom-white)', border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)', opacity: purchaseLoading ? 0.5 : 1, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                                                                                            {purchaseLoading ? 'Redirecting...' : `${priceLabel} Unlimited`}
                                                                                        </button>
                                                                                    )}
                                                                                    {!rel.released && userCanAccessClass(cls.id) && (
                                                                                        <span style={{ padding: '6px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: 'color-mix(in srgb, var(--mom-primary) 10%, transparent)', color: 'var(--mom-primary)', fontFamily: 'var(--mom-font-sans)' }}>
                                                                                            {isClassFree(cls.id) ? 'Free class' : isSubscriber ? 'Included' : 'Purchased ✓'}
                                                                                        </span>
                                                                                    )}
                                                                                    {rel.released && relLocked && (
                                                                                        <button onClick={() => handleClassCheckout(cls, 'subscription')} disabled={purchaseLoading}
                                                                                            style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))', color: 'var(--mom-white)', border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)', opacity: purchaseLoading ? 0.5 : 1, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                                                                                            {purchaseLoading ? 'Redirecting...' : `${priceLabel} · ${subscribeCta}`}
                                                                                        </button>
                                                                                    )}
                                                                                    {rel.released && relOpen && !relLocked && (
                                                                                        <button onClick={() => setActiveVideo(relVideo)}
                                                                                            style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, background: 'var(--mom-primary)', color: 'var(--mom-white)', border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)' }}>
                                                                                            {siteConfig.catchUpCtaLabel || "Let's move"}
                                                                                        </button>
                                                                                    )}
                                                                                    {rel.released && !relVideo && (
                                                                                        <span style={{ padding: '6px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: 'var(--mom-cream)', color: 'var(--mom-warm-gray)', fontFamily: 'var(--mom-font-sans)' }}>
                                                                                            {siteConfig.releaseComingSoonLabel || 'Coming soon'}
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                            {!isAdmin && rel && !rel.released && userCanAccessClass(cls.id) && (
                                                                                <p style={{ fontSize: 11, color: 'var(--mom-warm-gray)', fontFamily: 'var(--mom-font-sans)', marginTop: 6, marginBottom: 0 }}>
                                                                                    {siteConfig.releaseReadyLine || 'Ready at 12:00 AM, your time'}
                                                                                </p>
                                                                            )}

                                                                            {/* Aired-today viewer actions (2026-04-26).
                                                                                Three states:
                                                                                  • Recording finalizing — Mux still processing the VOD.
                                                                                    No CTA, just a calm subtitle + spinner. Tile inert.
                                                                                  • Locked — non-subscriber on a paid class. Subscribe CTA,
                                                                                    matches scheduled-tile pattern.
                                                                                  • Watchable — primary CTA "Watch recording" opens the
                                                                                    VideoPlayerModal with the matching classRecording.
                                                                                Whole-tile click is wired separately on the tileCard wrapper
                                                                                — these inline buttons are the explicit affordance. */}
                                                                            {!isAdmin && isAired && (
                                                                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 'auto', alignItems: 'center' }}>
                                                                                    {recordingFinalizing && (
                                                                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: 'var(--mom-cream)', color: 'var(--mom-warm-gray)', fontFamily: 'var(--mom-font-sans)' }}>
                                                                                            <span style={{ width: 10, height: 10, borderRadius: '50%', border: '2px solid var(--mom-cream-dark)', borderTopColor: 'var(--mom-primary)', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
                                                                                            {finalizingLabel}
                                                                                        </span>
                                                                                    )}
                                                                                    {recordingReady && airedLocked && (
                                                                                        <button onClick={(e) => { e.stopPropagation(); handleClassCheckout(cls, 'subscription'); }} disabled={purchaseLoading}
                                                                                            style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))', color: 'var(--mom-white)', border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)', opacity: purchaseLoading ? 0.5 : 1, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                                                                                            {purchaseLoading ? 'Redirecting...' : `${priceLabel} · ${subscribeCta}`}
                                                                                        </button>
                                                                                    )}
                                                                                    {recordingReady && !airedLocked && (
                                                                                        <button onClick={(e) => { e.stopPropagation(); setActiveVideo(matchingRecording); }}
                                                                                            style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, background: 'var(--mom-primary)', color: 'var(--mom-white)', border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)' }}>
                                                                                            {siteConfig.catchUpCtaLabel || "Let's move"}
                                                                                        </button>
                                                                                    )}
                                                                                </div>
                                                                            )}

                                                                            {/* Admin actions — LOGIC PRESERVED VERBATIM.
                                                                                onClick stopPropagation: when the parent tileCard is clickable
                                                                                (aired-today + admin), bare button clicks would otherwise bubble
                                                                                up and ALSO trigger setActiveVideo. Stopping propagation here
                                                                                keeps Delete / Thumbnail interaction local to its button. */}
                                                                            {isAdmin && (
                                                                                <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', gap: 6, marginTop: 'auto', flexWrap: 'wrap', alignItems: 'center' }}>
                                                                                    {(cls.classType || 'live') === 'live' && cls.status === 'scheduled' && !cls.muxStreamKey && (
                                                                                        <button onClick={() => handleCreateStream(cls)} disabled={streamLoading}
                                                                                            style={{ padding: '5px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: 'var(--mom-charcoal)', color: 'var(--mom-white)', border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)', opacity: streamLoading ? 0.5 : 1 }}>
                                                                                            {streamLoading ? 'Preparing...' : 'Prepare Stream'}
                                                                                        </button>
                                                                                    )}
                                                                                    {(cls.classType || 'live') === 'live' && cls.muxStreamKey && cls.status === 'scheduled' && !broadcastingClassId && (
                                                                                        <button onClick={() => setBroadcastingClassId(cls.id)}
                                                                                            style={{ padding: '5px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: '#dc2626', color: '#fff', border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)' }}>
                                                                                            Open Broadcaster
                                                                                        </button>
                                                                                    )}
                                                                                    {(cls.classType || 'live') === 'live' && cls.status === 'live' && (
                                                                                        <button onClick={() => setEndClassConfirm(cls)}
                                                                                            style={{ padding: '6px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700, background: '#dc2626', color: '#fff', border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)', letterSpacing: '0.02em' }}>
                                                                                            End Class
                                                                                        </button>
                                                                                    )}
                                                                                    {/* Pre-recorded single-button gate (2026-04-21 refactor) — collapsed the
                                                                                        two-step "Record Class" → "Open Recorder" flow into one button. Removed
                                                                                        the `!cls.muxStreamKey` / `cls.muxStreamKey` conditions since pre-recorded
                                                                                        no longer creates a Mux Live Stream (handleCreateStream pre-recorded fork
                                                                                        skips the API call). Legacy pre-recorded classes that carry muxStreamKey
                                                                                        from before the refactor ALSO satisfy this gate (the condition no longer
                                                                                        checks muxStreamKey either way), so legacy behavior is preserved. */}
                                                                                    {cls.classType === 'prerecorded' && cls.status === 'scheduled' && !broadcastingClassId && !cls.muxAssetId && cls.uploadStatus !== 'processing' && cls.uploadStatus !== 'uploading' && (
                                                                                        <button onClick={() => handleCreateStream(cls)}
                                                                                            style={{ padding: '5px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: '#dc2626', color: '#fff', border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)' }}>
                                                                                            Record Class
                                                                                        </button>
                                                                                    )}
                                                                                    {cls.classType === 'prerecorded' && cls.uploadStatus === 'uploading' && !cls.muxAssetId && (
                                                                                        <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 600, color: 'var(--mom-warm-gray)', display: 'flex', alignItems: 'center', gap: 4 }}>
                                                                                            <div style={{ width: 10, height: 10, borderRadius: '50%', border: '2px solid var(--mom-cream-dark)', borderTopColor: 'var(--mom-primary)', animation: 'spin 0.8s linear infinite' }} />
                                                                                            Uploading...
                                                                                        </span>
                                                                                    )}
                                                                                    {cls.classType === 'prerecorded' && cls.uploadStatus === 'processing' && !cls.muxAssetId && (
                                                                                        <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 600, color: 'var(--mom-warm-gray)', display: 'flex', alignItems: 'center', gap: 4 }}>
                                                                                            <div style={{ width: 10, height: 10, borderRadius: '50%', border: '2px solid var(--mom-cream-dark)', borderTopColor: 'var(--mom-primary)', animation: 'spin 0.8s linear infinite' }} />
                                                                                            Processing...
                                                                                        </span>
                                                                                    )}
                                                                                    {cls.classType === 'prerecorded' && (cls.muxAssetId || cls.uploadStatus === 'ready') && (
                                                                                        <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 600, color: 'var(--mom-primary)' }}>Video ready ✓</span>
                                                                                    )}
                                                                                    {/* Release-day: admin can play it any time, before its date
                                                                                        included (owner decision 2026-10-13). Members can't. */}
                                                                                    {rel && relVideo && (
                                                                                        <button onClick={() => setActiveVideo(relVideo)}
                                                                                            style={{ padding: '5px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: 'var(--mom-primary)', color: 'var(--mom-white)', border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)' }}>
                                                                                            {rel.released ? 'Play' : 'Preview'}
                                                                                        </button>
                                                                                    )}
                                                                                    {rel && (
                                                                                        <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 600, color: 'var(--mom-warm-gray)' }}>
                                                                                            Release day · {formatReleaseDate(rel.date, { weekday: 'short', month: 'short', day: 'numeric' })}
                                                                                        </span>
                                                                                    )}
                                                                                    {cls.muxPlaybackId && (
                                                                                        <button onClick={() => openThumbnailPicker(cls, false)}
                                                                                            style={{ padding: '5px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: 'none', color: 'var(--mom-warm-gray)', border: '1px solid var(--mom-cream-dark)', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)' }}>
                                                                                            Thumbnail
                                                                                        </button>
                                                                                    )}
                                                                                    {cls.status === 'scheduled' && (
                                                                                        <button onClick={() => handleEditClass(cls)}
                                                                                            style={{ padding: '5px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: 'none', color: 'var(--mom-warm-gray)', border: '1px solid var(--mom-cream-dark)', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)' }}>
                                                                                            Edit
                                                                                        </button>
                                                                                    )}
                                                                                    <button onClick={() => setDeleteConfirm(cls)}
                                                                                        style={{ padding: '5px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: 'none', color: 'var(--mom-warm-gray)', border: '1px solid var(--mom-cream-dark)', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)' }}>
                                                                                        Delete
                                                                                    </button>
                                                                                    {(cls.classType || 'live') === 'live' && cls.muxStreamKey && (
                                                                                        <button onClick={() => setShowObs(!showObs)}
                                                                                            style={{ padding: '5px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: 'none', color: 'var(--mom-warm-gray)', border: '1px solid var(--mom-cream-dark)', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)' }}>
                                                                                            {showObs ? 'Hide OBS' : 'OBS'}
                                                                                        </button>
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                            {isAdmin && showObs && (cls.classType || 'live') === 'live' && cls.muxStreamKey && (
                                                                                <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 8, padding: 10, background: 'var(--mom-cream)', borderRadius: 8, fontSize: 11, fontFamily: 'var(--mom-font-sans)', color: 'var(--mom-warm-gray)' }}>
                                                                                    <p style={{ marginBottom: 4 }}><strong>Server:</strong> {cls.rtmpUrl || 'rtmp://global-live.mux.com:5222/app'}</p>
                                                                                    <p><strong>Key:</strong> {cls.muxStreamKey}
                                                                                        <button onClick={() => { navigator.clipboard.writeText(cls.muxStreamKey); setCopied(cls.id); setTimeout(() => setCopied(''), 2000); }}
                                                                                            style={{ marginLeft: 6, padding: '2px 8px', borderRadius: 4, fontSize: 10, border: '1px solid var(--mom-cream-dark)', background: 'var(--mom-white)', cursor: 'pointer' }}>
                                                                                            {copied === cls.id ? 'Copied!' : 'Copy'}
                                                                                        </button>
                                                                                    </p>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                );

                                                    // Tile click target priority:
                                                    //   1. Aired-today + recording ready → setActiveVideo (opens recording modal)
                                                    //   2. Scheduled / waiting-room-open → setClassDetailModal (info modal)
                                                    //   3. Otherwise inert (live auto-routes; ending is brief drain)
                                                    // Inline buttons (Subscribe, Set reminder, Join Waiting Room, admin Delete, etc.)
                                                    // use e.stopPropagation so they remain one-tap fast paths independent of
                                                    // whole-tile click. Routes never call setActiveClass (LivePlayer remount path)
                                                    // — the activeClass auto-clear invariant must hold.
                                                    // Release-day: playable (her midnight has passed, or admin) → the
                                                    // player; not out yet → the detail modal; out but locked or not
                                                    // uploaded → inert (the inline Subscribe / Coming soon explains).
                                                    const onTileClick = rel
                                                        ? (relPlayable ? (() => setActiveVideo(relVideo)) : detailClickable ? (() => setClassDetailModal(cls)) : null)
                                                        : airedClickable ? (() => setActiveVideo(matchingRecording))
                                                        : detailClickable ? (() => setClassDetailModal(cls))
                                                        : null;
                                                    const isTileClickable = !!onTileClick;
                                                    const wrapperBaseStyle = {
                                                        borderRadius: 16,
                                                        border: '1px solid var(--mom-cream-dark)',
                                                        boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                                                        background: 'var(--mom-white)',
                                                        overflow: 'hidden',
                                                        // Pair: stretch so the two tiles end level.
                                                        ...(isPair ? { flex: 1, display: 'flex', flexDirection: 'column' } : {}),
                                                    };
                                                    // Build the inner card once — wrapped in a clickable shell when applicable.
                                                    // Spotlight gets a gradient outer frame regardless of clickability.
                                                    // Hover lift / shadow on the inner is suppressed inside spotlight: a 2px
                                                    // translateY would expose uneven padding inside the gradient frame, and the
                                                    // gradient already provides plenty of visual emphasis on its own.
                                                    const innerCard = isTileClickable ? (
                                                        <div
                                                            role="button"
                                                            tabIndex={0}
                                                            onClick={onTileClick}
                                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTileClick(); } }}
                                                            style={{ ...wrapperBaseStyle, cursor: 'pointer', transition: isSpotlight ? 'none' : 'transform 0.18s ease, box-shadow 0.18s ease' }}
                                                            onMouseEnter={isSpotlight ? undefined : (e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.08)'; }}
                                                            onMouseLeave={isSpotlight ? undefined : (e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.03)'; }}
                                                        >
                                                            {tileInner}
                                                        </div>
                                                    ) : (
                                                        <div style={wrapperBaseStyle}>
                                                            {tileInner}
                                                        </div>
                                                    );
                                                    const tileCard = isSpotlight ? (
                                                        <div style={{
                                                            padding: 2, borderRadius: 16,
                                                            background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                                                            boxShadow: '0 8px 28px color-mix(in srgb, var(--mom-primary) 22%, transparent)',
                                                        }}>
                                                            {innerCard}
                                                        </div>
                                                    ) : innerCard;

                                                    // Each grid cell wraps an optional day label + the tile. The label
                                                    // appears ONLY when this tile opens a new day — lets a lone Sunday
                                                    // tile sit next to Tuesday's first tile in the same row, each
                                                    // carrying its own heading, preserving editorial rhythm without
                                                    // enforcing row breaks.
                                                    // Day labels live at the section header above this grid — tiles
                                                    // are flat within their day's grid. The previous inline-label /
                                                    // hidden-spacer trick is gone since each day has its own grid now.
                                                    return (
                                                        <div key={cls.id} style={isPair ? { display: 'flex', flexDirection: 'column', minWidth: 0 } : undefined}>
                                                            {tileCard}
                                                        </div>
                                                    );
                                                }))}
                                            </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })()}

                                {/* ── Past Classes ── */}
                                {/* Admin: show past classes with delete buttons for cleanup */}
                                {isAdmin && past.length > 0 && (
                                    <div>
                                        <p style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 18, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 12 }}>Past classes</p>
                                        {past.slice(0, 5).map((cls) => (
                                            <div key={cls.id} style={{
                                                display: 'flex', alignItems: 'center', gap: 14,
                                                padding: '12px 16px', borderRadius: 14,
                                                background: 'var(--mom-white)', marginBottom: 8,
                                                border: '1px solid var(--mom-cream-dark)', opacity: 0.6,
                                            }}>
                                                <ClassThumb cls={cls} size={44} />
                                                <div style={{ flex: 1 }}>
                                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: 'var(--mom-charcoal)' }}>{cls.title}</p>
                                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: 'var(--mom-warm-gray)' }}>{classWhenLabel(cls)}</p>
                                                </div>
                                                <button onClick={() => setMetricsClassId(cls.id)}
                                                    style={{ padding: '4px 10px', borderRadius: 6, fontSize: 10, background: 'none', color: 'var(--mom-charcoal)', border: '1px solid var(--mom-cream-dark)', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)' }}>
                                                    Metrics
                                                </button>
                                                <button onClick={() => setDeleteConfirm(cls)}
                                                    style={{ padding: '4px 10px', borderRadius: 6, fontSize: 10, background: 'none', color: 'var(--mom-warm-gray)', border: '1px solid var(--mom-cream-dark)', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)' }}>
                                                    Delete
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {/* Non-admin: link to older recordings on On Demand.
                                    `past` already excludes this-week's aired (handled inline now), so this
                                    CTA only surfaces when older-than-this-week recordings exist — pointing
                                    the user to the back catalog without competing with this week's inline
                                    aired tiles. Filter by `state === 'ended'` so a lone cancelled class
                                    doesn't trigger a CTA labelled "Let's move" (cancelled classes have no
                                    recording). Boundary widened 2026-05-04 alongside the upcoming filter. */}
                                {!isAdmin && past.some((c) => (c.state || c.status) === 'ended') && (
                                    <button onClick={() => { setActiveTab('classes'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                                        style={{
                                            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            padding: '18px 24px', borderRadius: 16, border: 'none', cursor: 'pointer',
                                            background: 'linear-gradient(135deg, rgba(195,55,100,0.08), rgba(195,55,100,0.03))',
                                            transition: 'all 0.2s',
                                        }}>
                                        <div style={{ textAlign: 'left' }}>
                                            <p style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 16, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 2 }}>Looking for older classes?</p>
                                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-charcoal)' }}>Browse the full library On Demand</p>
                                        </div>
                                        <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 20, color: 'var(--mom-primary)', fontWeight: 300 }}>→</span>
                                    </button>
                                )}

                                {!loading && upcoming.length === 0 && past.length === 0 && !isAdmin && (
                                    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                                        <p style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 20, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 8 }}>No classes scheduled yet</p>
                                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: 'var(--mom-warm-gray)' }}>Check back soon — new classes are added regularly!</p>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* ══════════════════════════════════════════
                    CLASSES / SHORTS TABS
                ══════════════════════════════════════════ */}
                {isOnDemandTab && (
                    <div>
                        {(!onDemandLoaded || onDemandLoading) ? (
                            <div style={{ textAlign: 'center', padding: 60 }}>
                                <div style={{ width: 28, height: 28, borderRadius: '50%', border: '3px solid var(--mom-cream-dark)', borderTopColor: 'var(--mom-primary)', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-warm-gray)' }}>Loading workouts...</p>
                            </div>
                        ) : activeOdVideos.length === 0 ? (
                            <div className="mom-card" style={{ padding: '60px 32px', textAlign: 'center', borderRadius: 20 }}>
                                <p style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 8, position: 'relative', zIndex: 2 }}>
                                    {activeTab === 'classes' ? (siteConfig.noRecordingsMessage || 'No class recordings yet') : (siteConfig.noShortsMessage || 'No shorts yet')}
                                </p>
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, color: 'var(--mom-warm-gray)', position: 'relative', zIndex: 2 }}>{siteConfig.emptyWorkoutsSubtitle || 'Check back soon — new workouts are on the way!'}</p>
                            </div>
                        ) : (
                            <>
                                {/* Layer 1: Subscription banner for non-subscribers on On Demand */}
                                {siteConfig.subscriptionEnabled && !isSubscriber && !isAdmin && !isFriend && activeTab === 'classes' && (
                                    <div style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
                                        padding: '18px 28px', borderRadius: 16, marginBottom: 20,
                                        background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                                        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                                    }}>
                                        <div style={{ flex: 1, minWidth: 200 }}>
                                            <p style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 16, fontStyle: 'italic', color: '#fff', marginBottom: 2 }}>{siteConfig.onDemandBannerHeadline || 'Unlock the full library'}</p>
                                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>{siteConfig.onDemandBannerSubtitle || `Every class recording + all scheduled classes for ${priceLabel}`}</p>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                                            <button onClick={() => navigate('/join')}
                                                style={{ padding: '10px 22px', borderRadius: 12, fontSize: 13, fontWeight: 700, background: 'transparent', color: '#fff', border: '1.5px solid rgba(255,255,255,0.55)', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)', whiteSpace: 'nowrap' }}>
                                                {tellMeMoreCta}
                                            </button>
                                            <button onClick={() => handleClassCheckout(null, 'subscription')} disabled={purchaseLoading}
                                                style={{ padding: '10px 24px', borderRadius: 12, fontSize: 13, fontWeight: 700, background: '#fff', color: 'var(--mom-primary-dark)', border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)', opacity: purchaseLoading ? 0.5 : 1, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', whiteSpace: 'nowrap' }}>
                                                {subscribeCta}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <FilterBar
                                    lengthFilter={odLengthFilter}
                                    setLengthFilter={setOdLengthFilter}
                                    groupFilter={odGroupFilter}
                                    setGroupFilter={setOdGroupFilter}
                                    sortBy={odSortBy}
                                    setSortBy={setOdSortBy}
                                    availableDurations={availableOdDurations}
                                    completedFilter={odCompletedFilter}
                                    setCompletedFilter={setOdCompletedFilter}
                                    completedCount={completedIds.size}
                                    favoritedFilter={odFavoritedFilter}
                                    setFavoritedFilter={setOdFavoritedFilter}
                                    favoritedCount={activeTab === 'classes' ? activeOdVideos.filter((v) => userFavorites.has(v.classId)).length : activeOdVideos.filter((v) => userVideoFavorites.has(v.id)).length}
                                    easeInFilter={odEaseInFilter}
                                    setEaseInFilter={setOdEaseInFilter}
                                    easeInCount={activeOdVideos.filter((v) => v.easeIn).length}
                                    strongPlusFilter={odStrongPlusFilter}
                                    setStrongPlusFilter={setOdStrongPlusFilter}
                                    strongPlusCount={activeOdVideos.filter((v) => v.strongPlus).length}
                                />

                                <div
                                    className={activeTab === 'classes' ? 'mom-od-grid' : ''}
                                    style={{
                                        display: 'grid',
                                        gap: 14,
                                        // Shorts keeps the dense 160px grid inline. On Demand
                                        // lets the .mom-od-grid class drive columns so the
                                        // desktop media query can enlarge tiles without
                                        // affecting the shared mobile layout.
                                        ...(activeTab === 'classes'
                                            ? {}
                                            : { gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }),
                                    }}
                                >
                                    {filteredOnDemand.map((video, i) => (
                                        // Flex column wrapper: gives the grid cell a proper
                                        // intrinsic height = sum of (VideoCard + admin buttons).
                                        // Without this, VideoCard's `height: 100%` + `contain: content`
                                        // creates a circular sizing dependency where the cell's
                                        // height collapses to the card alone, and admin buttons
                                        // render outside the grid row track — visually overlapped
                                        // by the next row's card. With flex column, the cell's
                                        // intrinsic height includes everything, the row grows
                                        // accordingly, no overlap.
                                        <div key={video.id} style={{ display: 'flex', flexDirection: 'column' }}>
                                            <VideoCard
                                                video={video}
                                                onClick={video.isLocked ? () => setLockedVideo(video) : () => setActiveVideo(video)}
                                                animationDelay={Math.min(i * 0.05, 0.5)}
                                                isCompleted={completedIds.has(video.id)}
                                                completionCount={perVideoCompletions[video.id]?.count || 0}
                                                lastCompletedAt={perVideoCompletions[video.id]?.lastCompletedAt || null}
                                                onLogComplete={video.isLocked ? undefined : handleLogComplete}
                                                onUndoComplete={handleUndoComplete}
                                                onLoadHistory={video.isLocked ? undefined : handleLoadHistory}
                                                completionCopy={completionCopy}
                                                showFreeLabel={video.isFree}
                                                isLocked={video.isLocked}
                                                favoriteCount={video.likeCount || 0}
                                                // On Demand (Classes tab) only: the aired-date badge replaces
                                                // the FREE/CLASS tag. Shorts pass undefined ⇒ unchanged.
                                                airedLabel={activeTab === 'classes' ? video.airedLabel : undefined}
                                            />
                                            {/* ── Admin actions on the recording card ──
                                                Styled to match the "Mark Complete" pill above (proper padding,
                                                rounded border, readable size) so they read as real buttons rather
                                                than text links. Three actions stacked: Choose Thumbnail, Metrics,
                                                Make Free for All. The Free toggle visually distinguishes its
                                                active state via the primary gradient (matches existing pattern).
                                                See user feedback 2026-04-27 — "elegant / premium feeling for the
                                                admin side, too." */}
                                            {isAdmin && video.isClassRecording && video.muxPlaybackId && (
                                                <button onClick={() => openThumbnailPicker(video, true)}
                                                    style={{
                                                        width: '100%', marginTop: 6, padding: '8px 14px',
                                                        borderRadius: 10, fontSize: 12, fontWeight: 600,
                                                        fontFamily: 'var(--mom-font-sans)', cursor: 'pointer',
                                                        background: 'var(--mom-white)', color: 'var(--mom-charcoal)',
                                                        border: '1px solid var(--mom-cream-dark)',
                                                        transition: 'all 0.15s ease',
                                                    }}>
                                                    Choose Thumbnail
                                                </button>
                                            )}
                                            {/* Class metrics — needs source classId, not the recording's muxAssetId.
                                                classRecordings docs carry the source classId field. If absent (legacy
                                                or orphaned recording), the button is hidden. */}
                                            {isAdmin && video.isClassRecording && video.classId && (
                                                <button onClick={() => setMetricsClassId(video.classId)}
                                                    style={{
                                                        width: '100%', marginTop: 6, padding: '8px 14px',
                                                        borderRadius: 10, fontSize: 12, fontWeight: 600,
                                                        fontFamily: 'var(--mom-font-sans)', cursor: 'pointer',
                                                        background: 'var(--mom-white)', color: 'var(--mom-charcoal)',
                                                        border: '1px solid var(--mom-cream-dark)',
                                                        transition: 'all 0.15s ease',
                                                    }}>
                                                    Metrics
                                                </button>
                                            )}
                                            {/* Re-air this recording — seeds the modal with this class so the
                                                admin lands straight on the date step. Same guard as the service:
                                                a recording with no Mux asset can't be aired. */}
                                            {isAdmin && video.isClassRecording && video.muxAssetId && video.muxPlaybackId && (
                                                <button onClick={() => { setReAirSeed(video); setReAirOpen(true); }}
                                                    style={{
                                                        width: '100%', marginTop: 6, padding: '8px 14px',
                                                        borderRadius: 10, fontSize: 12, fontWeight: 600,
                                                        fontFamily: 'var(--mom-font-sans)', cursor: 'pointer',
                                                        background: 'var(--mom-white)', color: 'var(--mom-charcoal)',
                                                        border: '1px solid var(--mom-cream-dark)',
                                                        transition: 'all 0.15s ease',
                                                    }}>
                                                    Re-air
                                                </button>
                                            )}
                                            {isAdmin && video.isClassRecording && (
                                                <button onClick={() => handleToggleFreeRecording(video)}
                                                    style={{
                                                        width: '100%', marginTop: 6, padding: '8px 14px',
                                                        borderRadius: 10, fontSize: 12, fontWeight: 700,
                                                        fontFamily: 'var(--mom-font-sans)', cursor: 'pointer',
                                                        letterSpacing: '0.04em',
                                                        background: video.isFree
                                                            ? 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))'
                                                            : 'var(--mom-white)',
                                                        color: video.isFree ? 'var(--mom-white)' : 'var(--mom-charcoal)',
                                                        border: video.isFree ? '1px solid transparent' : '1px solid var(--mom-cream-dark)',
                                                        transition: 'all 0.15s ease',
                                                    }}>
                                                    {video.isFree ? 'FREE FOR ALL ✓' : 'Make Free for All'}
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {filteredOnDemand.length === 0 && (
                                    <div style={{ textAlign: 'center', padding: '60px 24px' }}>
                                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 16, color: 'var(--mom-warm-gray)' }}>
                                            {odCompletedFilter === 'completed' ? 'No completed workouts yet. Start one!' : 'No workouts match your filters.'}
                                        </p>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* ══════════════════════════════════════════
                    SERIES TAB (free self-paced challenges)
                    Gated above so only admins (always) or everyone
                    (when enableSeriesTab) can reach it. See adr-series.md.
                ══════════════════════════════════════════ */}
                {activeTab === 'series' && (() => {
                    const dayTemplate = siteConfig.seriesDayLabelTemplate || 'Day {n}';
                    const visibleSeries = (seriesList || []).filter((s) => isAdmin || s.active);
                    const selectedSeries = selectedSeriesId ? (seriesList || []).find((s) => s.id === selectedSeriesId) : null;

                    // ── List view ──
                    if (!selectedSeries) {
                        return (
                            <div style={{ maxWidth: 1000, margin: '0 auto' }}>
                                <div style={{ textAlign: 'center', marginBottom: 28 }}>
                                    <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 26, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 8 }}>{siteConfig.seriesTabHeading || 'Challenges'}</h2>
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: 'var(--mom-warm-gray)', maxWidth: 520, margin: '0 auto', lineHeight: 1.5 }}>{siteConfig.seriesTabSubtext || 'Multi-day movement journeys you can start anytime and do at your own pace.'}</p>
                                </div>
                                {/* A ?series= link that pointed at a deleted / unpublished / mistyped
                                    challenge lands here instead of erroring. */}
                                {seriesLinkMissed && (
                                    <div style={{ maxWidth: 520, margin: '0 auto 24px', padding: '12px 16px', borderRadius: 14, background: 'color-mix(in srgb, var(--mom-primary) 6%, transparent)', border: '1px solid color-mix(in srgb, var(--mom-primary) 18%, transparent)' }}>
                                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-charcoal)', textAlign: 'center', lineHeight: 1.5 }}>
                                            {siteConfig.seriesNotFoundText || "That challenge isn't available right now."}
                                        </p>
                                    </div>
                                )}
                                {seriesLoading ? (
                                    <div style={{ textAlign: 'center', padding: 60 }}>
                                        <div style={{ width: 28, height: 28, borderRadius: '50%', border: '3px solid var(--mom-cream-dark)', borderTopColor: 'var(--mom-primary)', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
                                    </div>
                                ) : visibleSeries.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: 60 }}>
                                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, color: 'var(--mom-warm-gray)' }}>
                                            {isAdmin
                                                ? 'No challenges yet. Create one in Admin → Series, then it appears here.'
                                                : (siteConfig.seriesEmptyComingSoon || 'New challenges are coming soon — check back shortly.')}
                                        </p>
                                    </div>
                                ) : (
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
                                        {visibleSeries.map((s) => {
                                            const total = (s.videoOrder || []).length;
                                            const scope = seriesCompletion[s.id] || {};
                                            const done = (s.videoOrder || []).filter((id) => (scope[id]?.count || 0) > 0).length;
                                            const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                                            const finished = total > 0 && done >= total;
                                            return (
                                                <div key={s.id} onClick={() => { setSeriesLinkMissed(false); setSelectedSeriesId(s.id); }} role="button" tabIndex={0}
                                                    style={{ background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)', border: '1px solid rgba(255,255,255,0.6)', borderRadius: 22, overflow: 'hidden', cursor: 'pointer', boxShadow: 'var(--shadow-md)', transition: 'transform 0.2s ease, box-shadow 0.2s ease', position: 'relative' }}>
                                                    {/* Cover */}
                                                    <div style={{ position: 'relative', height: 160, background: 'linear-gradient(135deg, color-mix(in srgb, var(--mom-primary) 22%, transparent), color-mix(in srgb, var(--mom-primary-dark, var(--mom-primary)) 30%, transparent))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        {s.imageUrl ? (
                                                            <img src={s.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} referrerPolicy="no-referrer" />
                                                        ) : (
                                                            <span style={{ fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontSize: 40, color: 'rgba(255,255,255,0.85)' }}>{total || ''}</span>
                                                        )}
                                                        <div style={{ position: 'absolute', top: 12, right: 12, display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                                                            {total > 0 && (
                                                                <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 10, background: 'var(--mom-primary)', color: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>{total} days</span>
                                                            )}
                                                            {isAdmin && !s.active && (
                                                                <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 8, background: 'rgba(45,41,38,0.75)', color: '#fff' }}>DRAFT</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {/* Body */}
                                                    <div style={{ padding: '16px 18px 18px' }}>
                                                        <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 19, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 6 }}>{s.title || 'Untitled'}</h3>
                                                        {s.description && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-warm-gray)', lineHeight: 1.5, marginBottom: 14, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{s.description}</p>}
                                                        {/* Progress */}
                                                        {total > 0 && (
                                                            done === 0 ? (
                                                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: 'var(--mom-primary)' }}>{siteConfig.seriesStartCue || 'Start the challenge'}</p>
                                                            ) : (
                                                                <div>
                                                                    <div style={{ height: 6, borderRadius: 4, background: 'var(--mom-cream-dark)', overflow: 'hidden', marginBottom: 6 }}>
                                                                        <div style={{ width: `${pct}%`, height: '100%', borderRadius: 4, background: 'var(--mom-primary)', transition: 'width 0.3s ease' }} />
                                                                    </div>
                                                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: finished ? 'var(--mom-primary)' : 'var(--mom-warm-gray)' }}>
                                                                        {finished ? (siteConfig.seriesCompletedLabel || 'Challenge complete') : (siteConfig.seriesProgressTemplate || '{done} of {total} days').replace('{done}', done).replace('{total}', total)}
                                                                    </p>
                                                                </div>
                                                            )
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    }

                    // ── Detail view (day list) ──
                    const scope = seriesCompletion[selectedSeries.id] || {};
                    // Rows that are NOT days (a welcome note, a bonus) keep their place
                    // in the running order but carry no number and are excluded from the
                    // count — so the first real workout after a welcome is Day 1, and
                    // "3 of 7 days" stays honest. No nonDayVideos ⇒ every row is a day
                    // and all of this reduces to the previous positional numbering.
                    const dayIndex = indexSeriesDays(seriesVideos, selectedSeries.nonDayVideos);
                    const dayRows = dayIndex.filter((r) => r.isDay);
                    const total = dayRows.length;
                    const done = dayRows.filter((r) => (scope[r.video.id]?.count || 0) > 0).length;
                    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                    // Scheduled-release state. Uses the SERVER-synced clock so a device
                    // with a fast clock can't open a day early. Read-only gating: no
                    // writes, so it can't fan out the way a shared write could.
                    // Admin always sees every day (to preview before it opens).
                    const releaseNow = serverNow();
                    const dayLocked = (v) => !isAdmin && !isSeriesDayReleased(selectedSeries.releaseDates, v.id, releaseNow);
                    // "Up next" points at the first day they can actually DO.
                    const upNextIdx = seriesVideos.findIndex((v) => !((scope[v.id]?.count || 0) > 0) && !dayLocked(v));

                    // ── Rich-content extras (all optional; absent ⇒ nothing renders) ──
                    // See docs/decisions/adr-series-deep-link-and-sections.md.
                    const dayCount = total || (selectedSeries.videoOrder || []).length;   // days only (extras excluded)
                    // Opt-out for challenges where the file length overstates the
                    // workout (intro/outro) and the real time is written into the
                    // title/description. Absent ⇒ false ⇒ lengths show as before.
                    const hideLengths = selectedSeries.hideVideoLengths === true;
                    const totalMinutes = seriesVideos.reduce((sum, v) => sum + (Number(v.length) || 0), 0);
                    const lengthPill = hideLengths ? null : (totalMinutes >= 60
                        ? `${Math.floor(totalMinutes / 60)} hr${totalMinutes % 60 ? ` ${totalMinutes % 60} min` : ''} total`
                        : (totalMinutes > 0 ? `${totalMinutes} min total` : null));
                    const heroPills = [
                        dayCount > 0 ? `${dayCount} day${dayCount === 1 ? '' : 's'}` : null,
                        lengthPill,
                        ...((selectedSeries.metaPills) || []),
                    ].filter(Boolean);
                    const shareUrl = seriesShareUrl(selectedSeries, typeof window !== 'undefined' ? window.location.origin : '');
                    // Only render the info wrapper when there is actually something in
                    // it — otherwise an existing challenge would pick up a stray 24px
                    // of empty space it never had.
                    const hasInfo = ((selectedSeries.highlights || []).some((h) => h && String(h).trim()))
                        || ((selectedSeries.sections || []).some((s) => s && ((s.heading || '').trim() || (s.body || '').trim())))
                        || ((selectedSeries.faqs || []).some((f) => f && (f.question || '').trim()));
                    const infoBlocks = hasInfo ? <SeriesInfoBlocks series={selectedSeries} siteConfig={siteConfig} /> : null;

                    return (
                        <div style={{ maxWidth: 760, margin: '0 auto' }}>
                            <button onClick={() => setSelectedSeriesId(null)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: 'var(--mom-warm-gray)', marginBottom: 16, padding: 0 }}>
                                <span style={{ fontSize: 16 }}>‹</span> {siteConfig.seriesTabHeading || 'Challenges'}
                            </button>
                            {/* Hero */}
                            <div style={{ background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)', border: '1px solid rgba(255,255,255,0.6)', borderRadius: 24, overflow: 'hidden', marginBottom: 24, boxShadow: 'var(--shadow-md)' }}>
                                {selectedSeries.imageUrl && <img src={selectedSeries.imageUrl} alt="" style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} referrerPolicy="no-referrer" />}
                                <div style={{ padding: '20px 24px 24px' }}>
                                    <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 26, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 8 }}>{selectedSeries.title}</h2>
                                    {selectedSeries.tagline && selectedSeries.tagline.trim() && (
                                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: 'color-mix(in srgb, var(--mom-primary) 78%, var(--mom-charcoal))', marginBottom: 10, lineHeight: 1.45 }}>{selectedSeries.tagline}</p>
                                    )}
                                    {selectedSeries.description && <div style={{ marginBottom: 16, fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: 'var(--mom-warm-gray)', lineHeight: 1.6 }}><FormattedText text={selectedSeries.description} /></div>}
                                    <SeriesMetaPills pills={heroPills} />
                                    {/* Progress + the shareable link. Stacks rather than crowds on phones. */}
                                    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
                                        <div style={{ flex: '1 1 240px', minWidth: 0 }}>
                                            {total > 0 && (
                                                <div>
                                                    <div style={{ height: 8, borderRadius: 5, background: 'var(--mom-cream-dark)', overflow: 'hidden', marginBottom: 8 }}>
                                                        <div style={{ width: `${pct}%`, height: '100%', borderRadius: 5, background: 'var(--mom-primary)', transition: 'width 0.3s ease' }} />
                                                    </div>
                                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: done >= total ? 'var(--mom-primary)' : 'var(--mom-warm-gray)' }}>
                                                        {done >= total ? (siteConfig.seriesCompletedLabel || 'Challenge complete') : (siteConfig.seriesProgressTemplate || '{done} of {total} days').replace('{done}', done).replace('{total}', total)}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                        <SeriesShareButton
                                            url={shareUrl}
                                            title={selectedSeries.title || ''}
                                            label={siteConfig.seriesShareLabel || 'Share'}
                                            copiedLabel={siteConfig.seriesShareCopiedLabel || 'Link copied'}
                                        />
                                    </div>
                                </div>
                            </div>
                            {/* Rich info blocks. Position is a per-series choice: orientation-first
                                challenges put them above the days, everything else below (the
                                returning-member default). */}
                            {hasInfo && selectedSeries.detailsFirst ? <div style={{ marginBottom: 8 }}>{infoBlocks}</div> : null}

                            {/* Optional day-list heading. Absent ⇒ nothing renders, which is the
                                pre-existing layout. */}
                            {selectedSeries.daysHeading && selectedSeries.daysHeading.trim() && (
                                <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 19, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 12 }}>{selectedSeries.daysHeading}</h3>
                            )}

                            {/* Day list */}
                            {seriesVideosLoading ? (
                                <div style={{ textAlign: 'center', padding: 40 }}><div style={{ width: 26, height: 26, borderRadius: '50%', border: '3px solid var(--mom-cream-dark)', borderTopColor: 'var(--mom-primary)', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} /></div>
                            ) : seriesVideos.length === 0 ? (
                                <p style={{ textAlign: 'center', fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: 'var(--mom-warm-gray)', padding: 24 }}>No videos in this challenge yet.</p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {seriesVideos.map((v, i) => {
                                        const completed = (scope[v.id]?.count || 0) > 0;
                                        const isUpNext = i === upNextIdx;
                                        const row = dayIndex[i] || { isDay: true, dayNumber: i + 1, kicker: '' };
                                        const title = seriesDayLabel({ video: v, dayLabels: selectedSeries.dayLabels, dayNumber: row.dayNumber || (i + 1), template: dayTemplate });
                                        const locked = dayLocked(v);
                                        const releaseAt = seriesDayReleaseAt(selectedSeries.releaseDates, v.id);
                                        // Admins see a scheduled-but-not-yet-open day as a normal,
                                        // playable row with a quiet note about when it opens.
                                        const adminPreview = isAdmin && releaseAt && releaseAt.getTime() > releaseNow;
                                        const unlockNote = releaseAt ? `${siteConfig.seriesLockedLabel || 'Unlocks'} ${formatSeriesReleaseDate(releaseAt)}` : '';
                                        return (
                                            <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', background: 'var(--mom-white)', borderRadius: 16, boxShadow: locked ? 'none' : 'var(--shadow-sm)', border: isUpNext ? '1.5px solid color-mix(in srgb, var(--mom-primary) 45%, transparent)' : '1px solid var(--mom-cream-dark)', opacity: locked ? 0.7 : 1 }}>
                                                {/* Day index + ✓ (or a lock until its release date) */}
                                                <div
                                                    onClick={locked ? undefined : () => setActiveSeriesVideo(v)}
                                                    role={locked ? undefined : 'button'}
                                                    tabIndex={locked ? undefined : 0}
                                                    style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 0, cursor: locked ? 'default' : 'pointer' }}
                                                >
                                                    <div style={{ width: 40, height: 40, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: completed ? 'var(--mom-primary)' : 'var(--mom-cream)', color: completed ? '#fff' : 'var(--mom-warm-gray)', fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontSize: 16, fontWeight: 700 }}>
                                                        {completed ? (
                                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                                        ) : locked ? (
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                                                        ) : row.isDay ? row.dayNumber : (
                                                            /* An extra has no number — a play mark keeps the row's rhythm
                                                               without inventing one. */
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><polygon points="7 4 20 12 7 20" /></svg>
                                                        )}
                                                    </div>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        {(() => {
                                                            // Days always show "Day N". An extra shows its optional label
                                                            // ("WELCOME") — and if it has none, no eyebrow line renders at
                                                            // all rather than an empty one holding space.
                                                            const lead = row.isDay ? dayTemplate.replace('{n}', row.dayNumber) : row.kicker;
                                                            const upNext = isUpNext && !completed ? (siteConfig.seriesUpNextLabel || 'Up next') : '';
                                                            const text = [lead, upNext].filter(Boolean).join(' · ');
                                                            if (!text) return null;
                                                            return (
                                                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', color: 'var(--mom-primary)', textTransform: 'uppercase' }}>{text}</p>
                                                            );
                                                        })()}
                                                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, fontWeight: 600, color: locked ? 'var(--mom-warm-gray)' : 'var(--mom-charcoal)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}{v.length && !hideLengths ? <span style={{ fontWeight: 400, color: 'var(--mom-warm-gray)' }}> · {v.length} min</span> : null}</p>
                                                        {(locked || adminPreview) && unlockNote && (
                                                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: 'color-mix(in srgb, var(--mom-primary) 70%, var(--mom-charcoal))', marginTop: 2 }}>
                                                                {unlockNote}{adminPreview ? ' · visible to you only' : ''}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                                {/* Mark done (manual fallback) — never on a day that hasn't opened */}
                                                {userId && !locked && (
                                                    completed ? (
                                                        <span style={{ flexShrink: 0, fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: 'var(--mom-primary)' }}>Done</span>
                                                    ) : (
                                                        <button onClick={() => handleSeriesMarkDone(v)} style={{ flexShrink: 0, fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: 'var(--mom-charcoal)', background: 'var(--mom-cream)', border: '1px solid var(--mom-cream-dark)', borderRadius: 10, padding: '7px 14px', cursor: 'pointer' }}>Mark done</button>
                                                    )
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Default placement: details below the days. */}
                            {hasInfo && !selectedSeries.detailsFirst ? <div style={{ marginTop: 24 }}>{infoBlocks}</div> : null}
                        </div>
                    );
                })()}
            </div>

            {/* Series day video modal — reuses VideoPlayerModal. Completions thread
                programId === seriesId + source 'series' (adr-series.md). */}
            {activeSeriesVideo && (
                <VideoPlayerModal
                    video={activeSeriesVideo}
                    onClose={() => setActiveSeriesVideo(null)}
                    autoplay={true}
                    userId={userId}
                    userName={clerkUser ? `${clerkUser.firstName || ''} ${(clerkUser.lastName || '').charAt(0)}.`.trim() : ''}
                    isAdmin={isAdmin}
                    onAutoComplete={handleSeriesAutoComplete}
                    programId={selectedSeriesId}
                    completionSource="series"
                    completionCount={(seriesCompletion[selectedSeriesId]?.[activeSeriesVideo.id]?.count) || 0}
                    lastCompletedAt={(seriesCompletion[selectedSeriesId]?.[activeSeriesVideo.id]?.lastCompletedAt) || null}
                    completionCopy={completionCopy}
                    onUndoComplete={handleSeriesUndo}
                    getToken={getToken}
                    showLength={(seriesList || []).find((s) => s.id === selectedSeriesId)?.hideVideoLengths !== true}
                />
            )}

            {/* Video player modal (on demand) */}
            {activeVideo && (
                <VideoPlayerModal
                    video={activeVideo}
                    onClose={() => { setActiveVideo(null); setDeepLinkCommentId(null); }}
                    highlightCommentId={deepLinkCommentId}
                    autoplay={!activeVideo?.isClassRecording}
                    isFavorited={activeVideo?.isClassRecording ? userFavorites.has(activeVideo?.classId) : userVideoFavorites.has(activeVideo?.id)}
                    onToggleFavorite={userId ? (activeVideo?.isClassRecording && activeVideo?.classId ? () => handleToggleFavorite(activeVideo.classId) : !activeVideo?.isClassRecording ? () => handleToggleVideoFavorite(activeVideo.id) : undefined) : undefined}
                    favoriteCount={activeVideo?.likeCount || 0}
                    userId={userId}
                    userName={clerkUser ? `${clerkUser.firstName || ''} ${(clerkUser.lastName || '').charAt(0)}.`.trim() : ''}
                    isAdmin={isAdmin}
                    // Auto-completion at 75% (class recordings only) — silent.
                    // See docs/decisions/adr-auto-completion.md.
                    onAutoComplete={handleVideoAutoComplete}
                    completionCount={activeVideo?.id ? (perVideoCompletions[activeVideo.id]?.count || 0) : 0}
                    lastCompletedAt={activeVideo?.id ? (perVideoCompletions[activeVideo.id]?.lastCompletedAt || null) : null}
                    completionCopy={completionCopy}
                    // Persistent per-entry undo in the modal's "Your history" list.
                    // (programId defaults null here → On Demand / free scope.)
                    onUndoComplete={handleUndoComplete}
                    // On-demand watch progress — admin metrics signal.
                    // See docs/decisions/adr-on-demand-watch-tracking.md.
                    getToken={getToken}
                    // Community on release-day classes (ClassTogether.js) — only when
                    // this video is a release-day class from the last week.
                    renderTogether={togetherClass ? ({ completionCount: cc, openComments }) => (
                        <ClassTogetherPanel
                            key={togetherClass.id}
                            classId={togetherClass.id}
                            dayLabel={togetherClass.releaseDate === localDateKey(new Date()) ? 'today' : formatReleaseDate(togetherClass.releaseDate, { weekday: 'long' })}
                            userId={userId}
                            isAdmin={isAdmin}
                            firstName={clerkUser?.firstName || ''}
                            lastInitial={(clerkUser?.lastName || '').charAt(0)}
                            getToken={getToken}
                            completionCount={cc}
                            openComments={openComments}
                            copy={siteConfig}
                        />
                    ) : undefined}
                />
            )}

            {/* Class metrics drill-down modal (admin only).
                Mounted by setting metricsClassId; closes via setMetricsClassId(null).
                Loads asynchronously from /api/admin-class-metrics on mount.
                See docs/decisions/adr-class-metrics.md. */}
            {isAdmin && metricsClassId && (
                <ClassMetricsModal
                    classId={metricsClassId}
                    getToken={getToken}
                    onClose={() => setMetricsClassId(null)}
                />
            )}

            {/* Re-air a past class (admin only).
                Reuses the already-loaded classVideos list, so opening the picker costs
                zero extra reads. On success we reload the schedule and scroll/pulse the
                create-form area so the new airing is visibly acknowledged. */}
            {isAdmin && reAirOpen && (
                <ReAirClassModal
                    recordings={classVideos}
                    initialRecording={reAirSeed}
                    onClose={() => { setReAirOpen(false); setReAirSeed(null); }}
                    onScheduled={async () => {
                        setReAirOpen(false);
                        setReAirSeed(null);
                        setActiveTab('live');
                        await loadClasses();
                        setAdminPulse('createForm');
                        setTimeout(() => setAdminPulse((p) => (p === 'createForm' ? null : p)), 1600);
                    }}
                />
            )}

            {/* Thumbnail picker modal (admin only) */}
            {thumbnailPicker && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
                    onClick={() => setThumbnailPicker(null)}>
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} />
                    <div style={{
                        position: 'relative', background: 'var(--mom-white)', borderRadius: 20, padding: 24,
                        maxWidth: 600, width: '100%', maxHeight: '80vh', overflowY: 'auto',
                        boxShadow: 'var(--shadow-xl)',
                    }} onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                            <h3 style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 16, fontWeight: 700, color: 'var(--mom-charcoal)', margin: 0 }}>Choose Thumbnail</h3>
                            <button onClick={() => setThumbnailPicker(null)}
                                style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--mom-warm-gray)', cursor: 'pointer', padding: '4px 8px' }}>
                                ×
                            </button>
                        </div>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-warm-gray)', marginBottom: 16 }}>
                            Click a frame to set it as the thumbnail
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 10 }}>
                            {getThumbnailTimestamps(thumbnailPicker.durationSeconds).map((time) => {
                                const url = `https://image.mux.com/${thumbnailPicker.playbackId}/thumbnail.jpg?time=${time}&width=640&height=360`;
                                const mins = Math.floor(time / 60);
                                const secs = time % 60;
                                return (
                                    <button key={time} onClick={() => handleThumbnailSelect(url)}
                                        style={{ padding: 0, border: '2px solid var(--mom-cream-dark)', borderRadius: 10, overflow: 'hidden', cursor: 'pointer', background: 'var(--mom-cream)', transition: 'all 0.15s ease' }}
                                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--mom-primary)'; e.currentTarget.style.transform = 'scale(1.03)'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--mom-cream-dark)'; e.currentTarget.style.transform = 'scale(1)'; }}>
                                        <img src={url} alt={`Frame at ${mins}:${secs.toString().padStart(2, '0')}`}
                                            style={{ width: '100%', height: 'auto', display: 'block' }}
                                            loading="lazy" />
                                        <span style={{ display: 'block', padding: '4px 0', fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 600, color: 'var(--mom-warm-gray)', textAlign: 'center' }}>
                                            {mins}:{secs.toString().padStart(2, '0')}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Reminder feedback toast — surfaces success / permission-required /
                unsupported-browser messages from handleToggleReminder. Auto-dismisses
                after 5 seconds via the effect on reminderToast. Positioned above
                the mobile bottom nav; high z-index keeps it above modals. */}
            {reminderToast && (
                <div style={{
                    position: 'fixed', bottom: 90, left: '50%', transform: 'translateX(-50%)',
                    zIndex: 10001, maxWidth: 360, width: 'calc(100% - 32px)',
                    padding: '12px 18px', borderRadius: 12,
                    background: reminderToast.kind === 'error' ? 'var(--mom-charcoal)' : 'var(--mom-primary)',
                    color: 'var(--mom-white)',
                    fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600,
                    lineHeight: 1.4, textAlign: 'center',
                    boxShadow: 'var(--shadow-lg)',
                    animation: 'fadeIn 0.2s ease-out',
                }}
                onClick={() => setReminderToast(null)}>
                    {reminderToast.message}
                </div>
            )}

            {/* Layer 1.4: LivePlayer test modal (admin-only) — mounts the REAL
                <LivePlayer> with synthetic viewer-side props pointing at the
                draft's recording. Lets admin apples-to-apples verify the
                LivePlayer changes (lock playback during airing, auto-fullscreen
                on rotate, auto-exit fullscreen on class end) WITHOUT publishing
                the class to real users. Drafts are filtered from upcoming, so
                no other user can ever see this class.

                Synthetic props chosen for prod parity:
                - status="live" + classType="prerecorded" → Mux Player runs in
                  on-demand mode, identical to a real prerecorded class during
                  its airing window
                - isAdmin={false} → admin behaves exactly like a regular viewer
                  (joins presence, gets the locked controls, no admin overlays)
                - userId={userId} → engagement features (favorite, comments)
                  work the same as for a real viewer
                - endingAt={null} → no graceful-end is forced; the natural
                  asset-end fires `playerState='ended'` which triggers Item 3's
                  auto-exit-fullscreen exactly as the real flow will tomorrow */}
            {livePlayerTestDraft && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, background: 'rgba(0,0,0,0.85)' }}
                    onClick={() => setLivePlayerTestDraft(null)}>
                    <div style={{ position: 'relative', maxWidth: 920, width: '100%' }} onClick={(e) => e.stopPropagation()}>
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '8px 12px', marginBottom: 8, gap: 12,
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                                <span style={{
                                    padding: '2px 10px', borderRadius: 999, fontSize: 9, fontWeight: 800,
                                    background: 'var(--mom-warm-gray)', color: 'var(--mom-white)',
                                    fontFamily: 'var(--mom-font-sans)', letterSpacing: '0.08em', flexShrink: 0,
                                }}>WATCH AS VIEWER</span>
                                <span style={{
                                    fontFamily: 'var(--mom-font-sans)', fontSize: 12,
                                    color: 'rgba(255,255,255,0.75)',
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}>
                                    {livePlayerTestDraft.title || 'Untitled draft'} — apples-to-apples viewer experience
                                </span>
                            </div>
                            <button onClick={() => setLivePlayerTestDraft(null)}
                                style={{ background: 'rgba(255,255,255,0.12)', border: 'none', color: 'var(--mom-white)', width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', flexShrink: 0, fontSize: 18, lineHeight: 1 }}>
                                ×
                            </button>
                        </div>
                        <LivePlayer
                            playbackId={livePlayerTestDraft.muxPlaybackId}
                            title={livePlayerTestDraft.title || 'Test'}
                            status="live"
                            state="live"
                            scheduledAt={livePlayerTestDraft.scheduledAt}
                            classId={livePlayerTestDraft.id}
                            isAdmin={false}
                            classType="prerecorded"
                            isFavorited={false}
                            userId={userId}
                            endingAt={null}
                        />
                    </div>
                </div>
            )}

            {/* Layer 1.5: Draft Preview modal (admin-only) — Mux Player against the draft's recording */}
            {previewDraft && (
                <DraftPreviewModal draft={previewDraft} onClose={() => setPreviewDraft(null)} />
            )}

            {/* Layer 1.6: Publish-Draft confirmation modal (admin-only) */}
            {publishConfirm && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
                    onClick={() => !publishing && setPublishConfirm(null)}>
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} />
                    <div style={{
                        position: 'relative', background: 'var(--mom-white)', borderRadius: 20, padding: '28px 28px 24px',
                        maxWidth: 420, width: '100%', boxShadow: 'var(--shadow-xl)',
                    }} onClick={(e) => e.stopPropagation()}>
                        <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 8 }}>
                            Publish this class?
                        </h3>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: 'var(--mom-charcoal)', lineHeight: 1.5, marginBottom: 4 }}>
                            <strong>{publishConfirm.title}</strong>
                        </p>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-warm-gray)', marginBottom: 16 }}>
                            {classWhenLabel(publishConfirm)} · {publishConfirm.duration || 45} min
                        </p>
                        {/* Warn if scheduledAt is too close — reminders cron runs every 5min and
                            auto-cancel fires at scheduledAt+15m. Publishing inside that window means
                            the class might miss reminders or get auto-cancelled. */}
                        {publishConfirm.scheduledAt && (new Date(publishConfirm.scheduledAt).getTime() - Date.now()) < 15 * 60 * 1000 && (
                            <div style={{
                                padding: '10px 14px', borderRadius: 10, marginBottom: 16,
                                background: 'color-mix(in srgb, var(--mom-warning, #d97706) 8%, transparent)',
                                border: '1px solid color-mix(in srgb, var(--mom-warning, #d97706) 30%, transparent)',
                            }}>
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-warning, #d97706)', fontWeight: 600 }}>
                                    ⚠ The scheduled time is in the past or very close — reminders may not fire and the cron could auto-cancel. Update the time before publishing if that's not what you want.
                                </p>
                            </div>
                        )}
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-charcoal)', lineHeight: 1.5, marginBottom: 20 }}>
                            Publishing moves this class to the Schedule. From here, all the normal flows take over — reminders, waiting room, going live at the scheduled time, and the recording landing on On Demand once it ends.
                        </p>
                        <div style={{ display: 'flex', gap: 10 }}>
                            <button onClick={() => setPublishConfirm(null)} disabled={publishing}
                                style={{ flex: 1, padding: '12px', borderRadius: 12, fontSize: 14, fontWeight: 600, background: 'none', color: 'var(--mom-charcoal)', border: '1px solid var(--mom-cream-dark)', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)' }}>
                                Cancel
                            </button>
                            <button onClick={confirmPublish} disabled={publishing}
                                style={{ flex: 1, padding: '12px', borderRadius: 12, fontSize: 14, fontWeight: 700, background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))', color: 'var(--mom-white)', border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)', opacity: publishing ? 0.6 : 1 }}>
                                {publishing ? 'Publishing…' : 'Publish'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Layer 2: Locked video paywall modal */}
            {lockedVideo && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
                    onClick={() => setLockedVideo(null)}>
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} />
                    <div style={{
                        position: 'relative', background: 'var(--mom-white)', borderRadius: 24, overflow: 'hidden',
                        maxWidth: 380, width: '100%', boxShadow: 'var(--shadow-xl)',
                    }} onClick={(e) => e.stopPropagation()}>
                        {/* Blurred thumbnail preview */}
                        <div style={{ position: 'relative', width: '100%', height: 180, overflow: 'hidden' }}>
                            {lockedVideo.driveThumbnail ? (
                                <img src={lockedVideo.driveThumbnail} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(6px) brightness(0.7)', transform: 'scale(1.1)' }} />
                            ) : (
                                <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, var(--mom-charcoal), var(--mom-charcoal-light))' }} />
                            )}
                            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)', border: '2px solid rgba(255,255,255,0.3)' }}>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                    </svg>
                                </div>
                            </div>
                            <button onClick={() => setLockedVideo(null)}
                                style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(0,0,0,0.4)', border: 'none', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', backdropFilter: 'blur(4px)' }}>
                                <span style={{ color: '#fff', fontSize: 18, lineHeight: 1 }}>×</span>
                            </button>
                        </div>
                        {/* Content */}
                        <div style={{ padding: '24px 28px 28px', textAlign: 'center' }}>
                            <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 20, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 4 }}>
                                {lockedVideo.title}
                            </h3>
                            {lockedVideo.length > 0 && (
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-warm-gray)', marginBottom: 16 }}>
                                    {lockedVideo.length} min
                                </p>
                            )}
                            <div style={{ width: 40, height: 2, background: 'var(--mom-primary)', borderRadius: 1, margin: '0 auto 16px', opacity: 0.4 }} />
                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: 'var(--mom-charcoal)', lineHeight: 1.6, marginBottom: 24 }}>
                                {siteConfig.lockedVideoDescription || 'Unlock unlimited access to all classes—follow the weekly schedule or work out On Demand, anytime.'}
                            </p>
                            {/* Defensive: only offer Subscribe checkout when subscriptions are
                                actually enabled. Belt-and-suspenders — the lock-state calc above
                                already prevents the modal from opening when subs are off, but if
                                the flag flips between modal open and click we don't want to send
                                the user to a Stripe checkout for a feature that's been disabled.
                                Uses `!== false` to mirror the lock-state semantics: during the
                                siteConfig load window we err on the side of showing Subscribe
                                (consistent with the lock calc, which keeps recordings locked
                                until subs are explicitly disabled). */}
                            {siteConfig.subscriptionEnabled !== false ? (
                                <>
                                    <button onClick={() => { setLockedVideo(null); handleClassCheckout(null, 'subscription'); }} disabled={purchaseLoading}
                                        style={{
                                            width: '100%', padding: '14px 24px', borderRadius: 14, fontSize: 15, fontWeight: 700, border: 'none', cursor: 'pointer',
                                            fontFamily: 'var(--mom-font-sans)',
                                            background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                                            color: '#fff', boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                                            opacity: purchaseLoading ? 0.5 : 1, transition: 'all 0.2s',
                                        }}>
                                        {purchaseLoading ? 'Redirecting...' : (siteConfig.lockedVideoButtonLabel || `Subscribe · ${priceLabel}`)}
                                    </button>
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: 'var(--mom-warm-gray)', marginTop: 12 }}>
                                        Cancel anytime
                                    </p>
                                </>
                            ) : (
                                <button onClick={() => setLockedVideo(null)}
                                    style={{
                                        width: '100%', padding: '14px 24px', borderRadius: 14, fontSize: 15, fontWeight: 700, border: 'none', cursor: 'pointer',
                                        fontFamily: 'var(--mom-font-sans)',
                                        background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                                        color: '#fff', boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                                    }}>
                                    Got it
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ── CLASS DETAIL MODAL (2026-04-26) ────────────
                Opens when a viewer clicks a scheduled / waiting-room-open tile.
                Surfaces the class title, full date/time, description (often
                invisible to users until inside the live player today), metadata
                badges, and a context-aware primary CTA.

                CTA priority:
                  • Within waiting room AND has access → Join Waiting Room (primary)
                  • Non-subscriber on paid class       → Subscribe (primary)
                  • Has access (free or subscriber)    → Set reminder toggle
                  • Admin                               → no secondary action
                                                          (admin info only — Edit/Delete on the tile)

                Mirrors the locked-video modal's design language verbatim
                (max 460px, white card on dim backdrop, hero on top, content
                below, click-backdrop to dismiss). */}
            {classDetailModal && (() => {
                const cls = classDetailModal;
                const sCurrent = cls.state || cls.status;
                const close = () => setClassDetailModal(null);
                const heroImage = cls.thumbnailUrl || cls.imageUrl;
                // Release-day class: a date, no time, no waiting room, no per-class
                // reminder — "Wednesday's class · October 21" / "Ready at 12:00 AM, your time".
                const relDetail = cls._release || null;
                const dateLine = relDetail
                    ? `${(siteConfig.releaseDayLabelTemplate || "{day}'s class").replace('{day}', relDetail.weekday)} · ${formatReleaseDate(relDetail.date, { month: 'long', day: 'numeric' })}`
                    : cls.scheduledAt ? new Date(cls.scheduledAt).toLocaleDateString('en-US', {
                    weekday: 'long', month: 'long', day: 'numeric',
                }) + ' at ' + new Date(cls.scheduledAt).toLocaleTimeString('en-US', {
                    hour: 'numeric', minute: '2-digit', hour12: true,
                }) : '';
                const relativeLine = relDetail
                    ? (siteConfig.releaseReadyLine || 'Ready at 12:00 AM, your time')
                    : formatRelativeStart(cls.scheduledAt, sCurrent);
                const groups = (cls.muscleGroups && cls.muscleGroups.length > 0) ? cls.muscleGroups : [];
                const userHasAccess = userCanAccessClass(cls.id);
                const showSubscribeCTA = !isAdmin && !userHasAccess && !isSubscriber && !isClassFree(cls.id);
                const showJoinCTA = !relDetail && !isAdmin && userHasAccess && isClassSoon(cls.scheduledAt);
                const showSetReminder = !relDetail && !isAdmin;
                // Release-day class: the member's standing class-day reminder instead.
                const showDayReminder = !!relDetail && !isAdmin && !!userId && userHasAccess;
                const reminderSet = userReminders.has(cls.id);
                return (
                    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
                        onClick={close}>
                        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} />
                        <div style={{
                            position: 'relative', background: 'var(--mom-white)', borderRadius: 24, overflow: 'hidden',
                            maxWidth: 460, width: '100%', maxHeight: '88vh', display: 'flex', flexDirection: 'column',
                            boxShadow: 'var(--shadow-xl)',
                        }} onClick={(e) => e.stopPropagation()}>
                            {/* Hero */}
                            <div style={{ position: 'relative', width: '100%', height: 200, overflow: 'hidden', flexShrink: 0 }}>
                                {heroImage ? (
                                    <img src={heroImage} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} referrerPolicy="no-referrer" />
                                ) : (
                                    <div style={{
                                        width: '100%', height: '100%',
                                        background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '12px 14px',
                                    }}>
                                        <span style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: 'rgba(255,255,255,0.92)', fontWeight: 600, textAlign: 'center', lineHeight: 1.1 }}>
                                            moments of movement
                                        </span>
                                        <span style={{ marginTop: 8, fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)' }}>
                                            with Danielle
                                        </span>
                                    </div>
                                )}
                                {/* Live badge top-left if currently live (drain reads as live too) */}
                                {(sCurrent === 'live' || sCurrent === 'ending') && (cls.classType || 'live') === 'live' && (
                                    <span style={{
                                        position: 'absolute', top: 12, left: 12,
                                        padding: '4px 10px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                                        background: '#dc2626', color: '#fff',
                                        letterSpacing: '0.08em', textTransform: 'uppercase',
                                        fontFamily: 'var(--mom-font-sans)',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                    }}>LIVE</span>
                                )}
                                <button onClick={close}
                                    style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(0,0,0,0.4)', border: 'none', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', backdropFilter: 'blur(4px)' }}>
                                    <span style={{ color: '#fff', fontSize: 18, lineHeight: 1 }}>×</span>
                                </button>
                            </div>
                            {/* Body — scrolls independently of the modal frame so a long description doesn't push the CTA off-screen */}
                            <div style={{ padding: '24px 28px 28px', overflowY: 'auto', flex: 1 }}>
                                <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 24, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 6, lineHeight: 1.2 }}>
                                    {cls.title}
                                </h3>
                                {dateLine && (
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-charcoal)', marginBottom: 4 }}>
                                        {dateLine}
                                    </p>
                                )}
                                {relativeLine && (
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: 'var(--mom-primary)', marginBottom: 14, letterSpacing: '0.02em' }}>
                                        {relativeLine}
                                    </p>
                                )}
                                {/* Metadata pills */}
                                {(cls.duration || groups.length > 0 || cls.classic || cls.easeIn || cls.strongPlus || isClassFree(cls.id)) && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
                                        {cls.duration && (
                                            <span style={{ padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: 'var(--mom-cream)', color: 'var(--mom-charcoal)', fontFamily: 'var(--mom-font-sans)' }}>
                                                {cls.duration} min
                                            </span>
                                        )}
                                        {groups.map((g) => (
                                            <span key={g} style={{ padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: 'var(--mom-cream)', color: 'var(--mom-charcoal)', fontFamily: 'var(--mom-font-sans)', textTransform: 'capitalize' }}>
                                                {g}
                                            </span>
                                        ))}
                                        {cls.classic && (
                                            <span style={{ padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: 'var(--mom-primary)', color: '#fff', fontFamily: 'var(--mom-font-sans)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                                                Classic
                                            </span>
                                        )}
                                        {cls.easeIn && (
                                            <span style={{ padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: 'var(--mom-success, #6b9b7a)', color: '#fff', fontFamily: 'var(--mom-font-sans)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                                                Ease in
                                            </span>
                                        )}
                                        {cls.strongPlus && (
                                            <span style={{ padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: STRONG_PLUS_COLOR, color: '#fff', fontFamily: 'var(--mom-font-sans)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                                                Strong+
                                            </span>
                                        )}
                                        {isClassFree(cls.id) && (
                                            <span style={{ padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: 'color-mix(in srgb, var(--mom-primary) 12%, transparent)', color: 'var(--mom-primary)', fontFamily: 'var(--mom-font-sans)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                                                Free
                                            </span>
                                        )}
                                    </div>
                                )}
                                {/* Description — only render the divider + body when there's actual copy.
                                    Uses the FormattedText component so admin's lightweight markdown renders. */}
                                {cls.description && cls.description.trim() && (
                                    <>
                                        <div style={{ width: 40, height: 2, background: 'var(--mom-primary)', borderRadius: 1, marginBottom: 14, opacity: 0.4 }} />
                                        <div style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: 'var(--mom-charcoal)', lineHeight: 1.6, marginBottom: 20 }}>
                                            <FormattedText text={cls.description} />
                                        </div>
                                    </>
                                )}
                            </div>
                            {/* Action bar — pinned bottom of card so it never scrolls away */}
                            {(showSubscribeCTA || showJoinCTA || showSetReminder || showDayReminder) && (
                                <div style={{ padding: '16px 28px 24px', borderTop: '1px solid var(--mom-cream-dark)', background: 'var(--mom-white)', display: 'flex', flexDirection: 'column', gap: 10, flexShrink: 0 }}>
                                    {showJoinCTA && (
                                        <button onClick={() => { close(); setActiveClass(cls); }}
                                            style={{
                                                width: '100%', padding: '14px 24px', borderRadius: 14, fontSize: 15, fontWeight: 700, border: 'none', cursor: 'pointer',
                                                fontFamily: 'var(--mom-font-sans)',
                                                background: 'var(--mom-primary)', color: '#fff',
                                                boxShadow: '0 4px 16px rgba(0,0,0,0.12)', transition: 'all 0.2s',
                                            }}>
                                            Join Waiting Room
                                        </button>
                                    )}
                                    {showSubscribeCTA && (
                                        <button onClick={() => { close(); handleClassCheckout(cls, 'subscription'); }} disabled={purchaseLoading}
                                            style={{
                                                width: '100%', padding: '14px 24px', borderRadius: 14, fontSize: 15, fontWeight: 700, border: 'none', cursor: 'pointer',
                                                fontFamily: 'var(--mom-font-sans)',
                                                background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                                                color: '#fff', boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                                                opacity: purchaseLoading ? 0.5 : 1, transition: 'all 0.2s',
                                            }}>
                                            {purchaseLoading ? 'Redirecting...' : `${priceLabel} · ${subscribeCta}`}
                                        </button>
                                    )}
                                    {showSetReminder && (
                                        <button onClick={() => handleToggleReminder(cls)}
                                            style={{
                                                width: '100%', padding: '12px 20px', borderRadius: 12, fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer',
                                                fontFamily: 'var(--mom-font-sans)',
                                                background: reminderSet ? 'color-mix(in srgb, var(--mom-primary) 12%, transparent)' : 'var(--mom-cream)',
                                                color: reminderSet ? 'var(--mom-primary)' : 'var(--mom-charcoal)',
                                                transition: 'all 0.2s',
                                            }}>
                                            {reminderSet ? 'Reminder set ✓' : 'Set reminder'}
                                        </button>
                                    )}
                                    {showDayReminder && (
                                        <ClassDayReminderControl userId={userId} getToken={getToken} copy={siteConfig} />
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })()}

            {/* ── END CLASS CONFIRMATION MODAL ────────────── */}
            {/* Styled confirm modal — admin must explicitly click "End Class" to trigger
                handleEndClass. Cancel closes the modal without side effects. Prevents
                accidental mid-broadcast end clicks, which are catastrophic once fired
                (stream disable + manifest seal are irreversible). */}
            {/* ── Pre-recorded recovery modal (2026-04-23) ──
                Surfaced on admin mount when IDB holds chunks from a prior
                interrupted recording session. Admin decides: upload what was
                captured, or discard. While busy (reading the Blob or starting
                upload) buttons show a spinner and are disabled. If the linked
                class was deleted upstream, only Discard is offered — uploading
                would orphan the asset at Mux.

                Copy notes: all strings here are admin-facing, and kept plain.
                We deliberately do NOT put this through siteConfig — these are
                recovery-flow system messages, not coach-tailored product
                strings, and they have zero impact on the viewer experience.  */}
            {pendingRecoveryRecording && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
                    onClick={() => { if (!recoveryBusy) setPendingRecoveryRecording(null); }}>
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)' }} />
                    <div role="dialog" aria-label="Unfinished recording found"
                        style={{
                            position: 'relative', background: 'var(--mom-white)', borderRadius: 20,
                            maxWidth: 460, width: '100%', boxShadow: 'var(--shadow-xl)',
                            padding: '28px 28px 24px', textAlign: 'center',
                        }}
                        onClick={(e) => e.stopPropagation()}>
                        {/* Info icon — reuses the brand primary */}
                        <div style={{
                            width: 56, height: 56, borderRadius: '50%',
                            background: 'color-mix(in srgb, var(--mom-primary) 10%, transparent)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 16px',
                        }}>
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--mom-primary)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
                                <polyline points="12 6 12 12 16 14" />
                            </svg>
                        </div>
                        <h3 style={{
                            fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic',
                            fontWeight: 600, color: 'var(--mom-charcoal)', marginBottom: 8, lineHeight: 1.2,
                        }}>
                            Unfinished recording found
                        </h3>
                        <p style={{
                            fontFamily: 'var(--mom-font-sans)', fontSize: 14, lineHeight: 1.6,
                            color: 'var(--mom-warm-gray)', marginBottom: 8,
                        }}>
                            {/* Format the captured duration sensibly. Under 60s: "about Ns";
                                otherwise HH:MM. Matches the BROADCASTING timer format admin
                                is already familiar with. */}
                            {(() => {
                                const s = Math.max(0, Math.round(pendingRecoveryRecording.estimatedSeconds || 0));
                                const durationText = s < 60
                                    ? `${s}s`
                                    : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
                                const startedText = pendingRecoveryRecording.startedAt
                                    ? new Date(pendingRecoveryRecording.startedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
                                    : 'earlier';
                                return (
                                    <>
                                        We saved a recording for <strong style={{ color: 'var(--mom-charcoal)' }}>{pendingRecoveryRecording.classTitle}</strong>{' '}
                                        — about <strong style={{ color: 'var(--mom-charcoal)' }}>{durationText}</strong> captured starting at {startedText}.
                                    </>
                                );
                            })()}
                        </p>
                        {pendingRecoveryRecording.classExists ? (
                            <p style={{
                                fontFamily: 'var(--mom-font-sans)', fontSize: 13, lineHeight: 1.55,
                                color: 'var(--mom-warm-gray)', marginBottom: 24,
                            }}>
                                Upload to Mux now so it can play at the scheduled class time, or discard if you'd rather re-record.
                            </p>
                        ) : (
                            <p style={{
                                fontFamily: 'var(--mom-font-sans)', fontSize: 13, lineHeight: 1.55,
                                color: 'var(--mom-warm-gray)', marginBottom: 24,
                            }}>
                                The class this recording belonged to has been deleted. You can only discard.
                            </p>
                        )}
                        <div style={{ display: 'flex', gap: 10 }}>
                            <button onClick={handleRecoveryDiscard}
                                disabled={recoveryBusy}
                                style={{
                                    flex: 1, padding: '12px 18px', borderRadius: 12,
                                    fontSize: 14, fontWeight: 600,
                                    background: 'var(--mom-cream)', color: 'var(--mom-charcoal)',
                                    border: 'none', cursor: recoveryBusy ? 'wait' : 'pointer',
                                    opacity: recoveryBusy ? 0.6 : 1,
                                    fontFamily: 'var(--mom-font-sans)',
                                }}>
                                Discard
                            </button>
                            {pendingRecoveryRecording.classExists && (
                                <button onClick={handleRecoveryUpload}
                                    disabled={recoveryBusy}
                                    style={{
                                        flex: 1, padding: '12px 18px', borderRadius: 12,
                                        fontSize: 14, fontWeight: 700,
                                        background: 'var(--mom-primary)', color: '#fff',
                                        border: 'none', cursor: recoveryBusy ? 'wait' : 'pointer',
                                        opacity: recoveryBusy ? 0.7 : 1,
                                        fontFamily: 'var(--mom-font-sans)',
                                        boxShadow: '0 2px 8px color-mix(in srgb, var(--mom-primary) 30%, transparent)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                    }}>
                                    {recoveryBusy && (
                                        <div style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.35)', borderTopColor: '#fff', animation: 'spin 0.8s linear infinite' }} />
                                    )}
                                    {recoveryBusy ? 'Preparing…' : 'Upload Now'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ── DELETE CLASS CONFIRMATION ──
                Every admin Delete (Schedule tile, Drafts, Past classes) lands here
                first. Deleting removes the class entry only — a recording already in
                On Demand stays (it lives in classRecordings), so the copy says so. */}
            {deleteConfirm && (() => {
                const cls = deleteConfirm;
                const sDel = cls.state || cls.status;
                const aired = sDel === 'ended';
                const close = () => { if (!deleting) setDeleteConfirm(null); };
                return (
                    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
                        onClick={close}>
                        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)' }} />
                        <div role="dialog" aria-label="Confirm delete class"
                            style={{
                                position: 'relative', background: 'var(--mom-white)', borderRadius: 20,
                                maxWidth: 420, width: '100%', boxShadow: 'var(--shadow-xl)',
                                padding: '28px 28px 24px', textAlign: 'center',
                            }}
                            onClick={(e) => e.stopPropagation()}>
                            <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', fontWeight: 600, color: 'var(--mom-charcoal)', marginBottom: 6, lineHeight: 1.25 }}>
                                Delete this class?
                            </h3>
                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, fontWeight: 600, color: 'var(--mom-charcoal)', marginBottom: 2 }}>
                                {cls.title || 'Untitled class'}
                            </p>
                            {cls.scheduledAt && (
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, color: 'var(--mom-warm-gray)', marginBottom: 14 }}>
                                    {classWhenLabel(cls)}
                                </p>
                            )}
                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, lineHeight: 1.6, color: 'var(--mom-warm-gray)', marginBottom: 24 }}>
                                {aired
                                    ? 'It will be removed from the Schedule. Its recording stays in On Demand.'
                                    : 'Members will no longer see it on the Schedule. This can’t be undone from the app.'}
                            </p>
                            <div style={{ display: 'flex', gap: 10 }}>
                                <button type="button" onClick={close} disabled={deleting} autoFocus
                                    style={{ flex: 1, padding: '12px 18px', borderRadius: 12, fontSize: 14, fontWeight: 600, background: 'var(--mom-cream)', color: 'var(--mom-charcoal)', border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)' }}>
                                    Keep it
                                </button>
                                <button type="button" disabled={deleting}
                                    onClick={async () => { setDeleting(true); try { await handleDeleteClass(cls); } finally { setDeleting(false); setDeleteConfirm(null); } }}
                                    style={{ flex: 1, padding: '12px 18px', borderRadius: 12, fontSize: 14, fontWeight: 700, background: '#dc2626', color: '#fff', border: 'none', cursor: deleting ? 'default' : 'pointer', fontFamily: 'var(--mom-font-sans)', boxShadow: '0 2px 8px rgba(220,38,38,0.3)', opacity: deleting ? 0.6 : 1 }}>
                                    {deleting ? 'Deleting…' : 'Delete class'}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {endClassConfirm && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
                    onClick={() => setEndClassConfirm(null)}>
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)' }} />
                    <div role="dialog" aria-label="Confirm end class"
                        style={{
                            position: 'relative', background: 'var(--mom-white)', borderRadius: 20,
                            maxWidth: 420, width: '100%', boxShadow: 'var(--shadow-xl)',
                            padding: '28px 28px 24px', textAlign: 'center',
                        }}
                        onClick={(e) => e.stopPropagation()}>
                        {/* Warning icon */}
                        <div style={{
                            width: 56, height: 56, borderRadius: '50%',
                            background: 'color-mix(in srgb, #dc2626 10%, transparent)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 16px',
                        }}>
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                        </div>
                        {/* Class-type aware copy. Three distinct cases:
                             1) Pre-recorded RECORDING phase (activeClass null OR not yet live) →
                                finalize-the-recording language; no viewers are watching yet.
                             2) Pre-recorded AIRING phase (status === 'live') →
                                cut-short language; this is an early-termination kill-switch, the
                                cron normally handles the natural end. Admin needs to know they're
                                bailing out of an in-progress class, not running the normal flow.
                             3) Live class AIRING (status === 'live') →
                                viewer-facing "final words" language — true-live classes need an
                                explicit end every time as part of the normal flow.
                           Detection mirrors the handleEndClass routing logic. */}
                        {(endClassConfirm.classType === 'prerecorded'
                          && (!activeClass || activeClass.id !== endClassConfirm.id || activeClass.status !== 'live')) ? (
                            // Case 1: pre-recorded RECORDING phase
                            <>
                                <h3 style={{
                                    fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic',
                                    fontWeight: 600, color: 'var(--mom-charcoal)', marginBottom: 8, lineHeight: 1.2,
                                }}>
                                    End recording?
                                </h3>
                                <p style={{
                                    fontFamily: 'var(--mom-font-sans)', fontSize: 14, lineHeight: 1.6,
                                    color: 'var(--mom-warm-gray)', marginBottom: 24,
                                }}>
                                    We'll upload your recording to Mux and save it as the class VOD.
                                    Keep this tab open while it uploads — the class will play to
                                    viewers at its scheduled time. This cannot be undone.
                                </p>
                            </>
                        ) : endClassConfirm.classType === 'prerecorded' ? (
                            // Case 2: pre-recorded AIRING — cut class short
                            <>
                                <h3 style={{
                                    fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic',
                                    fontWeight: 600, color: 'var(--mom-charcoal)', marginBottom: 8, lineHeight: 1.2,
                                }}>
                                    End this class early?
                                </h3>
                                <p style={{
                                    fontFamily: 'var(--mom-font-sans)', fontSize: 14, lineHeight: 1.6,
                                    color: 'var(--mom-warm-gray)', marginBottom: 24,
                                }}>
                                    The recording will keep playing to its natural end on its own — you don't
                                    need to do anything. Use this only if you need to cut viewers loose before
                                    the class finishes (e.g. a recording issue or audio problem). Viewers will
                                    immediately see the thank-you screen. This cannot be undone.
                                </p>
                            </>
                        ) : (
                            // Case 3: true-live AIRING — admin's normal end-of-class flow
                            <>
                                <h3 style={{
                                    fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic',
                                    fontWeight: 600, color: 'var(--mom-charcoal)', marginBottom: 8, lineHeight: 1.2,
                                }}>
                                    End this class?
                                </h3>
                                <p style={{
                                    fontFamily: 'var(--mom-font-sans)', fontSize: 14, lineHeight: 1.6,
                                    color: 'var(--mom-warm-gray)', marginBottom: 24,
                                }}>
                                    Your viewers will hear the rest of your final words, then see a thank-you
                                    screen. This cannot be undone.
                                </p>
                            </>
                        )}
                        <div style={{ display: 'flex', gap: 10 }}>
                            <button onClick={() => setEndClassConfirm(null)}
                                style={{
                                    flex: 1, padding: '12px 18px', borderRadius: 12,
                                    fontSize: 14, fontWeight: 600,
                                    background: 'var(--mom-cream)', color: 'var(--mom-charcoal)',
                                    border: 'none', cursor: 'pointer',
                                    fontFamily: 'var(--mom-font-sans)',
                                    transition: 'background 0.15s ease',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--mom-cream-dark)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--mom-cream)'; }}>
                                Cancel
                            </button>
                            <button onClick={() => { const cls = endClassConfirm; setEndClassConfirm(null); handleEndClass(cls); }}
                                style={{
                                    flex: 1, padding: '12px 18px', borderRadius: 12,
                                    fontSize: 14, fontWeight: 700,
                                    background: '#dc2626', color: '#fff',
                                    border: 'none', cursor: 'pointer',
                                    fontFamily: 'var(--mom-font-sans)',
                                    boxShadow: '0 2px 8px rgba(220,38,38,0.3)',
                                    transition: 'background 0.15s ease',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = '#b91c1c'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = '#dc2626'; }}>
                                {/* Confirm button mirrors the entry-point button copy. For pre-recorded
                                    AIRING (Case 2 above) the action is an early termination, so use
                                    "End Early" to be consistent with the kill-switch framing. The
                                    pre-recorded RECORDING phase (Case 1) and true-live (Case 3) both
                                    keep "End Class" — that's their normal terminating action. */}
                                {(endClassConfirm.classType === 'prerecorded'
                                  && activeClass
                                  && activeClass.id === endClassConfirm.id
                                  && activeClass.status === 'live') ? 'End Early' : 'End Class'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── CLASS-WRAPPING-UP BANNER ────────────────── */}
            {/* Non-blocking top-of-page banner shown immediately after admin confirms End
                Class. The broadcaster UI has already closed and admin can navigate anywhere.
                This banner is pure feedback: "the background processing is in flight, don't
                worry." Auto-dismisses at T+15s once Phase 2 (manifest seal at T+12s) is safely
                past. If admin closes the tab before then, the Vercel cron's stranded-ending
                sweep finishes the job within 5 min (D-050 extended with cron backstop). */}
            {endingBanner && (
                <div style={{
                    position: 'fixed', top: 16, left: 16, right: 16, margin: '0 auto',
                    maxWidth: 560, zIndex: 4000,
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '14px 20px', borderRadius: 14,
                    background: 'linear-gradient(135deg, color-mix(in srgb, var(--mom-charcoal) 96%, var(--mom-white)), color-mix(in srgb, var(--mom-charcoal) 82%, var(--mom-white)))',
                    border: '1.5px solid color-mix(in srgb, var(--mom-charcoal) 60%, transparent)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.25), 0 2px 8px rgba(0,0,0,0.08)',
                    animation: 'goLiveCueSlideIn 0.3s ease-out',
                }}>
                    <div style={{
                        flexShrink: 0, width: 28, height: 28, borderRadius: '50%',
                        border: '3px solid rgba(255,255,255,0.25)',
                        borderTopColor: 'rgba(255,255,255,0.95)',
                        animation: 'spin 0.8s linear infinite',
                    }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{
                            fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 700,
                            letterSpacing: '0.1em', textTransform: 'uppercase',
                            color: 'rgba(255,255,255,0.8)', margin: 0, marginBottom: 2,
                        }}>
                            {endingBanner.title}
                        </p>
                        <p style={{
                            fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600,
                            color: '#fff', margin: 0, lineHeight: 1.4,
                        }}>
                            {endingBanner.subtext}
                        </p>
                    </div>
                </div>
            )}

            {/* ── PRE-RECORDED UPLOAD BANNER ────────────────── */}
            {/* Rendered during the Mux Direct Upload phase for pre-recorded classes. Progress
                bar surfaces the PUT progress (MB uploaded / total); error state reveals a
                Retry button that re-invokes the upload with the cached Blob. The beforeunload
                guard above warns admin before closing the tab mid-upload. */}
            {uploadingClass && (
                <div style={{
                    position: 'fixed', top: 16, left: 16, right: 16, margin: '0 auto',
                    maxWidth: 560, zIndex: 4000,
                    display: 'flex', flexDirection: 'column', gap: 10,
                    padding: '14px 20px', borderRadius: 14,
                    background: 'linear-gradient(135deg, color-mix(in srgb, var(--mom-charcoal) 96%, var(--mom-white)), color-mix(in srgb, var(--mom-charcoal) 82%, var(--mom-white)))',
                    border: '1.5px solid color-mix(in srgb, var(--mom-charcoal) 60%, transparent)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.25), 0 2px 8px rgba(0,0,0,0.08)',
                    animation: 'goLiveCueSlideIn 0.3s ease-out',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {uploadingClass.phase !== 'error' && (
                            <div style={{
                                flexShrink: 0, width: 28, height: 28, borderRadius: '50%',
                                border: '3px solid rgba(255,255,255,0.25)',
                                borderTopColor: 'rgba(255,255,255,0.95)',
                                animation: 'spin 0.8s linear infinite',
                            }} />
                        )}
                        {uploadingClass.phase === 'error' && (
                            <div style={{
                                flexShrink: 0, width: 28, height: 28, borderRadius: '50%',
                                background: '#dc2626',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: '#fff', fontSize: 16, fontWeight: 700,
                            }}>!</div>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{
                                fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 700,
                                letterSpacing: '0.1em', textTransform: 'uppercase',
                                color: 'rgba(255,255,255,0.8)', margin: 0, marginBottom: 2,
                            }}>
                                {uploadingClass.phase === 'requesting' && 'Preparing upload'}
                                {uploadingClass.phase === 'uploading' && 'Uploading recording'}
                                {uploadingClass.phase === 'processing' && 'Processing'}
                                {uploadingClass.phase === 'error' && 'Upload failed'}
                            </p>
                            <p style={{
                                fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600,
                                color: '#fff', margin: 0, lineHeight: 1.4,
                            }}>
                                {uploadingClass.phase === 'requesting' && 'Contacting Mux…'}
                                {uploadingClass.phase === 'uploading' && (
                                    <>
                                        {Math.round((uploadingClass.progress || 0) * 100)}% —
                                        {' '}{(uploadingClass.loadedBytes / 1024 / 1024).toFixed(1)} MB of {(uploadingClass.totalBytes / 1024 / 1024).toFixed(1)} MB
                                    </>
                                )}
                                {uploadingClass.phase === 'processing' && 'Mux is finalizing your recording. This usually takes 30s–2min.'}
                                {uploadingClass.phase === 'error' && (uploadingClass.error || 'Something went wrong.')}
                            </p>
                        </div>
                        {uploadingClass.phase === 'error' && (
                            <button
                                onClick={retryUpload}
                                style={{
                                    flexShrink: 0, padding: '8px 16px', borderRadius: 8,
                                    background: '#fff', color: 'var(--mom-charcoal)',
                                    border: 'none', fontWeight: 700, fontSize: 13,
                                    fontFamily: 'var(--mom-font-sans)', cursor: 'pointer',
                                }}
                            >Retry</button>
                        )}
                    </div>
                    {/* Progress bar — only during active upload. */}
                    {uploadingClass.phase === 'uploading' && (
                        <div style={{
                            width: '100%', height: 6, borderRadius: 3,
                            background: 'rgba(255,255,255,0.15)',
                            overflow: 'hidden',
                        }}>
                            <div style={{
                                width: `${Math.max(2, Math.round((uploadingClass.progress || 0) * 100))}%`,
                                height: '100%',
                                background: 'linear-gradient(90deg, var(--mom-primary), var(--mom-accent, var(--mom-primary)))',
                                transition: 'width 0.15s linear',
                            }} />
                        </div>
                    )}
                    {/* Keep-tab-open hint while upload is in flight. */}
                    {(uploadingClass.phase === 'requesting' || uploadingClass.phase === 'uploading') && (
                        <p style={{
                            fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 500,
                            color: 'rgba(255,255,255,0.75)', margin: 0, lineHeight: 1.4,
                        }}>
                            Keep this tab open until the upload completes. You can navigate once it reaches Processing.
                        </p>
                    )}
                </div>
            )}

            {/* ── Yearly plan sheet (only ever opens when annualOffer exists) ── */}
            <PlanPickerModal
                open={!!planPickerFor}
                offer={annualOffer}
                copy={resolvePlanCopy(siteConfig)}
                loading={purchaseLoading}
                onClose={() => setPlanPickerFor(null)}
                onContinue={(plan) => handleClassCheckout(planPickerFor?.classItem || null, 'subscription', plan)}
            />

            {/* ── Subscription checkout error toast ────────────────────────────
                Surfaces "couldn't start checkout" failures from handleClassCheckout.
                Auto-dismisses after 5s; click to dismiss. Replaces the in-modal
                error display that lived in the (now-removed) subscription waiver
                modal. Same toast pattern as the reminder error toast above. */}
            {checkoutErrorToast && (
                <div style={{
                    position: 'fixed', bottom: 90, left: '50%', transform: 'translateX(-50%)',
                    zIndex: 10001, maxWidth: 360, width: 'calc(100% - 32px)',
                    padding: '12px 18px', borderRadius: 12,
                    background: 'var(--mom-charcoal)', color: 'var(--mom-white)',
                    fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600,
                    lineHeight: 1.4, textAlign: 'center',
                    boxShadow: 'var(--shadow-lg)',
                    animation: 'fadeIn 0.2s ease-out',
                }}
                onClick={() => setCheckoutErrorToast('')}>
                    {checkoutErrorToast}
                </div>
            )}

            {/* ── D-063: FULL VIEWER LIST SHEET ────────────────── */}
            <PresenceSheet
                open={presenceSheetOpen}
                viewers={liveViewers}
                onClose={() => setPresenceSheetOpen(false)}
            />

            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes goLiveCueSlideIn {
                    from { opacity: 0; transform: translateY(-12px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(24px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
}

// ── DraftPreviewModal ──────────────────────────────────────────────────────
// Admin-only Mux Player modal for previewing a draft pre-recorded class's
// recording before publishing. Loads Mux Player from CDN (same pattern as
// LivePlayer / VideoPlayerModal). No favorites, no comments, no engagement —
// just clean playback so admin can verify the recording before publish.
function DraftPreviewModal({ draft, onClose }) {
    const playerRef = useRef(null);
    const containerRef = useRef(null);

    // Load Mux Player web component once
    useEffect(() => {
        if (customElements.get('mux-player')) return;
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/@mux/mux-player@3/dist/mux-player.min.js';
        script.async = true;
        document.head.appendChild(script);
    }, []);

    // Mount the <mux-player> element imperatively so we can set attributes
    // identically to how LivePlayer does it. Re-mounts when draft changes.
    useEffect(() => {
        if (!containerRef.current || !draft?.muxPlaybackId) return;
        // Clear any previous player
        containerRef.current.innerHTML = '';
        const player = document.createElement('mux-player');
        player.setAttribute('playback-id', draft.muxPlaybackId);
        player.setAttribute('stream-type', 'on-demand');
        player.setAttribute('metadata-video-title', draft.title || 'Draft preview');
        player.setAttribute('accent-color', '#c33764');
        player.style.width = '100%';
        player.style.height = '100%';
        player.style.aspectRatio = '16 / 9';
        containerRef.current.appendChild(player);
        playerRef.current = player;
        return () => {
            try { player.pause?.(); } catch (e) { /* swallow */ }
            if (containerRef.current) containerRef.current.innerHTML = '';
            playerRef.current = null;
        };
    }, [draft?.muxPlaybackId, draft?.title]);

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9998, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
            onClick={onClose}>
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.7)' }} />
            <div style={{
                position: 'relative', background: 'var(--mom-charcoal)', borderRadius: 16, overflow: 'hidden',
                maxWidth: 920, width: '100%', boxShadow: 'var(--shadow-xl)',
            }} onClick={(e) => e.stopPropagation()}>
                <div style={{
                    padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: 'var(--mom-charcoal)', color: 'var(--mom-white)', gap: 12,
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                        <span style={{
                            padding: '2px 10px', borderRadius: 999, fontSize: 9, fontWeight: 800,
                            background: 'var(--mom-warm-gray)', color: 'var(--mom-white)',
                            fontFamily: 'var(--mom-font-sans)', letterSpacing: '0.08em', flexShrink: 0,
                        }}>DRAFT PREVIEW</span>
                        <h3 style={{
                            fontFamily: 'var(--mom-font-serif)', fontSize: 16, fontStyle: 'italic',
                            margin: 0, color: 'var(--mom-white)',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                            {draft.title || 'Untitled draft'}
                        </h3>
                    </div>
                    <button onClick={onClose}
                        style={{ background: 'rgba(255,255,255,0.12)', border: 'none', color: 'var(--mom-white)', width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', flexShrink: 0, fontSize: 18, lineHeight: 1 }}>
                        ×
                    </button>
                </div>
                <div ref={containerRef} style={{ width: '100%', background: '#000', minHeight: 200 }} />
            </div>
        </div>
    );
}

export default Workouts;
